"use client";
import * as React$1 from "react";
import React, { Children, createContext, createRef, forwardRef, memo, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import * as Collapsible from "@radix-ui/react-collapsible";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { AlertCircleIcon, AlertCircleIcon as AlertCircleIcon$1, AlertTriangleIcon, AlignLeftIcon, AppWindow, ArrowDownIcon, ArrowDownToLineIcon, ArrowLeftIcon, ArrowLeftIcon as ArrowLeftIcon$1, ArrowRightFromLineIcon, ArrowRightIcon, ArrowRightLeftIcon, ArrowRightToLineIcon, ArrowUpDownIcon, ArrowUpIcon, ArrowUpIcon as ArrowUpIcon$1, ArrowUpToLineIcon, BoldIcon, BookOpenIcon, CalendarIcon, CalendarIcon as CalendarIcon$1, CheckCircle2Icon, CheckCircleIcon, CheckIcon, CheckIcon as CheckIcon$1, CheckSquareIcon, ChevronDownIcon, ChevronDownIcon as ChevronDownIcon$1, ChevronLeftIcon, ChevronLeftIcon as ChevronLeftIcon$1, ChevronRightIcon, ChevronRightIcon as ChevronRightIcon$1, ChevronUpIcon, ChevronUpIcon as ChevronUpIcon$1, ChevronsLeftIcon, ChevronsRightIcon, ChevronsUpDownIcon, CircleDotIcon, CircleIcon, CircleUserIcon, CodeIcon, ColumnsIcon, CopyIcon, DatabaseIcon, DollarSignIcon, DownloadIcon, ExternalLinkIcon, EyeIcon, EyeOffIcon, FileIcon, FileSearchIcon, FileTextIcon, FilterIcon, FilterIcon as FilterIcon$1, FilterXIcon, FlagIcon, FolderIcon, FolderKanbanIcon, FolderPlusIcon, FolderUpIcon, FunctionSquareIcon, GitBranchIcon, GlobeIcon, HashIcon, Heading1Icon, Heading2Icon, Heading3Icon, HelpCircleIcon, HistoryIcon, HomeIcon, ImageIcon, ImageIcon as ImageIcon$1, ImageOffIcon, InfoIcon, ItalicIcon, Kanban, KanbanIcon, KeyIcon, KeyRoundIcon, LanguagesIcon, LayoutGrid, LayoutGridIcon, LayoutList, Link2Icon, LinkIcon, ListIcon, ListOrderedIcon, ListPlusIcon, ListTodoIcon, LoaderIcon, LockIcon, LogOutIcon, MailIcon, Maximize2Icon, MenuIcon, MessageCircleIcon, MinusCircleIcon, MinusIcon, MinusIcon as MinusIcon$1, MoonIcon, MoreVerticalIcon, Music2Icon, PanelLeftCloseIcon, PanelLeftIcon, PanelLeftOpenIcon, PauseIcon, PenLineIcon, PencilIcon, PhoneIcon, PinIcon, PlayIcon, PlusIcon, QuoteIcon, RefreshCcwIcon, RefreshCwIcon, RefreshCwIcon as RefreshCwIcon$1, RepeatIcon, Rows3Icon, SaveIcon, SearchIcon, SearchIcon as SearchIcon$1, SendIcon, Settings2, SettingsIcon, ShieldAlertIcon, ShieldIcon, ShoppingCartIcon, SlidersHorizontalIcon, SquareIcon, StarIcon, StickyNoteIcon, StrikethroughIcon, SunIcon, SunMoonIcon, Table2, TableIcon, TagIcon, TerminalIcon, TextIcon, Trash2Icon, TrendingUpIcon, TypeIcon, UnderlineIcon, UndoIcon, Unlink2Icon, UploadCloudIcon, UploadIcon, UserCheckIcon, UserIcon, UserPlus, UsersIcon, VideoIcon, VoteIcon, Wand2Icon, WrenchIcon, XCircleIcon, XIcon, XIcon as XIcon$1, icons as lucideIcons } from "lucide-react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { useDropzone } from "react-dropzone";
import * as LabelPrimitive from "@radix-ui/react-label";
import { deepEqual } from "fast-equals";
import MarkdownIt from "markdown-it";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as MenubarPrimitive from "@radix-ui/react-menubar";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Command } from "cmdk";
import * as SeparatorPrimitive from "@radix-ui/react-separator";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as SliderPrimitive from "@radix-ui/react-slider";
import * as ReactDOM from "react-dom";
import { createPortal } from "react-dom";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { FixedSizeList } from "react-window";
import useMeasure from "react-use-measure";
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DndContext, DragOverlay, PointerSensor, closestCenter, closestCorners, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import * as Portal from "@radix-ui/react-portal";
import { Slot } from "@radix-ui/react-slot";
import { CSS } from "@dnd-kit/utilities";
//#region src/styles.ts
var CONTROL_HEIGHT = {
	smallest: 28,
	small: 32,
	medium: 40,
	large: 48,
	xl: 56,
	"2xl": 64
};
/** Tailwind min-height class per control size. Keep in sync with CONTROL_HEIGHT. */
var controlHeightMixin = {
	smallest: "min-h-[28px]",
	small: "min-h-[32px]",
	medium: "min-h-[40px]",
	large: "min-h-[48px]",
	xl: "min-h-[56px]",
	"2xl": "min-h-[64px]"
};
/** Horizontal padding paired with each size, so gutters scale with height. */
var controlPaddingMixin = {
	smallest: "px-2",
	small: "px-2",
	medium: "px-3",
	large: "px-4",
	xl: "px-6",
	"2xl": "px-10"
};
/**
* Suppress the focus ring on a surface that is focusable but is not a control —
* a popover panel, a menu container, a bare cell input inside a table that
* indicates focus by other means.
*
* Still expressed as `ring-0`, and that is why the default ring in
* `index.css` overrides `--tw-ring-shadow` rather than switching to
* `inset-ring-*`: `inset-ring` is a different variable, which this would not
* have silenced, and every surface that had opted out would have got a ring
* back.
*/
var focusedDisabled = "focus-visible:ring-0 focus-visible:ring-offset-0";
var focusedInvisibleMixin = "focus:bg-surface-field-hover";
/**
* The focus ring, drawn OUTSIDE the element, for a control that opts into it.
*
* The product's default ring is the `:focus-visible` rule in `index.css`, and
* that one is drawn *inside* the border box: an outset ring is paint beyond the
* box, and any ancestor that clips — a scroller, a truncating cell, a
* max-height collapse — cuts it. This one is still outset because its only
* caller is a whole field row sitting inside `p-4`/`p-6` of card padding, where
* there is room around it and nothing clipping within 16px. Reach for it only
* where that is true; anywhere else, let the default apply.
*
* Three dead declarations were removed rather than reshuffled:
*   `outline-hidden` + `outline-none`  — the same instruction twice.
*   `ring-primary` + `ring-primary/50` — the first could never win.
*   `ring-opacity-50`                  — Tailwind v3 syntax. v4 expresses
*                                        opacity with the `/50` slash form, so
*                                        this generated nothing at all.
*
* What is left is the ring that was actually rendering, at 60% rather than 50%:
* on `surface-900` the old value sat close enough to the field's own border to
* be missed at a glance, which is the one thing a focus ring may not be.
* `ring-offset-0` + a transparent offset stay — without them the ring draws a
* white halo on dark surfaces.
*/
var focusedClasses = "z-30 outline-none ring-2 ring-primary/60 ring-offset-0 ring-offset-transparent";
/**
* Field surfaces.
*
* A field is the one surface that goes the other way: it is INSET into
* whatever holds it, so `surface-field` is an alpha fill rather than a solid,
* and it composes over a card, the sheet or a dialog alike. It also carries
* the hairline: a field is an object (DESIGN.md rule 2), and without the line
* a 4% fill on a card reads as a soft slab rather than a control — measured on
* the product form, eight of them in a row looked like a wall. The reference
* draws every input with its 1px edge, on both themes.
*
* The 1px inset top highlight that used to sit here was the recessed-edge
* device from before the field had a border; with a real hairline above it
* the two stacked into a doubled top edge. The border does its work now.
*
* Exactly one element carries this mixin per field — the box, never the
* input inside it — so the alpha fill and its hover composite once.
*/
var fieldBackgroundMixin = "bg-surface-field border border-hairline";
var fieldBackgroundInvisibleMixin = "bg-transparent";
var fieldBackgroundDisabledMixin = "bg-surface-field/60";
/**
* `transition-colors` here, because every other interactive surface in the kit
* has it and fields did not — `cardClickableMixin` eases, a TextField snapped.
* On a form of eight fields that difference is the whole feel of the screen.
*/
var fieldBackgroundHoverMixin = "transition-colors duration-150 hover:bg-surface-field-hover";
/**
* The hairline. One value per theme (`--hairline` in theme.css), and where it
* goes matters more than what it is: a line marks an OBJECT — a card on the
* sheet, a field, a floating surface. It does not mark a region: a tile inside
* a card, a row, a segmented track, a button, a chip are fills, and the
* lightness step between them is the whole separation. One exception, and it
* is the sheet's own edge against the frame: that step is about 3 L* on both
* themes and did not read on a real screen, so the Scaffold draws this line
* there. Measured before this existed, the panel drew the same line on every
* card and every tile as well, and with only two surface values available the
* line was doing the work a step should have done.
*/
var defaultBorderMixin = "border-hairline";
var paperMixin = "bg-surface-card rounded-lg border border-hairline-strong";
var cardMixin = "bg-surface-card rounded-xl border border-hairline";
/**
* An inset well: code, a query, a log tail, a connection string.
*
* It must read as *recessed into* the surface holding it, which means darker
* than that surface in dark mode. Cards now sit at `surface-card` (#181818),
* so #0a0a0a is already fourteen units below and reads as a well; #000 was
* tuned for the old #0a card and is now harsher than it needs to be. That value
* is the `surface-well` role (theme.css), the one surface that goes DOWN the
* ladder on purpose. Pair with `font-mono`; this mixin carries the surface
* only.
*/
var codeSurfaceMixin = "bg-surface-well rounded-md";
/**
* The accent, used as TEXT.
*
* `--color-primary` (#0070F4) is tuned to be a fill — white text on it, it on
* white. Read as text on our dark surfaces it lands at **4.36:1 on a
* `surface-900` card** (measured), which is below AA for body-sized text, and
* every accent link in the product sits on exactly that surface. On the page's
* `surface-950` it only reaches 4.62:1, so the margin was never real.
*
* `--color-primary-light` is the same hue lifted 0.15 in OKLCH lightness and
* measures **7.34:1** on the same card. It is indistinguishable as "the blue"
* and comfortably legible, so dark mode swaps to it for text.
*
* Use this for links, accent labels and any accent-coloured type. It is NOT for
* fills — a filled button keeps `bg-primary`, where the contrast question runs
* the other way and #0070F4 is already correct.
*/
var accentTextMixin = "text-primary dark:text-primary-light";
/**
* Hover on a SOLID surface goes one step lighter, and it stays neutral. The
* previous `hover:bg-primary/5` tinted every clickable card blue, which spent
* the brand colour on a state that carries no meaning; on the dark ladder it
* also read as a hue shift rather than a lift.
*/
var cardClickableMixin = "hover:bg-surface-card-hover cursor-pointer transition-colors duration-150";
var cardSelectedMixin = "bg-primary-bg/30 dark:bg-primary-bg/10 ring-1 ring-primary/75";
/**
* Hover on a TRANSPARENT ground — a nav item, a menu item, a list row, a tree
* row. `surface-hover` is an alpha fill, so it lifts whatever it lands on by
* the same amount, and `surface-active` is the same idea one step further for
* the selected row when no hue is wanted. Do not use these on a solid card:
* an alpha fill REPLACES a solid background rather than tinting it.
*/
var surfaceHoverMixin = "hover:bg-surface-hover transition-colors duration-150";
var surfaceActiveMixin = "bg-surface-active";
//#endregion
//#region src/util/cls.ts
function cls(...classes) {
	return twMerge(clsx(classes));
}
//#endregion
//#region src/util/debounce.ts
/**
* @ignore
*/
function debounce(func, wait = 166) {
	let timeout;
	function debounced(...args) {
		const later = () => {
			func.apply(this, args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	}
	debounced.clear = () => {
		clearTimeout(timeout);
	};
	return debounced;
}
//#endregion
//#region src/util/hash.ts
function hashString(str) {
	let hash = 0;
	let i;
	let chr;
	for (i = 0; i < str.length; i++) {
		chr = str.charCodeAt(i);
		hash = (hash << 5) - hash + chr;
		hash |= 0;
	}
	return Math.abs(hash);
}
//#endregion
//#region src/util/chip_colors.ts
/** WCAG floor, plus a little margin so rounding cannot drop a pair below 4.5. */
var CONTRAST_TARGET = 4.6;
/** Page backgrounds the outlined variant sits on: `bg-white` and `surface-950`. */
var PAGE_LIGHT = "#ffffff";
var PAGE_DARK = "#181818";
function toRgb(hex) {
	let h = hex.replace("#", "");
	if (h.length === 3) h = h.split("").map((c) => c + c).join("");
	return [
		0,
		2,
		4
	].map((i) => parseInt(h.slice(i, i + 2), 16));
}
function toHex(rgb) {
	return "#" + rgb.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("");
}
function toRgba(hex, alpha) {
	const [r, g, b] = toRgb(hex);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
/**
* What a translucent colour reads as over an opaque one: a per-channel sRGB
* mix, which is how the browser composites it. The tint ink is pushed against
* this, never against the page alone — a 16% wash of the hue moves the
* background a long way toward the ink, and an ink that only cleared AA on
* the bare page lands near 3.5:1 on its own tint.
*/
function compositeOver(fgHex, alpha, bgHex) {
	const fg = toRgb(fgHex);
	const bg = toRgb(bgHex);
	return toHex(fg.map((c, i) => alpha * c + (1 - alpha) * bg[i]));
}
/**
* Tint alpha by tone, light then dark. The tones keep meaning something in the
* tinted variant — a `Darker` chip is a heavier wash, not a different hue —
* and dark mode runs four points higher because the same alpha of a bright
* hue reads fainter over near-black than over white.
*/
var TINT_ALPHA = {
	Lighter: [.1, .14],
	Light: [.14, .18],
	Dark: [.18, .22],
	Darker: [.22, .26]
};
function relativeLuminance(hex) {
	const [r, g, b] = toRgb(hex).map((v) => {
		const c = v / 255;
		return c <= .03928 ? c / 12.92 : Math.pow((c + .055) / 1.055, 2.4);
	});
	return .2126 * r + .7152 * g + .0722 * b;
}
function contrastRatio(a, b) {
	const x = relativeLuminance(a);
	const y = relativeLuminance(b);
	const [hi, lo] = x > y ? [x, y] : [y, x];
	return (hi + .05) / (lo + .05);
}
function mixHex(from, to, t) {
	const A = toRgb(from);
	const B = toRgb(to);
	return toHex([
		0,
		1,
		2
	].map((i) => A[i] + (B[i] - A[i]) * t));
}
/**
* Walk a tinted ink toward black or white until it clears the floor on `bg`.
*
* Stops at the first passing step rather than going all the way, so the ink
* keeps as much of its hue as legibility allows.
*/
function push(base, toward, bg) {
	for (let t = 0; t <= 1.0001; t += .02) {
		const candidate = mixHex(base, toward, t);
		if (contrastRatio(candidate, bg) >= CONTRAST_TARGET) return candidate;
	}
	return toward;
}
/** The ink for a chip filled with `bg`: whichever direction has more headroom. */
function inkOn(stops, bg) {
	const dark = push(stops.text, "#000000", bg);
	const light = push(stops.onDeep ?? stops.pale, "#ffffff", bg);
	return contrastRatio(dark, bg) >= contrastRatio(light, bg) ? dark : light;
}
var CHIP_HUES = [
	"blue",
	"cyan",
	"teal",
	"green",
	"yellow",
	"orange",
	"red",
	"pink",
	"purple",
	"gray",
	"indigo",
	"violet",
	"fuchsia",
	"rose",
	"emerald"
];
var HUE_STOPS = {
	blue: {
		pale: "#cfdfff",
		mid: "#9cc7ff",
		solid: "#2d7ff9",
		deep: "#2750ae",
		text: "#102046"
	},
	cyan: {
		pale: "#d0f0fd",
		mid: "#77d1f3",
		solid: "#18bfff",
		deep: "#0b76b7",
		text: "#04283f"
	},
	teal: {
		pale: "#c2f5e9",
		mid: "#72ddc3",
		solid: "#20d9d2",
		deep: "#06a09b",
		text: "#012524",
		onDeep: "#daf3e9"
	},
	green: {
		pale: "#d1f7c4",
		mid: "#93e088",
		solid: "#20c933",
		deep: "#338a17",
		text: "#0b1d05"
	},
	yellow: {
		pale: "#ffeab6",
		mid: "#ffd66e",
		solid: "#fcb400",
		deep: "#b87503",
		text: "#3b2501"
	},
	orange: {
		pale: "#fee2d5",
		mid: "#ffa981",
		solid: "#ff6f2c",
		deep: "#d74d26",
		text: "#6b2613"
	},
	red: {
		pale: "#ffdce5",
		mid: "#ff9eb7",
		solid: "#f82b60",
		deep: "#ba1e45",
		text: "#4c0c1c"
	},
	pink: {
		pale: "#ffdaf6",
		mid: "#f99de2",
		solid: "#ff08c2",
		deep: "#b2158b",
		text: "#400832"
	},
	purple: {
		pale: "#ede2fe",
		mid: "#cdb0ff",
		solid: "#8b46ff",
		deep: "#6b1cb0",
		text: "#280b42"
	},
	gray: {
		pale: "#eeeeee",
		mid: "#cccccc",
		solid: "#666666",
		deep: "#444444",
		text: "#040404"
	},
	indigo: {
		pale: "#e0e7ff",
		mid: "#a5b4fc",
		solid: "#6366f1",
		deep: "#4f46e5",
		text: "#312e81"
	},
	violet: {
		pale: "#ede9fe",
		mid: "#c4b5fd",
		solid: "#8b5cf6",
		deep: "#7c3aed",
		text: "#4c1d95"
	},
	fuchsia: {
		pale: "#fae8ff",
		mid: "#f0abfc",
		solid: "#d946ef",
		deep: "#c026d3",
		text: "#701a75"
	},
	rose: {
		pale: "#ffe4e6",
		mid: "#fda4af",
		solid: "#f43f5e",
		deep: "#e11d48",
		text: "#881337"
	},
	emerald: {
		pale: "#d1fae5",
		mid: "#6ee7b7",
		solid: "#10b981",
		deep: "#059669",
		text: "#064e3b"
	}
};
/**
* One tone of one hue.
*
* Backgrounds are the palette's, unchanged — the stops were not the problem and
* moving them would have restyled every chip in the product. Only the ink moved,
* and it moved to wherever the measurement says it has to be.
*/
function tone(stops, chipTone) {
	const outline = {
		outlineText: push(stops.text, "#000000", PAGE_LIGHT),
		darkOutlineText: push(stops.onDeep ?? stops.pale, "#ffffff", PAGE_DARK)
	};
	const [alphaLight, alphaDark] = TINT_ALPHA[chipTone];
	const tint = {
		tintColor: toRgba(stops.solid, alphaLight),
		tintText: push(stops.deep, "#000000", compositeOver(stops.solid, alphaLight, PAGE_LIGHT)),
		darkTintColor: toRgba(stops.solid, alphaDark),
		darkTintText: push(stops.mid, "#ffffff", compositeOver(stops.solid, alphaDark, PAGE_DARK))
	};
	const filled = (light, dark) => ({
		color: light,
		text: inkOn(stops, light),
		darkColor: dark,
		darkText: inkOn(stops, dark),
		...outline,
		...tint
	});
	switch (chipTone) {
		case "Light": return filled(stops.mid, stops.solid);
		case "Dark": return filled(stops.solid, stops.solid);
		case "Darker": return filled(stops.deep, stops.deep);
		default: return filled(stops.pale, stops.deep);
	}
}
var CHIP_TONES = [
	"Lighter",
	"Light",
	"Dark",
	"Darker"
];
function buildChipColors() {
	const colors = {};
	for (const hue of CHIP_HUES) {
		const stops = HUE_STOPS[hue];
		for (const chipTone of CHIP_TONES) colors[`${hue}${chipTone}`] = tone(stops, chipTone);
		colors[hue] = tone(stops, "Lighter");
	}
	return colors;
}
/**
* Every chip scheme, keyed by hue and tone.
*
* This table used to hold four tones per hue and was flattened to one, which
* left `blueDark`, `redDarker` and friends resolving to `undefined` — a chip
* with a colour in its config rendering with no colour at all — and left seeded
* chips picking from ten schemes, so a five-value enum routinely drew the same
* background three times. The tones are generated from {@link HUE_STOPS} now,
* so a new hue brings its whole family with it.
*/
var CHIP_COLORS = buildChipColors();
/**
* The keys a seeded chip may be assigned, in a stable order.
*
* The bare-hue aliases are excluded: they are the same scheme as `<hue>Lighter`
* and would make the palest tone twice as likely as any other.
*/
var CHIP_SEED_KEYS = CHIP_HUES.flatMap((hue) => CHIP_TONES.map((chipTone) => `${hue}${chipTone}`));
function getColorSchemeForKey(key) {
	return CHIP_COLORS[key] ?? getColorSchemeForSeed(String(key));
}
function getColorSchemeForSeed(seed) {
	return CHIP_COLORS[CHIP_SEED_KEYS[hashString(seed) % CHIP_SEED_KEYS.length]];
}
//#endregion
//#region src/hooks/PortalContainerContext.tsx
var PortalContainerContext = createContext(void 0);
/**
* Provider component that sets the portal container for all descendants.
* This can be used at any level of the tree to specify where portals should be attached.
*
* @example
* ```tsx
* const containerRef = useRef<HTMLDivElement>(null);
*
* <div ref={containerRef}>
*   <PortalContainerProvider container={containerRef.current}>
*     <YourComponents />
*   </PortalContainerProvider>
* </div>
* ```
*/
function PortalContainerProvider({ container, children }) {
	const contextValue = useMemo(() => ({ container }), [container]);
	return /* @__PURE__ */ jsx(PortalContainerContext.Provider, {
		value: contextValue,
		children
	});
}
/**
* Hook to access the portal container from context.
* Returns null if no provider is found in the tree.
*
* @returns The portal container element or null
*/
function usePortalContainer() {
	return useContext(PortalContainerContext)?.container ?? null;
}
//#endregion
//#region src/hooks/useInjectStyles.tsx
/**
* Use this hook to create a `<style>` element and inject it into the DOM.
* It checks if the style already exists, and if it does, it does not inject it again.
* @param key
* @param styles
*/
function useInjectStyles(key, styles) {
	const portalContainer = usePortalContainer();
	useEffect(() => {
		if (typeof document === "undefined") return;
		const targetContainer = portalContainer ?? document.head;
		if (!targetContainer.querySelector?.(`#${key}`)) {
			const style = document.createElement("style");
			style.id = key;
			style.innerHTML = styles;
			(targetContainer || document.head).appendChild(style);
		}
	}, []);
}
//#endregion
//#region src/hooks/useOutsideAlerter.tsx
/**
* Hook that alerts clicks outside the passed ref
*/
function useOutsideAlerter(ref, onOutsideClick, active = true) {
	useEffect(() => {
		if (!active) return;
		/**
		* Alert if clicked on outside of element
		*/
		function handleClickOutside(event) {
			if (isInPresentationLayer(event.target)) return;
			if (ref.current && !ref.current.contains(event.target)) onOutsideClick();
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [
		ref,
		active,
		onOutsideClick
	]);
}
/**
* Check if any parent of the node is a presentation node
* @param node
*/
function isInPresentationLayer(node) {
	if (node instanceof HTMLElement) {
		if (node.getAttribute("role") === "presentation") return true;
		return isInPresentationLayer(node.parentNode);
	}
	return false;
}
//#endregion
//#region src/hooks/useDebouncedCallback.ts
function useDebouncedCallback(value, callback, immediate, timeoutMs = 300) {
	const pendingUpdate = React.useRef(false);
	const callbackRef = React.useRef(callback);
	React.useEffect(() => {
		callbackRef.current = callback;
	}, [callback]);
	const performUpdate = React.useCallback(() => {
		callbackRef.current();
		pendingUpdate.current = false;
	}, []);
	const handlerRef = React.useRef(void 0);
	React.useEffect(() => {
		if (immediate) {
			clearTimeout(handlerRef.current);
			if (pendingUpdate.current) performUpdate();
			return;
		}
		pendingUpdate.current = true;
		clearTimeout(handlerRef.current);
		handlerRef.current = setTimeout(performUpdate, timeoutMs);
		return () => {
			if (immediate) performUpdate();
		};
	}, [
		immediate,
		value,
		performUpdate,
		timeoutMs
	]);
}
//#endregion
//#region src/hooks/useDebounceCallback.tsx
function useDebounceCallback(callback, delay) {
	const timeoutRef = useRef(null);
	return useCallback((...args) => {
		if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
		timeoutRef.current = window.setTimeout(() => {
			callback?.(...args);
		}, delay ?? 200);
	}, [callback, delay]);
}
//#endregion
//#region src/hooks/useDebounceValue.tsx
function useDebounceValue(value, delay = 300) {
	const [debouncedValue, setDebouncedValue] = useState(value);
	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);
		return () => {
			clearTimeout(handler);
		};
	}, [value, delay]);
	return debouncedValue;
}
//#endregion
//#region src/hooks/useIsDarkMode.ts
/**
* Whether the document is in dark mode right now, and re-rendered when that
* changes.
*
* The theme is a class on `<html>` (`dark`, set by the panel's mode
* controller) or a `data-theme` attribute (the marketing site). Components
* that pick colours in JS rather than through CSS variables — `Chip` derives
* its ink per theme — used to read the class once at render and never again,
* so a theme switch left every chip on screen wearing the other theme's ink
* until something else happened to re-render it. Subscribing through a
* `MutationObserver` on the root's attributes makes the read live.
*/
function useIsDarkMode() {
	return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
function getSnapshot() {
	if (typeof document === "undefined") return false;
	const root = document.documentElement;
	return root.classList.contains("dark") || root.getAttribute("data-theme") === "dark";
}
function getServerSnapshot() {
	return false;
}
function subscribe(onChange) {
	if (typeof document === "undefined" || typeof MutationObserver === "undefined") return () => void 0;
	const observer = new MutationObserver(onChange);
	observer.observe(document.documentElement, {
		attributes: true,
		attributeFilter: ["class", "data-theme"]
	});
	return () => observer.disconnect();
}
//#endregion
//#region src/components/Collapse.tsx
var Collapse = React.memo(({ children, className, in: isOpen = false, duration = 220 }) => {
	useInjectStyles(`Collapse-${duration}`, `
.CollapseContent-${duration} {
  overflow: hidden;
}
.CollapseContent-${duration}[data-state='open'] {
  animation: slideDown ${duration}ms ease-out;
}
.CollapseContent-${duration}[data-state='closed'] {
  animation: slideUp ${duration}ms ease-in;
}

@keyframes slideDown {
  from {
    height: 0;
  }
  to {
    height: var(--radix-collapsible-content-height);
  }
}

@keyframes slideUp {
  from {
    height: var(--radix-collapsible-content-height);
  }
  to {
    height: 0;
  }
}
`);
	return /* @__PURE__ */ jsx(Collapsible.Root, {
		open: isOpen,
		className,
		children: /* @__PURE__ */ jsx(Collapsible.Content, {
			className: cls(`CollapseContent-${duration}`),
			children
		})
	});
});
Collapse.displayName = "Collapse";
//#endregion
//#region src/components/Autocomplete.tsx
var useAutoComplete = ({ ref }) => {
	const [autoCompleteOpen, setAutoCompleteOpen] = React.useState(false);
	const [inputFocused, setInputFocused] = React.useState(false);
	React.useEffect(() => {
		if (ref.current) {
			ref.current.onfocus = () => {
				setAutoCompleteOpen(true);
				setInputFocused(true);
			};
			ref.current.onblur = () => {
				setInputFocused(false);
			};
		}
	}, [ref]);
	return {
		inputFocused,
		autoCompleteOpen,
		setAutoCompleteOpen
	};
};
function Autocomplete({ children, open, setOpen, className }) {
	const autocompleteRef = React.useRef(null);
	useOutsideAlerter(autocompleteRef, () => setOpen(false));
	return /* @__PURE__ */ jsx(Collapse, {
		in: open,
		duration: 30,
		className: cls("absolute top-full left-0 right-0 overflow-visible", open ? "shadow-2xs" : "", "my-2", "z-20", "w-full"),
		children: /* @__PURE__ */ jsx("div", {
			ref: autocompleteRef,
			className: cls(open ? paperMixin : "", "bg-surface-card", className),
			children
		})
	});
}
var AutocompleteItem = React.memo(function AutocompleteItem({ children, onClick, className }) {
	return /* @__PURE__ */ jsx("div", {
		className: cls("flex w-full items-center h-[48px] cursor-pointer transition-colors duration-150 hover:bg-surface-hover", className),
		onClick,
		children
	});
});
//#endregion
//#region src/icons/icon_keys.ts
var iconKeys = [
	"AArrowDown",
	"AArrowUp",
	"ALargeSmall",
	"Accessibility",
	"Activity",
	"ActivitySquare",
	"AirVent",
	"Airplay",
	"AlarmCheck",
	"AlarmClock",
	"AlarmClockCheck",
	"AlarmClockMinus",
	"AlarmClockOff",
	"AlarmClockPlus",
	"AlarmMinus",
	"AlarmPlus",
	"AlarmSmoke",
	"Album",
	"AlertCircle",
	"AlertOctagon",
	"AlertTriangle",
	"AlignCenter",
	"AlignCenterHorizontal",
	"AlignCenterVertical",
	"AlignEndHorizontal",
	"AlignEndVertical",
	"AlignHorizontalDistributeCenter",
	"AlignHorizontalDistributeEnd",
	"AlignHorizontalDistributeStart",
	"AlignHorizontalJustifyCenter",
	"AlignHorizontalJustifyEnd",
	"AlignHorizontalJustifyStart",
	"AlignHorizontalSpaceAround",
	"AlignHorizontalSpaceBetween",
	"AlignJustify",
	"AlignLeft",
	"AlignRight",
	"AlignStartHorizontal",
	"AlignStartVertical",
	"AlignVerticalDistributeCenter",
	"AlignVerticalDistributeEnd",
	"AlignVerticalDistributeStart",
	"AlignVerticalJustifyCenter",
	"AlignVerticalJustifyEnd",
	"AlignVerticalJustifyStart",
	"AlignVerticalSpaceAround",
	"AlignVerticalSpaceBetween",
	"Ambulance",
	"Ampersand",
	"Ampersands",
	"Amphora",
	"Anchor",
	"Angry",
	"Annoyed",
	"Antenna",
	"Anvil",
	"Aperture",
	"AppWindow",
	"AppWindowMac",
	"Apple",
	"Archive",
	"ArchiveRestore",
	"ArchiveX",
	"AreaChart",
	"Armchair",
	"ArrowBigDown",
	"ArrowBigDownDash",
	"ArrowBigLeft",
	"ArrowBigLeftDash",
	"ArrowBigRight",
	"ArrowBigRightDash",
	"ArrowBigUp",
	"ArrowBigUpDash",
	"ArrowDown",
	"ArrowDown01",
	"ArrowDown10",
	"ArrowDownAZ",
	"ArrowDownCircle",
	"ArrowDownFromLine",
	"ArrowDownLeft",
	"ArrowDownLeftFromCircle",
	"ArrowDownLeftFromSquare",
	"ArrowDownLeftSquare",
	"ArrowDownNarrowWide",
	"ArrowDownRight",
	"ArrowDownRightFromCircle",
	"ArrowDownRightFromSquare",
	"ArrowDownRightSquare",
	"ArrowDownSquare",
	"ArrowDownToDot",
	"ArrowDownToLine",
	"ArrowDownUp",
	"ArrowDownWideNarrow",
	"ArrowDownZA",
	"ArrowLeft",
	"ArrowLeftCircle",
	"ArrowLeftFromLine",
	"ArrowLeftRight",
	"ArrowLeftSquare",
	"ArrowLeftToLine",
	"ArrowRight",
	"ArrowRightCircle",
	"ArrowRightFromLine",
	"ArrowRightLeft",
	"ArrowRightSquare",
	"ArrowRightToLine",
	"ArrowUp",
	"ArrowUp01",
	"ArrowUp10",
	"ArrowUpAZ",
	"ArrowUpCircle",
	"ArrowUpDown",
	"ArrowUpFromDot",
	"ArrowUpFromLine",
	"ArrowUpLeft",
	"ArrowUpLeftFromCircle",
	"ArrowUpLeftFromSquare",
	"ArrowUpLeftSquare",
	"ArrowUpNarrowWide",
	"ArrowUpRight",
	"ArrowUpRightFromCircle",
	"ArrowUpRightFromSquare",
	"ArrowUpRightSquare",
	"ArrowUpSquare",
	"ArrowUpToLine",
	"ArrowUpWideNarrow",
	"ArrowUpZA",
	"ArrowsUpFromLine",
	"Asterisk",
	"AsteriskSquare",
	"Astroid",
	"AtSign",
	"Atom",
	"AudioLines",
	"AudioWaveform",
	"Award",
	"Axe",
	"Axis3D",
	"Baby",
	"Backpack",
	"Badge",
	"BadgeAlert",
	"BadgeCent",
	"BadgeCheck",
	"BadgeDollarSign",
	"BadgeEuro",
	"BadgeHelp",
	"BadgeIndianRupee",
	"BadgeInfo",
	"BadgeJapaneseYen",
	"BadgeMinus",
	"BadgePercent",
	"BadgePlus",
	"BadgePoundSterling",
	"BadgeQuestionMark",
	"BadgeRussianRuble",
	"BadgeSwissFranc",
	"BadgeTurkishLira",
	"BadgeX",
	"BaggageClaim",
	"Balloon",
	"Ban",
	"Banana",
	"Bandage",
	"Banknote",
	"BanknoteArrowDown",
	"BanknoteArrowUp",
	"BanknoteX",
	"BarChart",
	"BarChart2",
	"BarChart3",
	"BarChart4",
	"BarChartBig",
	"BarChartHorizontal",
	"BarChartHorizontalBig",
	"Barcode",
	"Barrel",
	"Baseline",
	"Bath",
	"Battery",
	"BatteryCharging",
	"BatteryFull",
	"BatteryLow",
	"BatteryMedium",
	"BatteryPlus",
	"BatteryWarning",
	"Beaker",
	"Bean",
	"BeanOff",
	"Bed",
	"BedDouble",
	"BedSingle",
	"Beef",
	"BeefOff",
	"Beer",
	"BeerOff",
	"Bell",
	"BellCheck",
	"BellDot",
	"BellElectric",
	"BellMinus",
	"BellOff",
	"BellPlus",
	"BellRing",
	"BetweenHorizonalEnd",
	"BetweenHorizonalStart",
	"BetweenHorizontalEnd",
	"BetweenHorizontalStart",
	"BetweenVerticalEnd",
	"BetweenVerticalStart",
	"BicepsFlexed",
	"Bike",
	"Binary",
	"Binoculars",
	"Biohazard",
	"Bird",
	"Birdhouse",
	"Bitcoin",
	"Blend",
	"Blinds",
	"Blocks",
	"Bluetooth",
	"BluetoothConnected",
	"BluetoothOff",
	"BluetoothSearching",
	"Bold",
	"Bolt",
	"Bomb",
	"Bone",
	"Book",
	"BookA",
	"BookAlert",
	"BookAudio",
	"BookCheck",
	"BookCopy",
	"BookDashed",
	"BookDown",
	"BookHeadphones",
	"BookHeart",
	"BookImage",
	"BookKey",
	"BookLock",
	"BookMarked",
	"BookMinus",
	"BookOpen",
	"BookOpenCheck",
	"BookOpenText",
	"BookPlus",
	"BookSearch",
	"BookTemplate",
	"BookText",
	"BookType",
	"BookUp",
	"BookUp2",
	"BookUser",
	"BookX",
	"Bookmark",
	"BookmarkCheck",
	"BookmarkMinus",
	"BookmarkOff",
	"BookmarkPlus",
	"BookmarkX",
	"BoomBox",
	"Bot",
	"BotMessageSquare",
	"BotOff",
	"BottleWine",
	"BowArrow",
	"Box",
	"BoxSelect",
	"Boxes",
	"Braces",
	"Brackets",
	"Brain",
	"BrainCircuit",
	"BrainCog",
	"BrickWall",
	"BrickWallFire",
	"BrickWallShield",
	"Briefcase",
	"BriefcaseBusiness",
	"BriefcaseConveyorBelt",
	"BriefcaseMedical",
	"BringToFront",
	"Brush",
	"BrushCleaning",
	"Bubbles",
	"Bug",
	"BugOff",
	"BugPlay",
	"Building",
	"Building2",
	"Bus",
	"BusFront",
	"Cable",
	"CableCar",
	"Cake",
	"CakeSlice",
	"Calculator",
	"Calendar",
	"Calendar1",
	"CalendarArrowDown",
	"CalendarArrowUp",
	"CalendarCheck",
	"CalendarCheck2",
	"CalendarClock",
	"CalendarCog",
	"CalendarDays",
	"CalendarFold",
	"CalendarHeart",
	"CalendarMinus",
	"CalendarMinus2",
	"CalendarOff",
	"CalendarPlus",
	"CalendarPlus2",
	"CalendarRange",
	"CalendarSearch",
	"CalendarSync",
	"CalendarX",
	"CalendarX2",
	"Calendars",
	"Camera",
	"CameraOff",
	"CandlestickChart",
	"Candy",
	"CandyCane",
	"CandyOff",
	"Cannabis",
	"CannabisOff",
	"Captions",
	"CaptionsOff",
	"Car",
	"CarFront",
	"CarTaxiFront",
	"Caravan",
	"CardSim",
	"Carrot",
	"CaseLower",
	"CaseSensitive",
	"CaseUpper",
	"CassetteTape",
	"Cast",
	"Castle",
	"Cat",
	"Cctv",
	"CctvOff",
	"ChartArea",
	"ChartBar",
	"ChartBarBig",
	"ChartBarDecreasing",
	"ChartBarIncreasing",
	"ChartBarStacked",
	"ChartCandlestick",
	"ChartColumn",
	"ChartColumnBig",
	"ChartColumnDecreasing",
	"ChartColumnIncreasing",
	"ChartColumnStacked",
	"ChartGantt",
	"ChartLine",
	"ChartNetwork",
	"ChartNoAxesColumn",
	"ChartNoAxesColumnDecreasing",
	"ChartNoAxesColumnIncreasing",
	"ChartNoAxesCombined",
	"ChartNoAxesGantt",
	"ChartPie",
	"ChartScatter",
	"ChartSpline",
	"Check",
	"CheckCheck",
	"CheckCircle",
	"CheckCircle2",
	"CheckLine",
	"CheckSquare",
	"CheckSquare2",
	"ChefHat",
	"Cherry",
	"ChessBishop",
	"ChessKing",
	"ChessKnight",
	"ChessPawn",
	"ChessQueen",
	"ChessRook",
	"ChevronDown",
	"ChevronDownCircle",
	"ChevronDownSquare",
	"ChevronFirst",
	"ChevronLast",
	"ChevronLeft",
	"ChevronLeftCircle",
	"ChevronLeftSquare",
	"ChevronRight",
	"ChevronRightCircle",
	"ChevronRightSquare",
	"ChevronUp",
	"ChevronUpCircle",
	"ChevronUpSquare",
	"ChevronsDown",
	"ChevronsDownUp",
	"ChevronsLeft",
	"ChevronsLeftRight",
	"ChevronsLeftRightEllipsis",
	"ChevronsRight",
	"ChevronsRightLeft",
	"ChevronsUp",
	"ChevronsUpDown",
	"Church",
	"Cigarette",
	"CigaretteOff",
	"Circle",
	"CircleAlert",
	"CircleArrowDown",
	"CircleArrowLeft",
	"CircleArrowOutDownLeft",
	"CircleArrowOutDownRight",
	"CircleArrowOutUpLeft",
	"CircleArrowOutUpRight",
	"CircleArrowRight",
	"CircleArrowUp",
	"CircleCheck",
	"CircleCheckBig",
	"CircleChevronDown",
	"CircleChevronLeft",
	"CircleChevronRight",
	"CircleChevronUp",
	"CircleDashed",
	"CircleDivide",
	"CircleDollarSign",
	"CircleDot",
	"CircleDotDashed",
	"CircleEllipsis",
	"CircleEqual",
	"CircleFadingArrowUp",
	"CircleFadingPlus",
	"CircleGauge",
	"CircleHelp",
	"CircleMinus",
	"CircleOff",
	"CircleParking",
	"CircleParkingOff",
	"CirclePause",
	"CirclePercent",
	"CirclePile",
	"CirclePlay",
	"CirclePlus",
	"CirclePoundSterling",
	"CirclePower",
	"CircleQuestionMark",
	"CircleSlash",
	"CircleSlash2",
	"CircleSlashed",
	"CircleSmall",
	"CircleStar",
	"CircleStop",
	"CircleUser",
	"CircleUserRound",
	"CircleX",
	"CircuitBoard",
	"Citrus",
	"Clapperboard",
	"Clipboard",
	"ClipboardCheck",
	"ClipboardClock",
	"ClipboardCopy",
	"ClipboardEdit",
	"ClipboardList",
	"ClipboardMinus",
	"ClipboardPaste",
	"ClipboardPen",
	"ClipboardPenLine",
	"ClipboardPlus",
	"ClipboardSignature",
	"ClipboardType",
	"ClipboardX",
	"Clock",
	"Clock1",
	"Clock10",
	"Clock11",
	"Clock12",
	"Clock2",
	"Clock3",
	"Clock4",
	"Clock5",
	"Clock6",
	"Clock7",
	"Clock8",
	"Clock9",
	"ClockAlert",
	"ClockArrowDown",
	"ClockArrowUp",
	"ClockCheck",
	"ClockFading",
	"ClockPlus",
	"ClosedCaption",
	"Cloud",
	"CloudAlert",
	"CloudBackup",
	"CloudCheck",
	"CloudCog",
	"CloudDownload",
	"CloudDrizzle",
	"CloudFog",
	"CloudHail",
	"CloudLightning",
	"CloudMoon",
	"CloudMoonRain",
	"CloudOff",
	"CloudRain",
	"CloudRainWind",
	"CloudSnow",
	"CloudSun",
	"CloudSunRain",
	"CloudSync",
	"CloudUpload",
	"Cloudy",
	"Clover",
	"Club",
	"Code",
	"Code2",
	"CodeSquare",
	"CodeXml",
	"Coffee",
	"Cog",
	"Coins",
	"Columns",
	"Columns2",
	"Columns3",
	"Columns3Cog",
	"Columns4",
	"ColumnsSettings",
	"Combine",
	"Command",
	"Compass",
	"Component",
	"Computer",
	"ConciergeBell",
	"Cone",
	"Construction",
	"Contact",
	"Contact2",
	"ContactRound",
	"Container",
	"Contrast",
	"Cookie",
	"CookingPot",
	"Copy",
	"CopyCheck",
	"CopyMinus",
	"CopyPlus",
	"CopySlash",
	"CopyX",
	"Copyleft",
	"Copyright",
	"CornerDownLeft",
	"CornerDownRight",
	"CornerLeftDown",
	"CornerLeftUp",
	"CornerRightDown",
	"CornerRightUp",
	"CornerUpLeft",
	"CornerUpRight",
	"Cpu",
	"CreativeCommons",
	"CreditCard",
	"Croissant",
	"Crop",
	"Cross",
	"Crosshair",
	"Crown",
	"Cuboid",
	"CupSoda",
	"CurlyBraces",
	"Currency",
	"Cylinder",
	"Dam",
	"Database",
	"DatabaseBackup",
	"DatabaseSearch",
	"DatabaseZap",
	"DecimalsArrowLeft",
	"DecimalsArrowRight",
	"Delete",
	"Dessert",
	"Diameter",
	"Diamond",
	"DiamondMinus",
	"DiamondPercent",
	"DiamondPlus",
	"Dice1",
	"Dice2",
	"Dice3",
	"Dice4",
	"Dice5",
	"Dice6",
	"Dices",
	"Diff",
	"Disc",
	"Disc2",
	"Disc3",
	"DiscAlbum",
	"Divide",
	"DivideCircle",
	"DivideSquare",
	"Dna",
	"DnaOff",
	"Dock",
	"Dog",
	"DollarSign",
	"Donut",
	"DoorClosed",
	"DoorClosedLocked",
	"DoorOpen",
	"Dot",
	"DotSquare",
	"Download",
	"DownloadCloud",
	"DraftingCompass",
	"Drama",
	"Drill",
	"Drone",
	"Droplet",
	"DropletOff",
	"Droplets",
	"Drum",
	"Drumstick",
	"Dumbbell",
	"Ear",
	"EarOff",
	"Earth",
	"EarthLock",
	"Eclipse",
	"Edit",
	"Edit2",
	"Edit3",
	"Egg",
	"EggFried",
	"EggOff",
	"Ellipse",
	"Ellipsis",
	"EllipsisVertical",
	"Equal",
	"EqualApproximately",
	"EqualNot",
	"EqualSquare",
	"Eraser",
	"EthernetPort",
	"Euro",
	"EvCharger",
	"Expand",
	"ExternalLink",
	"Eye",
	"EyeClosed",
	"EyeOff",
	"Factory",
	"Fan",
	"FastForward",
	"Feather",
	"Fence",
	"FerrisWheel",
	"File",
	"FileArchive",
	"FileAudio",
	"FileAudio2",
	"FileAxis3D",
	"FileBadge",
	"FileBadge2",
	"FileBarChart",
	"FileBarChart2",
	"FileBox",
	"FileBraces",
	"FileBracesCorner",
	"FileChartColumn",
	"FileChartColumnIncreasing",
	"FileChartLine",
	"FileChartPie",
	"FileCheck",
	"FileCheck2",
	"FileCheckCorner",
	"FileClock",
	"FileCode",
	"FileCode2",
	"FileCodeCorner",
	"FileCog",
	"FileCog2",
	"FileDiff",
	"FileDigit",
	"FileDown",
	"FileEdit",
	"FileExclamationPoint",
	"FileHeadphone",
	"FileHeart",
	"FileImage",
	"FileInput",
	"FileJson",
	"FileJson2",
	"FileKey",
	"FileKey2",
	"FileLineChart",
	"FileLock",
	"FileLock2",
	"FileMinus",
	"FileMinus2",
	"FileMinusCorner",
	"FileMusic",
	"FileOutput",
	"FilePen",
	"FilePenLine",
	"FilePieChart",
	"FilePlay",
	"FilePlus",
	"FilePlus2",
	"FilePlusCorner",
	"FileQuestion",
	"FileQuestionMark",
	"FileScan",
	"FileSearch",
	"FileSearch2",
	"FileSearchCorner",
	"FileSignal",
	"FileSignature",
	"FileSliders",
	"FileSpreadsheet",
	"FileStack",
	"FileSymlink",
	"FileTerminal",
	"FileText",
	"FileType",
	"FileType2",
	"FileTypeCorner",
	"FileUp",
	"FileUser",
	"FileVideo",
	"FileVideo2",
	"FileVideoCamera",
	"FileVolume",
	"FileVolume2",
	"FileWarning",
	"FileX",
	"FileX2",
	"FileXCorner",
	"Files",
	"Film",
	"Filter",
	"FilterX",
	"Fingerprint",
	"FingerprintPattern",
	"FireExtinguisher",
	"Fish",
	"FishOff",
	"FishSymbol",
	"FishingHook",
	"FishingRod",
	"Flag",
	"FlagOff",
	"FlagTriangleLeft",
	"FlagTriangleRight",
	"Flame",
	"FlameKindling",
	"Flashlight",
	"FlashlightOff",
	"FlaskConical",
	"FlaskConicalOff",
	"FlaskRound",
	"FlipHorizontal",
	"FlipHorizontal2",
	"FlipVertical",
	"FlipVertical2",
	"Flower",
	"Flower2",
	"Focus",
	"FoldHorizontal",
	"FoldVertical",
	"Folder",
	"FolderArchive",
	"FolderBookmark",
	"FolderCheck",
	"FolderClock",
	"FolderClosed",
	"FolderCode",
	"FolderCog",
	"FolderCog2",
	"FolderDot",
	"FolderDown",
	"FolderEdit",
	"FolderGit",
	"FolderGit2",
	"FolderHeart",
	"FolderInput",
	"FolderKanban",
	"FolderKey",
	"FolderLock",
	"FolderMinus",
	"FolderOpen",
	"FolderOpenDot",
	"FolderOutput",
	"FolderPen",
	"FolderPlus",
	"FolderRoot",
	"FolderSearch",
	"FolderSearch2",
	"FolderSymlink",
	"FolderSync",
	"FolderTree",
	"FolderUp",
	"FolderX",
	"Folders",
	"Footprints",
	"ForkKnife",
	"ForkKnifeCrossed",
	"Forklift",
	"Form",
	"FormInput",
	"Forward",
	"Frame",
	"Frown",
	"Fuel",
	"Fullscreen",
	"FunctionSquare",
	"Funnel",
	"FunnelPlus",
	"FunnelX",
	"GalleryHorizontal",
	"GalleryHorizontalEnd",
	"GalleryThumbnails",
	"GalleryVertical",
	"GalleryVerticalEnd",
	"Gamepad",
	"Gamepad2",
	"GamepadDirectional",
	"GanttChart",
	"GanttChartSquare",
	"Gauge",
	"GaugeCircle",
	"Gavel",
	"Gem",
	"GeorgianLari",
	"Ghost",
	"Gift",
	"GitBranch",
	"GitBranchMinus",
	"GitBranchPlus",
	"GitCommit",
	"GitCommitHorizontal",
	"GitCommitVertical",
	"GitCompare",
	"GitCompareArrows",
	"GitFork",
	"GitGraph",
	"GitMerge",
	"GitMergeConflict",
	"GitPullRequest",
	"GitPullRequestArrow",
	"GitPullRequestClosed",
	"GitPullRequestCreate",
	"GitPullRequestCreateArrow",
	"GitPullRequestDraft",
	"GlassWater",
	"Glasses",
	"Globe",
	"Globe2",
	"GlobeLock",
	"GlobeOff",
	"GlobeX",
	"Goal",
	"Gpu",
	"Grab",
	"GraduationCap",
	"Grape",
	"Grid",
	"Grid2X2",
	"Grid2X2Check",
	"Grid2X2Plus",
	"Grid2X2X",
	"Grid3X3",
	"Grid3x2",
	"Grip",
	"GripHorizontal",
	"GripVertical",
	"Group",
	"Guitar",
	"Ham",
	"Hamburger",
	"Hammer",
	"Hand",
	"HandCoins",
	"HandFist",
	"HandGrab",
	"HandHeart",
	"HandHelping",
	"HandMetal",
	"HandPlatter",
	"Handbag",
	"Handshake",
	"HardDrive",
	"HardDriveDownload",
	"HardDriveUpload",
	"HardHat",
	"Hash",
	"HatGlasses",
	"Haze",
	"Hd",
	"HdmiPort",
	"Heading",
	"Heading1",
	"Heading2",
	"Heading3",
	"Heading4",
	"Heading5",
	"Heading6",
	"HeadphoneOff",
	"Headphones",
	"Headset",
	"Heart",
	"HeartCrack",
	"HeartHandshake",
	"HeartMinus",
	"HeartOff",
	"HeartPlus",
	"HeartPulse",
	"HeartX",
	"Heater",
	"Helicopter",
	"HelpCircle",
	"HelpingHand",
	"Hexagon",
	"Highlighter",
	"History",
	"Home",
	"Hop",
	"HopOff",
	"Hospital",
	"Hotel",
	"Hourglass",
	"House",
	"HouseHeart",
	"HousePlug",
	"HousePlus",
	"HouseWifi",
	"IceCream",
	"IceCream2",
	"IceCreamBowl",
	"IceCreamCone",
	"IdCard",
	"IdCardLanyard",
	"Image",
	"ImageDown",
	"ImageMinus",
	"ImageOff",
	"ImagePlay",
	"ImagePlus",
	"ImageUp",
	"ImageUpscale",
	"Images",
	"Import",
	"Inbox",
	"Indent",
	"IndentDecrease",
	"IndentIncrease",
	"IndianRupee",
	"Infinity",
	"Info",
	"Inspect",
	"InspectionPanel",
	"Italic",
	"IterationCcw",
	"IterationCw",
	"JapaneseYen",
	"Joystick",
	"Kanban",
	"KanbanSquare",
	"KanbanSquareDashed",
	"Kayak",
	"Key",
	"KeyRound",
	"KeySquare",
	"Keyboard",
	"KeyboardMusic",
	"KeyboardOff",
	"Lamp",
	"LampCeiling",
	"LampDesk",
	"LampFloor",
	"LampWallDown",
	"LampWallUp",
	"LandPlot",
	"Landmark",
	"Languages",
	"Laptop",
	"Laptop2",
	"LaptopMinimal",
	"LaptopMinimalCheck",
	"Lasso",
	"LassoSelect",
	"Laugh",
	"Layers",
	"Layers2",
	"Layers3",
	"LayersMinus",
	"LayersPlus",
	"Layout",
	"LayoutDashboard",
	"LayoutGrid",
	"LayoutList",
	"LayoutPanelLeft",
	"LayoutPanelTop",
	"LayoutTemplate",
	"Leaf",
	"LeafyGreen",
	"Lectern",
	"LensConcave",
	"LensConvex",
	"LetterText",
	"Library",
	"LibraryBig",
	"LibrarySquare",
	"LifeBuoy",
	"Ligature",
	"Lightbulb",
	"LightbulbOff",
	"LineChart",
	"LineDotRightHorizontal",
	"LineSquiggle",
	"LineStyle",
	"Link",
	"Link2",
	"Link2Off",
	"List",
	"ListCheck",
	"ListChecks",
	"ListChevronsDownUp",
	"ListChevronsUpDown",
	"ListCollapse",
	"ListEnd",
	"ListFilter",
	"ListFilterPlus",
	"ListIndentDecrease",
	"ListIndentIncrease",
	"ListMinus",
	"ListMusic",
	"ListOrdered",
	"ListPlus",
	"ListRestart",
	"ListStart",
	"ListTodo",
	"ListTree",
	"ListVideo",
	"ListX",
	"Loader",
	"Loader2",
	"LoaderCircle",
	"LoaderPinwheel",
	"Locate",
	"LocateFixed",
	"LocateOff",
	"LocationEdit",
	"Lock",
	"LockKeyhole",
	"LockKeyholeOpen",
	"LockOpen",
	"LogIn",
	"LogOut",
	"Logs",
	"Lollipop",
	"Luggage",
	"MSquare",
	"Magnet",
	"Mail",
	"MailCheck",
	"MailMinus",
	"MailOpen",
	"MailPlus",
	"MailQuestion",
	"MailQuestionMark",
	"MailSearch",
	"MailWarning",
	"MailX",
	"Mailbox",
	"Mails",
	"Map",
	"MapMinus",
	"MapPin",
	"MapPinCheck",
	"MapPinCheckInside",
	"MapPinHouse",
	"MapPinMinus",
	"MapPinMinusInside",
	"MapPinOff",
	"MapPinPen",
	"MapPinPlus",
	"MapPinPlusInside",
	"MapPinSearch",
	"MapPinX",
	"MapPinXInside",
	"MapPinned",
	"MapPlus",
	"Mars",
	"MarsStroke",
	"Martini",
	"Maximize",
	"Maximize2",
	"Medal",
	"Megaphone",
	"MegaphoneOff",
	"Meh",
	"MemoryStick",
	"Menu",
	"MenuSquare",
	"Merge",
	"MessageCircle",
	"MessageCircleCheck",
	"MessageCircleCode",
	"MessageCircleDashed",
	"MessageCircleHeart",
	"MessageCircleMore",
	"MessageCircleOff",
	"MessageCirclePlus",
	"MessageCircleQuestion",
	"MessageCircleQuestionMark",
	"MessageCircleReply",
	"MessageCircleWarning",
	"MessageCircleX",
	"MessageSquare",
	"MessageSquareCheck",
	"MessageSquareCode",
	"MessageSquareDashed",
	"MessageSquareDiff",
	"MessageSquareDot",
	"MessageSquareHeart",
	"MessageSquareLock",
	"MessageSquareMore",
	"MessageSquareOff",
	"MessageSquarePlus",
	"MessageSquareQuote",
	"MessageSquareReply",
	"MessageSquareShare",
	"MessageSquareText",
	"MessageSquareWarning",
	"MessageSquareX",
	"MessagesSquare",
	"Metronome",
	"Mic",
	"Mic2",
	"MicOff",
	"MicVocal",
	"Microchip",
	"Microscope",
	"Microwave",
	"Milestone",
	"Milk",
	"MilkOff",
	"Minimize",
	"Minimize2",
	"Minus",
	"MinusCircle",
	"MinusSquare",
	"MirrorRectangular",
	"MirrorRound",
	"Monitor",
	"MonitorCheck",
	"MonitorCloud",
	"MonitorCog",
	"MonitorDot",
	"MonitorDown",
	"MonitorOff",
	"MonitorPause",
	"MonitorPlay",
	"MonitorSmartphone",
	"MonitorSpeaker",
	"MonitorStop",
	"MonitorUp",
	"MonitorX",
	"Moon",
	"MoonStar",
	"MoreHorizontal",
	"MoreVertical",
	"Motorbike",
	"Mountain",
	"MountainSnow",
	"Mouse",
	"MouseLeft",
	"MouseOff",
	"MousePointer",
	"MousePointer2",
	"MousePointer2Off",
	"MousePointerBan",
	"MousePointerClick",
	"MousePointerSquareDashed",
	"MouseRight",
	"Move",
	"Move3D",
	"MoveDiagonal",
	"MoveDiagonal2",
	"MoveDown",
	"MoveDownLeft",
	"MoveDownRight",
	"MoveHorizontal",
	"MoveLeft",
	"MoveRight",
	"MoveUp",
	"MoveUpLeft",
	"MoveUpRight",
	"MoveVertical",
	"Music",
	"Music2",
	"Music3",
	"Music4",
	"Navigation",
	"Navigation2",
	"Navigation2Off",
	"NavigationOff",
	"Network",
	"Newspaper",
	"Nfc",
	"NonBinary",
	"Notebook",
	"NotebookPen",
	"NotebookTabs",
	"NotebookText",
	"NotepadText",
	"NotepadTextDashed",
	"Nut",
	"NutOff",
	"Octagon",
	"OctagonAlert",
	"OctagonMinus",
	"OctagonPause",
	"OctagonX",
	"Omega",
	"Option",
	"Orbit",
	"Origami",
	"Outdent",
	"Package",
	"Package2",
	"PackageCheck",
	"PackageMinus",
	"PackageOpen",
	"PackagePlus",
	"PackageSearch",
	"PackageX",
	"PaintBucket",
	"PaintRoller",
	"Paintbrush",
	"Paintbrush2",
	"PaintbrushVertical",
	"Palette",
	"Palmtree",
	"Panda",
	"PanelBottom",
	"PanelBottomClose",
	"PanelBottomDashed",
	"PanelBottomInactive",
	"PanelBottomOpen",
	"PanelLeft",
	"PanelLeftClose",
	"PanelLeftDashed",
	"PanelLeftInactive",
	"PanelLeftOpen",
	"PanelLeftRightDashed",
	"PanelRight",
	"PanelRightClose",
	"PanelRightDashed",
	"PanelRightInactive",
	"PanelRightOpen",
	"PanelTop",
	"PanelTopBottomDashed",
	"PanelTopClose",
	"PanelTopDashed",
	"PanelTopInactive",
	"PanelTopOpen",
	"PanelsLeftBottom",
	"PanelsLeftRight",
	"PanelsRightBottom",
	"PanelsTopBottom",
	"PanelsTopLeft",
	"Paperclip",
	"Parentheses",
	"ParkingCircle",
	"ParkingCircleOff",
	"ParkingMeter",
	"ParkingSquare",
	"ParkingSquareOff",
	"PartyPopper",
	"Pause",
	"PauseCircle",
	"PauseOctagon",
	"PawPrint",
	"PcCase",
	"Pen",
	"PenBox",
	"PenLine",
	"PenOff",
	"PenSquare",
	"PenTool",
	"Pencil",
	"PencilLine",
	"PencilOff",
	"PencilRuler",
	"Pentagon",
	"Percent",
	"PercentCircle",
	"PercentDiamond",
	"PercentSquare",
	"PersonStanding",
	"PhilippinePeso",
	"Phone",
	"PhoneCall",
	"PhoneForwarded",
	"PhoneIncoming",
	"PhoneMissed",
	"PhoneOff",
	"PhoneOutgoing",
	"Pi",
	"PiSquare",
	"Piano",
	"Pickaxe",
	"PictureInPicture",
	"PictureInPicture2",
	"PieChart",
	"PiggyBank",
	"Pilcrow",
	"PilcrowLeft",
	"PilcrowRight",
	"PilcrowSquare",
	"Pill",
	"PillBottle",
	"Pin",
	"PinOff",
	"Pipette",
	"Pizza",
	"Plane",
	"PlaneLanding",
	"PlaneTakeoff",
	"Play",
	"PlayCircle",
	"PlaySquare",
	"Plug",
	"Plug2",
	"PlugZap",
	"PlugZap2",
	"Plus",
	"PlusCircle",
	"PlusSquare",
	"PocketKnife",
	"Podcast",
	"Pointer",
	"PointerOff",
	"Popcorn",
	"Popsicle",
	"PoundSterling",
	"Power",
	"PowerCircle",
	"PowerOff",
	"PowerSquare",
	"Presentation",
	"Printer",
	"PrinterCheck",
	"PrinterX",
	"Projector",
	"Proportions",
	"Puzzle",
	"Pyramid",
	"QrCode",
	"Quote",
	"Rabbit",
	"Radar",
	"Radiation",
	"Radical",
	"Radio",
	"RadioOff",
	"RadioReceiver",
	"RadioTower",
	"Radius",
	"Rainbow",
	"Rat",
	"Ratio",
	"Receipt",
	"ReceiptCent",
	"ReceiptEuro",
	"ReceiptIndianRupee",
	"ReceiptJapaneseYen",
	"ReceiptPoundSterling",
	"ReceiptRussianRuble",
	"ReceiptSwissFranc",
	"ReceiptText",
	"ReceiptTurkishLira",
	"RectangleCircle",
	"RectangleEllipsis",
	"RectangleGoggles",
	"RectangleHorizontal",
	"RectangleVertical",
	"Recycle",
	"Redo",
	"Redo2",
	"RedoDot",
	"RefreshCcw",
	"RefreshCcwDot",
	"RefreshCw",
	"RefreshCwOff",
	"Refrigerator",
	"Regex",
	"RemoveFormatting",
	"Repeat",
	"Repeat1",
	"Repeat2",
	"RepeatOff",
	"Replace",
	"ReplaceAll",
	"Reply",
	"ReplyAll",
	"Rewind",
	"Ribbon",
	"Road",
	"Rocket",
	"RockingChair",
	"RollerCoaster",
	"Rose",
	"Rotate3D",
	"RotateCcw",
	"RotateCcwKey",
	"RotateCcwSquare",
	"RotateCw",
	"RotateCwSquare",
	"Route",
	"RouteOff",
	"Router",
	"Rows",
	"Rows2",
	"Rows3",
	"Rows4",
	"Rss",
	"Ruler",
	"RulerDimensionLine",
	"RussianRuble",
	"Sailboat",
	"Salad",
	"Sandwich",
	"Satellite",
	"SatelliteDish",
	"SaudiRiyal",
	"Save",
	"SaveAll",
	"SaveOff",
	"Scale",
	"Scale3D",
	"Scaling",
	"Scan",
	"ScanBarcode",
	"ScanEye",
	"ScanFace",
	"ScanHeart",
	"ScanLine",
	"ScanQrCode",
	"ScanSearch",
	"ScanText",
	"ScatterChart",
	"School",
	"School2",
	"Scissors",
	"ScissorsLineDashed",
	"ScissorsSquare",
	"ScissorsSquareDashedBottom",
	"Scooter",
	"ScreenShare",
	"ScreenShareOff",
	"Scroll",
	"ScrollText",
	"Search",
	"SearchAlert",
	"SearchCheck",
	"SearchCode",
	"SearchSlash",
	"SearchX",
	"Section",
	"Send",
	"SendHorizonal",
	"SendHorizontal",
	"SendToBack",
	"SeparatorHorizontal",
	"SeparatorVertical",
	"Server",
	"ServerCog",
	"ServerCrash",
	"ServerOff",
	"Settings",
	"Settings2",
	"Shapes",
	"Share",
	"Share2",
	"Sheet",
	"Shell",
	"ShelvingUnit",
	"Shield",
	"ShieldAlert",
	"ShieldBan",
	"ShieldCheck",
	"ShieldClose",
	"ShieldCog",
	"ShieldCogCorner",
	"ShieldEllipsis",
	"ShieldHalf",
	"ShieldMinus",
	"ShieldOff",
	"ShieldPlus",
	"ShieldQuestion",
	"ShieldQuestionMark",
	"ShieldUser",
	"ShieldX",
	"Ship",
	"ShipWheel",
	"Shirt",
	"ShoppingBag",
	"ShoppingBasket",
	"ShoppingCart",
	"Shovel",
	"ShowerHead",
	"Shredder",
	"Shrimp",
	"Shrink",
	"Shrub",
	"Shuffle",
	"Sidebar",
	"SidebarClose",
	"SidebarOpen",
	"Sigma",
	"SigmaSquare",
	"Signal",
	"SignalHigh",
	"SignalLow",
	"SignalMedium",
	"SignalZero",
	"Signature",
	"Signpost",
	"SignpostBig",
	"Siren",
	"SkipBack",
	"SkipForward",
	"Skull",
	"Slash",
	"SlashSquare",
	"Slice",
	"Sliders",
	"SlidersHorizontal",
	"SlidersVertical",
	"Smartphone",
	"SmartphoneCharging",
	"SmartphoneNfc",
	"Smile",
	"SmilePlus",
	"Snail",
	"Snowflake",
	"SoapDispenserDroplet",
	"Sofa",
	"SolarPanel",
	"SortAsc",
	"SortDesc",
	"Soup",
	"Space",
	"Spade",
	"Sparkle",
	"Sparkles",
	"Speaker",
	"Speech",
	"SpellCheck",
	"SpellCheck2",
	"Spline",
	"SplinePointer",
	"Split",
	"SplitSquareHorizontal",
	"SplitSquareVertical",
	"Spool",
	"SportShoe",
	"Spotlight",
	"SprayCan",
	"Sprout",
	"Square",
	"SquareActivity",
	"SquareArrowDown",
	"SquareArrowDownLeft",
	"SquareArrowDownRight",
	"SquareArrowLeft",
	"SquareArrowOutDownLeft",
	"SquareArrowOutDownRight",
	"SquareArrowOutUpLeft",
	"SquareArrowOutUpRight",
	"SquareArrowRight",
	"SquareArrowRightEnter",
	"SquareArrowRightExit",
	"SquareArrowUp",
	"SquareArrowUpLeft",
	"SquareArrowUpRight",
	"SquareAsterisk",
	"SquareBottomDashedScissors",
	"SquareCenterlineDashedHorizontal",
	"SquareCenterlineDashedVertical",
	"SquareChartGantt",
	"SquareCheck",
	"SquareCheckBig",
	"SquareChevronDown",
	"SquareChevronLeft",
	"SquareChevronRight",
	"SquareChevronUp",
	"SquareCode",
	"SquareDashed",
	"SquareDashedBottom",
	"SquareDashedBottomCode",
	"SquareDashedKanban",
	"SquareDashedMousePointer",
	"SquareDashedText",
	"SquareDashedTopSolid",
	"SquareDivide",
	"SquareDot",
	"SquareEqual",
	"SquareFunction",
	"SquareGanttChart",
	"SquareKanban",
	"SquareLibrary",
	"SquareM",
	"SquareMenu",
	"SquareMinus",
	"SquareMousePointer",
	"SquareParking",
	"SquareParkingOff",
	"SquarePause",
	"SquarePen",
	"SquarePercent",
	"SquarePi",
	"SquarePilcrow",
	"SquarePlay",
	"SquarePlus",
	"SquarePower",
	"SquareRadical",
	"SquareRoundCorner",
	"SquareScissors",
	"SquareSigma",
	"SquareSlash",
	"SquareSplitHorizontal",
	"SquareSplitVertical",
	"SquareSquare",
	"SquareStack",
	"SquareStar",
	"SquareStop",
	"SquareTerminal",
	"SquareUser",
	"SquareUserRound",
	"SquareX",
	"SquaresExclude",
	"SquaresIntersect",
	"SquaresSubtract",
	"SquaresUnite",
	"Squircle",
	"SquircleDashed",
	"Squirrel",
	"Stamp",
	"Star",
	"StarHalf",
	"StarOff",
	"Stars",
	"StepBack",
	"StepForward",
	"Stethoscope",
	"Sticker",
	"StickyNote",
	"Stone",
	"StopCircle",
	"Store",
	"StretchHorizontal",
	"StretchVertical",
	"Strikethrough",
	"Subscript",
	"Subtitles",
	"Sun",
	"SunDim",
	"SunMedium",
	"SunMoon",
	"SunSnow",
	"Sunrise",
	"Sunset",
	"Superscript",
	"SwatchBook",
	"SwissFranc",
	"SwitchCamera",
	"Sword",
	"Swords",
	"Syringe",
	"Table",
	"Table2",
	"TableCellsMerge",
	"TableCellsSplit",
	"TableColumnsSplit",
	"TableConfig",
	"TableOfContents",
	"TableProperties",
	"TableRowsSplit",
	"Tablet",
	"TabletSmartphone",
	"Tablets",
	"Tag",
	"Tags",
	"Tally1",
	"Tally2",
	"Tally3",
	"Tally4",
	"Tally5",
	"Tangent",
	"Target",
	"Telescope",
	"Tent",
	"TentTree",
	"Terminal",
	"TerminalSquare",
	"TestTube",
	"TestTube2",
	"TestTubeDiagonal",
	"TestTubes",
	"Text",
	"TextAlignCenter",
	"TextAlignEnd",
	"TextAlignJustify",
	"TextAlignStart",
	"TextCursor",
	"TextCursorInput",
	"TextInitial",
	"TextQuote",
	"TextSearch",
	"TextSelect",
	"TextSelection",
	"TextWrap",
	"Theater",
	"Thermometer",
	"ThermometerSnowflake",
	"ThermometerSun",
	"ThumbsDown",
	"ThumbsUp",
	"Ticket",
	"TicketCheck",
	"TicketMinus",
	"TicketPercent",
	"TicketPlus",
	"TicketSlash",
	"TicketX",
	"Tickets",
	"TicketsPlane",
	"Timeline",
	"Timer",
	"TimerOff",
	"TimerReset",
	"ToggleLeft",
	"ToggleRight",
	"Toilet",
	"ToolCase",
	"Toolbox",
	"Tornado",
	"Torus",
	"Touchpad",
	"TouchpadOff",
	"TowelRack",
	"TowerControl",
	"ToyBrick",
	"Tractor",
	"TrafficCone",
	"Train",
	"TrainFront",
	"TrainFrontTunnel",
	"TrainTrack",
	"TramFront",
	"Transgender",
	"Trash",
	"Trash2",
	"TreeDeciduous",
	"TreePalm",
	"TreePine",
	"Trees",
	"TrendingDown",
	"TrendingUp",
	"TrendingUpDown",
	"Triangle",
	"TriangleAlert",
	"TriangleDashed",
	"TriangleRight",
	"Trophy",
	"Truck",
	"TruckElectric",
	"TurkishLira",
	"Turntable",
	"Turtle",
	"Tv",
	"Tv2",
	"TvMinimal",
	"TvMinimalPlay",
	"Type",
	"TypeOutline",
	"Umbrella",
	"UmbrellaOff",
	"Underline",
	"Undo",
	"Undo2",
	"UndoDot",
	"UnfoldHorizontal",
	"UnfoldVertical",
	"Ungroup",
	"University",
	"Unlink",
	"Unlink2",
	"Unlock",
	"UnlockKeyhole",
	"Unplug",
	"Upload",
	"UploadCloud",
	"Usb",
	"User",
	"User2",
	"UserCheck",
	"UserCheck2",
	"UserCircle",
	"UserCircle2",
	"UserCog",
	"UserCog2",
	"UserKey",
	"UserLock",
	"UserMinus",
	"UserMinus2",
	"UserPen",
	"UserPlus",
	"UserPlus2",
	"UserRound",
	"UserRoundCheck",
	"UserRoundCog",
	"UserRoundKey",
	"UserRoundMinus",
	"UserRoundPen",
	"UserRoundPlus",
	"UserRoundSearch",
	"UserRoundX",
	"UserSearch",
	"UserSquare",
	"UserSquare2",
	"UserStar",
	"UserX",
	"UserX2",
	"Users",
	"Users2",
	"UsersRound",
	"Utensils",
	"UtensilsCrossed",
	"UtilityPole",
	"Van",
	"Variable",
	"Vault",
	"VectorSquare",
	"Vegan",
	"VenetianMask",
	"Venus",
	"VenusAndMars",
	"Verified",
	"Vibrate",
	"VibrateOff",
	"Video",
	"VideoOff",
	"Videotape",
	"View",
	"Voicemail",
	"Volleyball",
	"Volume",
	"Volume1",
	"Volume2",
	"VolumeOff",
	"VolumeX",
	"Vote",
	"Wallet",
	"Wallet2",
	"WalletCards",
	"WalletMinimal",
	"Wallpaper",
	"Wand",
	"Wand2",
	"WandSparkles",
	"Warehouse",
	"WashingMachine",
	"Watch",
	"Waves",
	"WavesArrowDown",
	"WavesArrowUp",
	"WavesHorizontal",
	"WavesLadder",
	"WavesVertical",
	"Waypoints",
	"Webcam",
	"Webhook",
	"WebhookOff",
	"Weight",
	"WeightTilde",
	"Wheat",
	"WheatOff",
	"WholeWord",
	"Wifi",
	"WifiCog",
	"WifiHigh",
	"WifiLow",
	"WifiOff",
	"WifiPen",
	"WifiSync",
	"WifiZero",
	"Wind",
	"WindArrowDown",
	"Wine",
	"WineOff",
	"Workflow",
	"Worm",
	"WrapText",
	"Wrench",
	"X",
	"XCircle",
	"XLineTop",
	"XOctagon",
	"XSquare",
	"Zap",
	"ZapOff",
	"ZodiacAquarius",
	"ZodiacAries",
	"ZodiacCancer",
	"ZodiacCapricorn",
	"ZodiacGemini",
	"ZodiacLeo",
	"ZodiacLibra",
	"ZodiacOphiuchus",
	"ZodiacPisces",
	"ZodiacSagittarius",
	"ZodiacScorpio",
	"ZodiacTaurus",
	"ZodiacVirgo",
	"ZoomIn",
	"ZoomOut"
];
//#endregion
//#region src/icons/cool_icon_keys.ts
var coolIconKeys = [
	"Rows4",
	"Video",
	"Plane",
	"LayoutDashboard",
	"LayoutGrid",
	"Square",
	"Snowflake",
	"CircleDashed",
	"AlignCenterHorizontal",
	"Disc",
	"Infinity",
	"Maximize",
	"Play",
	"Sparkles",
	"Music",
	"Wallet",
	"Layout",
	"Zap",
	"Circle",
	"Coffee",
	"ImageOff",
	"Sun",
	"Cable",
	"CalendarDays",
	"Dices",
	"Shapes",
	"Cloud",
	"Palette",
	"CreditCard",
	"Headphones",
	"Egg",
	"Disc3",
	"Flag",
	"Bandage",
	"Thermometer",
	"Hexagon",
	"Network",
	"Library",
	"Pizza",
	"Cpu",
	"Plug",
	"Files",
	"Globe",
	"SignalZero",
	"Gamepad2",
	"CloudLightning",
	"ListTree",
	"Command"
];
//#endregion
//#region src/icons/Icon.tsx
/**
* Standardized icon size map in px.
* Use with direct Lucide imports: `<Database size={iconSize.small} />`
*/
var iconSize = {
	smallest: 16,
	small: 20,
	medium: 24,
	large: 28
};
var colorClassesMapping = {
	inherit: "",
	primary: "text-primary",
	success: "text-green-500",
	warning: "text-yellow-500",
	secondary: "text-secondary",
	disabled: "text-text-disabled dark:text-text-disabled-dark",
	error: "text-red-500"
};
//#endregion
//#region src/icons/GitHubIcon.tsx
function GitHubIcon({ size = 24, color, className, onClick, style }) {
	const px = typeof size === "number" ? size : iconSize[size];
	return /* @__PURE__ */ jsx("svg", {
		xmlns: "http://www.w3.org/2000/svg",
		className: cls(color ? colorClassesMapping[color] : "", className),
		onClick,
		style,
		fill: "currentColor",
		width: px,
		height: px,
		viewBox: "0 0 24 24",
		children: /* @__PURE__ */ jsx("path", { d: "M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" })
	});
}
//#endregion
//#region src/icons/HandleIcon.tsx
function HandleIcon({ size = 24, color, className, onClick, style }) {
	const px = typeof size === "number" ? size : iconSize[size];
	return /* @__PURE__ */ jsxs("svg", {
		xmlns: "http://www.w3.org/2000/svg",
		className: cls(color ? colorClassesMapping[color] : "", className),
		onClick,
		style,
		width: px,
		height: px,
		viewBox: "0 0 100 100",
		fill: "none",
		children: [
			/* @__PURE__ */ jsx("circle", {
				cx: "28",
				cy: "50",
				r: "9",
				fill: "currentColor"
			}),
			/* @__PURE__ */ jsx("circle", {
				cx: "28",
				cy: "21",
				r: "9",
				fill: "currentColor"
			}),
			/* @__PURE__ */ jsx("circle", {
				cx: "71",
				cy: "21",
				r: "9",
				fill: "currentColor"
			}),
			/* @__PURE__ */ jsx("circle", {
				cx: "71",
				cy: "50",
				r: "9",
				fill: "currentColor"
			}),
			/* @__PURE__ */ jsx("circle", {
				cx: "71",
				cy: "78",
				r: "9",
				fill: "currentColor"
			}),
			/* @__PURE__ */ jsx("circle", {
				cx: "28",
				cy: "78",
				r: "9",
				fill: "currentColor"
			})
		]
	});
}
//#endregion
//#region src/icons/LucideIconByName.tsx
var loaded;
var pending;
/**
* The full lucide icon map, fetched on first use and cached for the session.
*
* Prefer {@link LucideIconByName}. This is here for callers that need to know
* whether a name resolves, such as the icon picker's grid.
*/
function loadLucideIcons() {
	if (loaded) return Promise.resolve(loaded);
	pending ??= import("lucide-react").then(({ icons }) => {
		loaded = icons;
		return loaded;
	});
	return pending;
}
/** The map if it has already been fetched, otherwise `undefined`. Never fetches. */
function getLoadedLucideIcons() {
	return loaded;
}
/**
* Subscribe to the icon map.
*
* Returns it synchronously once loaded — including on the very first render of
* every icon after the first one anywhere, which is what stops a page full of
* icons from each flashing its placeholder.
*/
function useLucideIcons() {
	const [map, setMap] = React.useState(loaded);
	React.useEffect(() => {
		if (map) return;
		let cancelled = false;
		loadLucideIcons().then((resolved) => {
			if (!cancelled) setMap(resolved);
		}).catch((error) => console.error("[rebase] could not load the icon set", error));
		return () => {
			cancelled = true;
		};
	}, [map]);
	return map;
}
function toPascalCase(str) {
	return str.split(/[-_]/).map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join("");
}
/**
* The same resolution order the two call sites used against the eager map:
* exact name, then PascalCase, then `CircleAlert` as the visible "unknown icon".
*/
function resolveLucideIcon(icons, name) {
	return icons[name] ?? icons[toPascalCase(name)] ?? icons.CircleAlert;
}
/**
* An icon named at runtime. Renders `fallback` until the icon set arrives.
*/
var LucideIconByName = React.memo(function LucideIconByName({ name, size, className, fallback }) {
	const icons = useLucideIcons();
	const Icon = icons ? resolveLucideIcon(icons, name) : void 0;
	if (!Icon) {
		if (fallback !== void 0) return /* @__PURE__ */ jsx(Fragment, { children: fallback });
		return /* @__PURE__ */ jsx("span", {
			"aria-hidden": true,
			className: "inline-block shrink-0",
			style: {
				width: size,
				height: size
			}
		});
	}
	return /* @__PURE__ */ jsx(Icon, {
		size,
		className
	});
});
//#endregion
//#region src/components/Alert.tsx
var getSizeClasses = (size) => {
	switch (size) {
		case "small": return "px-3 py-1.5 text-xs gap-2 rounded-md";
		case "large": return "px-4 py-3.5 text-sm gap-3 rounded-lg";
		default: return "px-3.5 py-2.5 text-sm gap-2.5 rounded-lg";
	}
};
var getIconSize = (size) => size === "small" ? 16 : 18;
var getColorClasses = (severity) => {
	switch (severity) {
		case "error": return "bg-red-500/8 dark:bg-red-500/12 text-red-800 dark:text-red-200 border border-red-500/20 dark:border-red-500/25";
		case "warning": return "bg-amber-500/8 dark:bg-amber-500/12 text-amber-800 dark:text-amber-200 border border-amber-500/20 dark:border-amber-500/25";
		case "info": return "bg-blue-500/8 dark:bg-blue-500/12 text-blue-800 dark:text-blue-200 border border-blue-500/20 dark:border-blue-500/25";
		case "success": return "bg-emerald-500/8 dark:bg-emerald-500/12 text-emerald-800 dark:text-emerald-200 border border-emerald-500/20 dark:border-emerald-500/25";
		default: return "bg-surface-field text-text-primary dark:text-text-primary-dark border border-hairline";
	}
};
var getIconColor = (severity) => {
	switch (severity) {
		case "error": return "text-red-500 dark:text-red-400";
		case "warning": return "text-amber-500 dark:text-amber-400";
		case "info": return "text-blue-500 dark:text-blue-400";
		case "success": return "text-emerald-500 dark:text-emerald-400";
		default: return "text-surface-accent-500 dark:text-surface-accent-400";
	}
};
var getIcon = (severity, size, className) => {
	switch (severity) {
		case "error": return /* @__PURE__ */ jsx(AlertCircleIcon, {
			size,
			className
		});
		case "warning": return /* @__PURE__ */ jsx(AlertTriangleIcon, {
			size,
			className
		});
		case "success": return /* @__PURE__ */ jsx(CheckCircleIcon, {
			size,
			className
		});
		default: return /* @__PURE__ */ jsx(InfoIcon, {
			size,
			className
		});
	}
};
var Alert = ({ children, onDismiss, color = "info", size = "medium", action, outerClassName, className, style }) => {
	const classes = getColorClasses(color);
	const iconSize = getIconSize(size);
	return /* @__PURE__ */ jsxs("div", {
		style,
		className: cls(getSizeClasses(size), "w-full", "font-medium leading-snug", "flex items-start", classes, outerClassName),
		children: [
			/* @__PURE__ */ jsx("span", {
				className: cls("shrink-0 mt-px", getIconColor(color)),
				children: getIcon(color, iconSize, "")
			}),
			/* @__PURE__ */ jsx("div", {
				className: cls("grow min-w-0 self-center", className),
				children
			}),
			action,
			onDismiss && /* @__PURE__ */ jsx("button", {
				type: "button",
				"aria-label": "Dismiss",
				className: cls("shrink-0 rounded p-0.5 opacity-60 transition-opacity hover:opacity-100", "focus:outline-none focus-visible:ring-2 focus-visible:ring-current/40"),
				onClick: onDismiss,
				children: /* @__PURE__ */ jsx(XIcon, { size: iconSize - 2 })
			})
		]
	});
};
//#endregion
//#region src/components/Avatar.tsx
var AvatarInner = ({ src, alt, children, className, style, outerClassName, hover = true, ...props }, ref) => {
	const [isImageError, setIsImageError] = useState(false);
	const handleImageError = () => {
		setIsImageError(true);
	};
	return /* @__PURE__ */ jsx("button", {
		ref,
		style,
		...props,
		className: cls("rounded-full flex items-center justify-center overflow-hidden", "p-1 w-12 h-12 min-w-12 min-h-12", hover && "transition-colors duration-150 hover:bg-surface-raised-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50", outerClassName),
		children: src && !isImageError ? /* @__PURE__ */ jsx("img", {
			className: cls("bg-surface-raised", "w-full h-full object-cover rounded-full", className),
			src,
			alt,
			onError: handleImageError
		}) : /* @__PURE__ */ jsx("span", {
			className: cls("bg-surface-raised", "flex items-center justify-center", "w-full h-full py-1.5 text-lg font-medium text-surface-accent-900 dark:text-text-primary-dark rounded-full", className),
			children
		})
	});
};
var Avatar = React.forwardRef(AvatarInner);
//#endregion
//#region src/components/BooleanSwitch.tsx
/**
* Switch geometry, one row per size: track w x h, knob k, and the knob's
* off/on x-offsets. `on` is always `w - k - inset` so the knob is inset by the
* same amount at both ends and vertically centred.
*
* This is a table rather than inline `cls({...})` conditions on purpose. The
* offsets used to be computed object keys — `[value ? "translate-x-[24px]" :
* "translate-x-[3px]"]: size === "large"` — and `large` and `medium` both
* produced the key `translate-x-[3px]` when off. The later literal overwrote
* the earlier, so the off-state class was silently dropped for `large` and the
* knob sat flush against the edge.
*/
var SWITCH_GEOMETRY = {
	smallest: {
		track: "w-[34px] h-[18px] min-w-[34px] min-h-[18px]",
		knob: "w-[16px] h-[16px]",
		off: "translate-x-[1px]",
		on: "translate-x-[17px]",
		bar: "w-[16px] h-[6px]",
		barX: "translate-x-[9px]"
	},
	small: {
		track: "w-[38px] h-[22px] min-w-[38px] min-h-[22px]",
		knob: "w-[18px] h-[18px]",
		off: "translate-x-[2px]",
		on: "translate-x-[18px]",
		bar: "w-[18px] h-[8px]",
		barX: "translate-x-[10px]"
	},
	medium: {
		track: "w-[44px] h-[26px] min-w-[44px] min-h-[26px]",
		knob: "w-[20px] h-[20px]",
		off: "translate-x-[3px]",
		on: "translate-x-[21px]",
		bar: "w-[20px] h-[10px]",
		barX: "translate-x-[12px]"
	},
	large: {
		track: "w-[48px] h-[28px] min-w-[48px] min-h-[28px]",
		knob: "w-[22px] h-[22px]",
		off: "translate-x-[3px]",
		on: "translate-x-[23px]",
		bar: "w-[22px] h-[11px]",
		barX: "translate-x-[13px]"
	}
};
var BooleanSwitch = React.forwardRef(function BooleanSwitch({ value, allowIndeterminate, className, onValueChange, disabled = false, size = "medium", ...props }, ref) {
	return /* @__PURE__ */ jsxs("button", {
		type: "button",
		role: "switch",
		"aria-checked": allowIndeterminate && (value === null || value === void 0) ? "mixed" : !!value,
		"aria-disabled": disabled || void 0,
		ref,
		tabIndex: disabled ? -1 : void 0,
		onClick: disabled ? (e) => e.preventDefault() : (e) => {
			e.preventDefault();
			if (allowIndeterminate) if (value === null || value === void 0) onValueChange?.(true);
			else if (value) onValueChange?.(false);
			else onValueChange?.(null);
			else onValueChange?.(!value);
		},
		className: cls(SWITCH_GEOMETRY[size].track, "outline-none outline-hidden rounded-full relative shadow-sm", disabled ? value ? "bg-primary/40 ring-1 ring-transparent" : "bg-surface-field ring-1 ring-hairline" : value ? "bg-primary ring-1 ring-primary" : "bg-surface-active ring-1 ring-hairline-strong", className),
		...props,
		children: [allowIndeterminate && (value === null || value === void 0) && /* @__PURE__ */ jsx("div", { className: cls("block rounded-full transition-transform duration-100 ease-out transform will-change-auto shadow-sm", "bg-surface-400", SWITCH_GEOMETRY[size].bar, SWITCH_GEOMETRY[size].barX) }, "knob"), !(allowIndeterminate && (value === null || value === void 0)) && /* @__PURE__ */ jsx("div", { className: cls("block rounded-full transition-transform duration-100 ease-out transform will-change-auto shadow-sm", disabled ? "bg-surface-300 dark:bg-surface-600" : value ? "bg-white shadow" : "bg-surface-500 dark:bg-surface-400", SWITCH_GEOMETRY[size].knob, value ? SWITCH_GEOMETRY[size].on : SWITCH_GEOMETRY[size].off) }, "knob")]
	});
});
//#endregion
//#region src/components/BooleanSwitchWithLabel.tsx
/**
* Simple boolean switch.
*
*/
var BooleanSwitchWithLabel = function BooleanSwitchWithLabel({ value, position = "end", size = "medium", invisible, onValueChange, error, label, autoFocus, disabled, className, fullWidth = true, inputClassName, ...props }) {
	const ref = React.useRef(null);
	const refInput = React.useRef(null);
	const switchLabelId = React.useId();
	const [_, setFocused] = React.useState(autoFocus);
	const onFocus = () => setFocused(true);
	const onBlur = () => setFocused(false);
	React.useEffect(() => {
		if (autoFocus) {}
	}, []);
	const focus = document.activeElement === refInput?.current || document.activeElement === ref?.current;
	return /* @__PURE__ */ jsxs("div", {
		ref,
		onFocus,
		onBlur,
		role: "switch",
		"aria-checked": props.allowIndeterminate && (value === null || value === void 0) ? "mixed" : !!value,
		"aria-disabled": disabled || void 0,
		"aria-labelledby": label ? switchLabelId : void 0,
		tabIndex: -1,
		className: cls(!invisible && "bg-surface-field border border-hairline", !invisible && (disabled ? "bg-surface-field/60" : "transition-colors duration-150 hover:bg-surface-field-hover"), disabled ? "cursor-default" : "cursor-pointer", "rounded-lg max-w-full justify-between box-border relative inline-flex items-center", !invisible && focus && !disabled ? focusedClasses : "", error ? "text-red-500 dark:text-red-600" : focus && !disabled ? "text-primary" : !disabled ? "text-text-primary dark:text-text-primary-dark" : "text-text-secondary dark:text-text-secondary-dark", controlHeightMixin[size], size === "small" || size === "smallest" ? "pl-2" : "pl-4", size === "small" || size === "smallest" ? "pr-4" : "pr-6", position === "end" ? "flex-row-reverse" : "flex-row", fullWidth ? "w-full" : "", className),
		onClick: disabled ? void 0 : (e) => {
			if (props.allowIndeterminate) {
				const onChange = onValueChange;
				if (value === null || value === void 0) onChange?.(true);
				else if (value) onChange?.(false);
				else onChange?.(null);
			} else onValueChange?.(!value);
		},
		children: [/* @__PURE__ */ jsx(BooleanSwitch, {
			value,
			ref: refInput,
			size,
			className: cls(invisible && focus ? focusedClasses : "", inputClassName),
			disabled,
			...props
		}), /* @__PURE__ */ jsx("div", {
			id: switchLabelId,
			className: cls("grow", position === "end" ? "mr-4" : "ml-4", size === "small" || size === "smallest" ? "text-sm" : "text-base"),
			children: label
		})]
	});
};
//#endregion
//#region src/components/Button.tsx
var ButtonInner = React.memo(React.forwardRef(({ children, className, variant = "filled", disabled = false, size = "medium", startIcon = null, fullWidth = false, component: Component, color = "neutral", ...props }, ref) => {
	const baseClasses = "typography-button h-fit rounded-lg whitespace-nowrap inline-flex items-center justify-center p-2 px-4 focus:outline-none transition-colors ease-in-out duration-150 gap-2";
	const buttonClasses = cls({
		"w-full": fullWidth,
		"w-fit": !fullWidth,
		"border border-primary bg-primary focus:ring-primary text-white hover:text-white hover:brightness-105": variant === "filled" && color === "primary" && !disabled,
		"border border-secondary bg-secondary focus:ring-secondary text-white hover:text-white hover:brightness-105": variant === "filled" && color === "secondary" && !disabled,
		"border border-red-600 bg-red-600 hover:bg-red-700 focus:ring-red-600 text-white hover:text-white": variant === "filled" && color === "error" && !disabled,
		"border border-hairline-strong bg-surface-raised hover:bg-surface-raised-hover text-text-primary hover:text-text-primary dark:text-text-primary-dark hover:dark:text-text-primary-dark": variant === "filled" && color === "text" && !disabled,
		"border border-transparent bg-surface-raised hover:bg-surface-raised-hover text-text-primary hover:text-text-primary dark:text-text-primary-dark hover:dark:text-text-primary-dark": variant === "filled" && color === "neutral" && !disabled,
		"border border-transparent text-primary hover:text-primary hover:bg-surface-hover": variant === "text" && color === "primary" && !disabled,
		"border border-transparent text-secondary hover:text-secondary hover:bg-surface-hover": variant === "text" && color === "secondary" && !disabled,
		"border border-transparent text-red-600 hover:text-red-600 hover:bg-red-600 hover:bg-opacity-10 hover:bg-red-600/10": variant === "text" && color === "error" && !disabled,
		"border border-transparent text-text-primary hover:text-text-primary dark:text-text-primary-dark hover:dark:text-text-primary-dark hover:bg-surface-hover": variant === "text" && color === "text" && !disabled,
		"border border-transparent text-text-primary hover:text-text-primary dark:text-text-primary-dark dark:hover:text-text-primary-dark hover:bg-surface-hover": variant === "text" && color === "neutral" && !disabled,
		"border border-primary text-primary hover:text-primary hover:bg-primary-bg hover:bg-primary/10": variant === "outlined" && color === "primary" && !disabled,
		"border border-secondary text-secondary hover:text-secondary hover:bg-secondary-bg": variant === "outlined" && color === "secondary" && !disabled,
		"border border-red-500 text-red-600 hover:text-white hover:bg-red-600": variant === "outlined" && color === "error" && !disabled,
		"border border-hairline-strong text-text-primary hover:text-text-primary dark:text-text-primary-dark hover:bg-surface-hover": variant === "outlined" && color === "text" && !disabled,
		"border border-hairline-strong text-text-primary dark:text-text-primary-dark hover:bg-surface-hover": variant === "outlined" && color === "neutral" && !disabled,
		"text-text-disabled dark:text-text-disabled-dark": disabled,
		"border border-transparent opacity-50": variant === "text" && disabled,
		"border border-hairline-strong opacity-50": variant === "outlined" && disabled,
		"border border-transparent bg-surface-raised": variant === "filled" && disabled
	});
	const sizeClasses = cls("py-0", controlHeightMixin[size], controlPaddingMixin[size]);
	const iconColorClass = (color === "neutral" || color === "text") && !disabled ? "[&>svg]:text-surface-accent-500 dark:[&>svg]:text-surface-accent-300" : "";
	if (Component) return /* @__PURE__ */ jsxs(Component, {
		ref,
		onClick: props.onClick,
		className: cls(startIcon ? "pl-3" : "", baseClasses, buttonClasses, sizeClasses, iconColorClass, className),
		...props,
		children: [startIcon, children]
	});
	return /* @__PURE__ */ jsxs("button", {
		ref,
		type: props.type ?? "button",
		onClick: props.onClick,
		className: cls(startIcon ? "pl-3" : "", baseClasses, buttonClasses, sizeClasses, iconColorClass, className),
		disabled,
		"data-variant": variant,
		"data-size": size,
		...props,
		children: [startIcon, children]
	});
}));
ButtonInner.displayName = "Button";
var Button = ButtonInner;
//#endregion
//#region src/components/Card.tsx
var Card = React.forwardRef(({ children, className, onClick, style, ...props }, ref) => {
	return /* @__PURE__ */ jsx("div", {
		ref,
		onKeyPress: useCallback((e) => {
			if (e.key === "Enter" || e.key === " ") onClick?.();
		}, [onClick]),
		role: onClick ? "button" : void 0,
		tabIndex: onClick ? 0 : void 0,
		onClick,
		className: cls(cardMixin, onClick && "hover:bg-surface-card-hover cursor-pointer transition-colors duration-150", className),
		style,
		...props,
		children
	});
});
Card.displayName = "Card";
//#endregion
//#region src/components/Container.tsx
var containerMaxWidths = {
	xs: "max-w-xs",
	sm: "max-w-sm",
	md: "max-w-md",
	lg: "max-w-lg",
	xl: "max-w-xl",
	"2xl": "max-w-2xl",
	"3xl": "max-w-3xl",
	"4xl": "max-w-4xl",
	"5xl": "max-w-5xl",
	"6xl": "max-w-6xl",
	"7xl": "max-w-7xl"
};
var ContainerInner = ({ children, className, style, maxWidth = "7xl" }, ref) => {
	return /* @__PURE__ */ jsx("div", {
		ref,
		className: cls("mx-auto px-3 md:px-4 lg:px-6", maxWidth ? containerMaxWidths[maxWidth] : "", className),
		style,
		children
	});
};
var Container = React.forwardRef(ContainerInner);
//#endregion
//#region src/components/CenteredView.tsx
var CenteredView = React.forwardRef(({ children, maxWidth, outerClassName, className, fullScreen, ...rest }, ref) => {
	return /* @__PURE__ */ jsx("div", {
		ref,
		className: cls("flex flex-col grow", fullScreen ? "h-screen" : "h-full", outerClassName),
		...rest,
		children: /* @__PURE__ */ jsx(Container, {
			className: cls("m-auto", className),
			maxWidth,
			children
		})
	});
});
CenteredView.displayName = "CenteredView";
//#endregion
//#region src/components/CircularProgress.tsx
function CircularProgress({ size = "medium", className }) {
	let sizeClasses = "";
	if (size === "smallest") sizeClasses = "w-4 h-4";
	else if (size === "small") sizeClasses = "w-6 h-6";
	else if (size === "medium") sizeClasses = "w-8 h-8 m-1";
	else sizeClasses = "w-10 h-10 m-1";
	let borderClasses = "";
	if (size === "smallest") borderClasses = "border-2";
	else if (size === "small") borderClasses = "border-2";
	else if (size === "medium") borderClasses = "border-[3px]";
	else borderClasses = "border-4";
	return /* @__PURE__ */ jsx("div", {
		className: cls(sizeClasses, borderClasses, "inline-block shrink-0 rounded-full border-solid align-[-0.125em]", "animate-[spin_0.7s_linear_infinite] motion-reduce:animate-[spin_1.5s_linear_infinite]", "border-hairline-strong border-t-primary dark:border-t-primary", className),
		role: "status",
		children: /* @__PURE__ */ jsx("span", {
			className: "absolute! -m-px! h-px! w-px! overflow-hidden! whitespace-nowrap! border-0! p-0! [clip:rect(0,0,0,0)]!",
			children: "Loading..."
		})
	});
}
//#endregion
//#region src/components/Typography.tsx
var typographyVariants = {
	h1: "h1",
	h2: "h2",
	h3: "h3",
	h4: "h4",
	h5: "h5",
	h6: "h6",
	subtitle1: "h6",
	subtitle2: "h6",
	label: "label",
	body1: "p",
	body2: "p",
	inherit: "p",
	caption: "p",
	button: "span",
	lead: "p",
	micro: "span",
	mono: "span",
	stat: "span"
};
var colorToClasses = {
	inherit: "text-inherit",
	initial: "text-current",
	primary: "text-text-primary dark:text-text-primary-dark",
	secondary: "text-text-secondary dark:text-text-secondary-dark",
	disabled: "text-text-disabled dark:text-text-disabled-dark",
	error: "text-red-600 dark:text-red-500"
};
var gutterBottomClasses = {
	h1: "mb-5",
	h2: "mb-4",
	h3: "mb-4",
	h4: "mb-4",
	h5: "mb-3",
	h6: "mb-3",
	subtitle1: "mb-3",
	subtitle2: "mb-3",
	body1: "mb-3",
	body2: "mb-3",
	inherit: "mb-3",
	caption: "mb-2",
	button: "mb-2",
	label: "mb-2",
	lead: "mb-4",
	micro: "mb-1",
	mono: "mb-2",
	stat: "mb-1"
};
var variantToClasses = {
	h1: "typography-h1",
	h2: "typography-h2",
	h3: "typography-h3",
	h4: "typography-h4",
	h5: "typography-h5",
	h6: "typography-h6",
	subtitle1: "typography-subtitle1",
	subtitle2: "typography-subtitle2",
	body1: "typography-body1",
	body2: "typography-body2",
	label: "typography-label",
	inherit: "typography-inherit",
	caption: "typography-caption",
	button: "typography-button",
	lead: "typography-lead",
	micro: "typography-micro",
	mono: "typography-mono",
	stat: "typography-stat"
};
function Typography({ align = "inherit", color = "primary", children, className, component, gutterBottom = false, noWrap = false, paragraph = false, variant = "body1", variantMapping = typographyVariants, style, onClick, ...other }) {
	return /* @__PURE__ */ jsx(component || (paragraph ? "p" : variantMapping[variant] || typographyVariants[variant]) || "span", {
		className: cls(variantToClasses[variant], color ? colorToClasses[color] : "", align !== "inherit" && `text-${align}`, gutterBottom && gutterBottomClasses[variant], noWrap && "truncate", paragraph && "mb-3", className),
		onClick,
		style,
		...other,
		children
	});
}
//#endregion
//#region src/components/CircularProgressCenter.tsx
/**
*
* @param text
* @param props

* @ignore
*/
function CircularProgressCenter({ text, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: "flex w-full h-screen max-h-full max-w-full gap-4",
		children: /* @__PURE__ */ jsxs("div", {
			className: "m-auto flex flex-col gap-2 items-center",
			children: [/* @__PURE__ */ jsx(CircularProgress, { ...props }), text && /* @__PURE__ */ jsx(Typography, {
				color: "secondary",
				variant: "caption",
				className: "text-center",
				children: text
			})]
		})
	});
}
//#endregion
//#region src/util/lazy_chunk.ts
/**
* Dynamic imports that survive a redeploy.
*
* A built SPA names its chunks by content hash and a deploy replaces the whole
* `assets/` directory, so a tab opened before the deploy still holds the *old*
* entry chunk. The first lazy route that tab opens asks for a hash that no
* longer exists on the server, the import rejects, and the view is dead until
* the user thinks to reload — which nothing on screen tells them to do. The
* browser's own wording ("Failed to fetch dynamically imported module: …") reads
* like a build bug, so this is usually reported as one.
*
* `loadChunk` retries once — that covers a transient network failure — and then
* gives up with an error tagged as a chunk load failure, so `ErrorBoundary` can
* offer a reload instead of printing the browser's message.
*/
/**
* Marker property set on the error thrown after a failed retry. Read by
* `isChunkLoadError`, which is what the UI matches on.
*/
var CHUNK_LOAD_ERROR_FLAG = "rebaseChunkLoadError";
/**
* Each engine words this differently, and the message is the only signal — none
* of them use a distinguishable error type. The MIME variants matter as much as
* the fetch ones: a server whose SPA fallback answers a missing `/assets/x.js`
* with `index.html` returns 200 HTML, and the browser rejects it as a module.
*/
var CHUNK_ERROR_PATTERNS = [
	"failed to fetch dynamically imported module",
	"error loading dynamically imported module",
	"importing a module script failed",
	"failed to load module script",
	"expected a javascript",
	"unable to preload css"
];
/**
* Does this error mean a chunk could not be loaded — rather than the chunk's
* own code throwing once it did load?
*
* Errors raised anywhere are matched, not just ones `loadChunk` produced: React
* `lazy()` calls that still import directly, and Vite's own CSS preloads, fail
* the same way and deserve the same recovery.
*/
function isChunkLoadError(error) {
	if (!error || typeof error !== "object") return false;
	if (error[CHUNK_LOAD_ERROR_FLAG] === true) return true;
	const message = error.message;
	if (typeof message !== "string") return false;
	const lower = message.toLowerCase();
	return CHUNK_ERROR_PATTERNS.some((pattern) => lower.includes(pattern));
}
function chunkLoadError(cause) {
	const error = /* @__PURE__ */ new Error("This app was updated while the tab was open, so part of it could not be loaded. Reload to continue.");
	Object.assign(error, {
		[CHUNK_LOAD_ERROR_FLAG]: true,
		cause
	});
	return error;
}
/**
* Run a dynamic import, retrying once before declaring the chunk unreachable.
*
* Errors that are not chunk load failures — the imported module throwing while
* it evaluates, say — are re-thrown untouched and never retried: running a
* module's side effects twice is worse than the original failure.
*/
async function loadChunk(loader) {
	try {
		return await loader();
	} catch (error) {
		if (!isChunkLoadError(error)) throw error;
		await new Promise((resolve) => setTimeout(resolve, 250));
		try {
			return await loader();
		} catch (retryError) {
			throw chunkLoadError(retryError);
		}
	}
}
/**
* `React.lazy` with the retry above. A drop-in replacement — use it for every
* lazy route or dialog, since any one of them can be the first chunk a stale
* tab reaches for.
*
* Note that React caches the rejection: once a lazy component has failed, later
* renders re-throw without calling the loader again. Resetting an error boundary
* therefore cannot recover it, which is why the boundary offers a reload.
*/
function lazyChunk(loader) {
	return React.lazy(() => loadChunk(loader));
}
//#endregion
//#region src/components/ErrorBoundary.tsx
/**
* The one error a user can fix themselves. A deploy replaces the hashed chunk
* files, so a tab opened before it cannot load any lazy view it has not already
* fetched — with the browser's raw message ("Failed to fetch dynamically
* imported module: …") this reads as a broken build rather than a stale tab.
*/
var CHUNK_ERROR_DESCRIPTION = "This app was updated while the tab was open, so part of it could not be loaded. Reload to continue.";
/**
* Checks whether the error message relates to missing permissions or
* authorization failures. Used to show a friendlier message in fullPage mode.
*/
function isPermissionError(error) {
	if (!error) return false;
	const msg = error.message?.toLowerCase() ?? "";
	const code = error.code?.toLowerCase() ?? "";
	return msg.includes("permission") || msg.includes("insufficient") || msg.includes("unauthorized") || msg.includes("forbidden") || msg.includes("access denied") || code === "permission_denied" || code === "insufficient_permissions" || error.status === 403;
}
var ErrorBoundary = class extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			error: null,
			showDetails: false
		};
	}
	static getDerivedStateFromError(error) {
		return { error };
	}
	componentDidCatch(error, _errorInfo) {
		console.error(error);
	}
	handleReset = () => {
		this.setState({
			error: null,
			showDetails: false
		});
		this.props.onReset?.();
	};
	handleReload = () => {
		window.location.reload();
	};
	toggleDetails = () => {
		this.setState((prev) => ({ showDetails: !prev.showDetails }));
	};
	render() {
		if (this.state.error) {
			if (this.props.fullPage) return this.renderFullPage();
			return this.renderInline();
		}
		return this.props.children;
	}
	renderInline() {
		const isStaleChunk = isChunkLoadError(this.state.error);
		return /* @__PURE__ */ jsxs("div", {
			className: "flex flex-col m-2 items-start",
			children: [
				/* @__PURE__ */ jsxs("div", {
					className: "flex items-center m-2",
					children: [isStaleChunk ? /* @__PURE__ */ jsx(RefreshCwIcon$1, {
						className: "text-text-secondary dark:text-text-secondary-dark",
						size: iconSize.small
					}) : /* @__PURE__ */ jsx(AlertCircleIcon$1, {
						className: "text-red-500 dark:text-red-400",
						size: iconSize.small
					}), /* @__PURE__ */ jsx("div", {
						className: "ml-4",
						children: isStaleChunk ? "New version available" : "Error"
					})]
				}),
				/* @__PURE__ */ jsx(Typography, {
					variant: "caption",
					children: isStaleChunk ? CHUNK_ERROR_DESCRIPTION : this.state.error?.message ?? "See the error in the console"
				}),
				isStaleChunk && /* @__PURE__ */ jsxs(Button, {
					variant: "outlined",
					color: "neutral",
					size: "small",
					className: "mt-3 ml-2",
					onClick: this.handleReload,
					children: [/* @__PURE__ */ jsx(RefreshCwIcon$1, { size: 16 }), "Reload"]
				})
			]
		});
	}
	renderFullPage() {
		const { error, showDetails } = this.state;
		const isPermission = isPermissionError(error);
		const isStaleChunk = !isPermission && isChunkLoadError(error);
		const Icon = isPermission ? ShieldAlertIcon : isStaleChunk ? RefreshCwIcon$1 : AlertCircleIcon$1;
		const title = isPermission ? "Access denied" : isStaleChunk ? "New version available" : "Something went wrong";
		const description = isPermission ? "You don't have permission to access this resource. Please check your account permissions or contact your administrator." : isStaleChunk ? CHUNK_ERROR_DESCRIPTION : "An unexpected error occurred. You can try reloading the page or going back.";
		return /* @__PURE__ */ jsx("div", {
			className: cls("flex items-center justify-center min-h-[400px] h-full w-full", "bg-surface-sheet"),
			children: /* @__PURE__ */ jsxs("div", {
				className: "flex flex-col items-center max-w-md px-6 py-10 text-center",
				children: [
					/* @__PURE__ */ jsx("div", {
						className: cls("flex items-center justify-center w-14 h-14 rounded-xl mb-6", isPermission ? "bg-amber-100 dark:bg-amber-900/30" : isStaleChunk ? "bg-surface-raised" : "bg-red-100 dark:bg-red-900/30"),
						children: /* @__PURE__ */ jsx(Icon, {
							size: 28,
							className: isPermission ? "text-amber-600 dark:text-amber-400" : isStaleChunk ? "text-text-secondary dark:text-text-secondary-dark" : "text-red-500 dark:text-red-400"
						})
					}),
					/* @__PURE__ */ jsx(Typography, {
						variant: "h6",
						className: "text-text-primary dark:text-text-primary-dark mb-2",
						children: title
					}),
					/* @__PURE__ */ jsx(Typography, {
						variant: "body2",
						className: "text-text-secondary dark:text-text-secondary-dark mb-8",
						children: description
					}),
					/* @__PURE__ */ jsxs("div", {
						className: "flex gap-3",
						children: [!isStaleChunk && /* @__PURE__ */ jsxs(Button, {
							variant: "outlined",
							color: "neutral",
							size: "medium",
							onClick: this.handleReset,
							children: [/* @__PURE__ */ jsx(ArrowLeftIcon$1, { size: 16 }), "Try again"]
						}), /* @__PURE__ */ jsxs(Button, {
							variant: "filled",
							color: "primary",
							size: "medium",
							onClick: this.handleReload,
							children: [/* @__PURE__ */ jsx(RefreshCwIcon$1, { size: 16 }), "Reload page"]
						})]
					}),
					!isStaleChunk && error?.message && /* @__PURE__ */ jsxs("div", {
						className: "mt-8 w-full",
						children: [/* @__PURE__ */ jsxs("button", {
							onClick: this.toggleDetails,
							className: cls("flex items-center gap-1 mx-auto text-xs", "text-text-disabled dark:text-text-disabled-dark", "hover:text-text-secondary dark:hover:text-text-secondary-dark", "transition-colors cursor-pointer bg-transparent border-none p-0"),
							children: ["Technical details", showDetails ? /* @__PURE__ */ jsx(ChevronUpIcon$1, { size: 14 }) : /* @__PURE__ */ jsx(ChevronDownIcon$1, { size: 14 })]
						}), showDetails && /* @__PURE__ */ jsx("div", {
							className: cls("mt-3 p-3 rounded-lg text-left text-xs", "bg-surface-field", "text-text-secondary dark:text-text-secondary-dark", "font-mono break-all"),
							children: error.message
						})]
					})
				]
			})
		});
	}
};
//#endregion
//#region src/components/Checkbox.tsx
var sizeClasses$2 = {
	large: "w-6 h-6 rounded flex items-center justify-center",
	medium: "w-5 h-5 rounded flex items-center justify-center",
	small: "w-4 h-4 rounded-sm flex items-center justify-center",
	smallest: "w-4 h-4 rounded-sm flex items-center justify-center"
};
var outerSizeClasses = {
	medium: "w-10 h-10",
	small: "w-8 h-8",
	large: "w-12 h-12 ",
	smallest: "w-7 h-7"
};
var paddingClasses = {
	medium: "p-2",
	small: "p-2",
	large: "p-2",
	smallest: "p-2"
};
var colorClasses$2 = {
	primary: "bg-primary",
	secondary: "bg-secondary"
};
var Checkbox = React.memo(({ id, checked, indeterminate = false, padding = true, disabled, size = "medium", onCheckedChange, color = "primary", "aria-label": ariaLabel }) => {
	const isChecked = indeterminate ? false : checked;
	const iconSize = size === "medium" ? 20 : size === "small" ? 16 : size === "smallest" ? 14 : 24;
	return /* @__PURE__ */ jsx(CheckboxPrimitive.Root, {
		id,
		checked: indeterminate || isChecked,
		disabled,
		"aria-label": ariaLabel,
		"aria-checked": indeterminate ? "mixed" : isChecked,
		className: "rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
		onCheckedChange: disabled ? void 0 : onCheckedChange,
		children: /* @__PURE__ */ jsx("div", {
			className: cls(padding ? paddingClasses[size] : "", padding || onCheckedChange ? outerSizeClasses[size] : "", "inline-flex items-center justify-center text-sm font-medium focus:outline-none transition-colors ease-in-out duration-150", onCheckedChange ? "rounded-full hover:bg-surface-hover" : "", onCheckedChange ? "cursor-pointer" : "cursor-default"),
			children: /* @__PURE__ */ jsx("div", {
				className: cls("border-2 shrink-0 relative transition-colors ease-in-out duration-150", sizeClasses$2[size], disabled ? "bg-surface-300 dark:bg-surface-600" : indeterminate || isChecked ? colorClasses$2[color] : "bg-surface-card", indeterminate || isChecked ? "text-surface-accent-100 dark:text-surface-accent-900" : "", disabled ? "border-transparent" : indeterminate || isChecked ? "border-transparent" : "border-surface-accent-800 dark:border-surface-accent-500"),
				children: /* @__PURE__ */ jsx(CheckboxPrimitive.Indicator, {
					asChild: true,
					children: indeterminate ? /* @__PURE__ */ jsx(MinusIcon$1, {
						size: iconSize,
						className: "absolute"
					}) : /* @__PURE__ */ jsx(CheckIcon$1, {
						size: iconSize,
						className: "absolute"
					})
				})
			})
		})
	});
});
Checkbox.displayName = "Checkbox";
//#endregion
//#region src/components/Chip.tsx
var sizeClassNames = {
	smallest: "px-1.5 py-px text-[10px]",
	small: "px-2 py-0.5 text-xs",
	medium: "px-2.5 py-1 text-xs",
	large: "px-3 py-1.5 text-sm"
};
/**
* Helper to generate rgba from hex or standard colors.
*/
function getRgba(hex, alpha) {
	if (!hex || !hex.startsWith("#")) return hex;
	let color = hex.slice(1);
	if (color.length === 3) color = color[0] + color[0] + color[1] + color[1] + color[2] + color[2];
	return `rgba(${parseInt(color.slice(0, 2), 16)}, ${parseInt(color.slice(2, 4), 16)}, ${parseInt(color.slice(4, 6), 16)}, ${alpha})`;
}
/**
* @group Preview components
*/
function Chip({ children, colorScheme, error, outlined, variant = "tinted", onClick, icon, size = "medium", className, style }) {
	const usedColorScheme = typeof colorScheme === "string" ? getColorSchemeForKey(colorScheme) : colorScheme;
	const dark = useIsDarkMode();
	const hasScheme = error || usedColorScheme;
	let textColor = "";
	let bgColor = "";
	let border = "";
	if (error) textColor = dark ? "#f87171" : "#dc2626";
	else if (usedColorScheme) textColor = dark && usedColorScheme.darkText ? usedColorScheme.darkText : usedColorScheme.text;
	if (hasScheme) {
		if (outlined) {
			if (!error && usedColorScheme) {
				const outlineInk = dark ? usedColorScheme.darkOutlineText ?? usedColorScheme.darkText : usedColorScheme.outlineText;
				if (outlineInk) textColor = outlineInk;
			}
			bgColor = getRgba(textColor, dark ? .1 : .06);
			border = `1px solid ${getRgba(textColor, dark ? .2 : .14)}`;
		} else if (error) {
			bgColor = dark ? "rgba(220, 38, 38, 0.15)" : "rgba(239, 68, 68, 0.1)";
			border = `1px solid ${dark ? "rgba(220, 38, 38, 0.3)" : "rgba(239, 68, 68, 0.2)"}`;
		} else if (usedColorScheme) {
			const tintColor = dark ? usedColorScheme.darkTintColor : usedColorScheme.tintColor;
			const tintText = dark ? usedColorScheme.darkTintText : usedColorScheme.tintText;
			if (variant === "tinted" && tintColor && tintText) {
				bgColor = tintColor;
				textColor = tintText;
			} else bgColor = dark && usedColorScheme.darkColor ? usedColorScheme.darkColor : usedColorScheme.color;
		}
	}
	return /* @__PURE__ */ jsxs("div", {
		className: cls("rounded-md max-w-full w-max h-fit font-medium inline-flex gap-1", "text-ellipsis", "items-center", "transition-colors duration-150", !hasScheme && "bg-surface-raised text-text-secondary dark:text-text-secondary-dark", !hasScheme && outlined && "bg-transparent border border-hairline-strong", onClick ? "cursor-pointer hover:bg-surface-raised-hover" : "", sizeClassNames[size], className),
		onClick,
		style: {
			...hasScheme ? {
				backgroundColor: bgColor,
				color: textColor,
				border: border || void 0
			} : {},
			overflow: "hidden",
			...style
		},
		children: [icon, children]
	});
}
//#endregion
//#region src/components/Tooltip.tsx
var Tooltip = ({ open, defaultOpen, side = "bottom", delayDuration = 200, sideOffset, align, onOpenChange, title, tooltipClassName, tooltipStyle, children, asChild = false, container, className, style }) => {
	useInjectStyles("Tooltip", styles$2);
	const contextContainer = usePortalContainer();
	const finalContainer = container ?? contextContainer ?? void 0;
	if (!title) return /* @__PURE__ */ jsx(Fragment, { children });
	const trigger = asChild ? /* @__PURE__ */ jsx(TooltipPrimitive.Trigger, {
		asChild: true,
		children
	}) : /* @__PURE__ */ jsx(TooltipPrimitive.Trigger, {
		asChild: true,
		children: /* @__PURE__ */ jsx("div", {
			style,
			className,
			children
		})
	});
	return /* @__PURE__ */ jsx(TooltipPrimitive.Provider, {
		delayDuration,
		children: /* @__PURE__ */ jsxs(TooltipPrimitive.Root, {
			...open !== void 0 ? {
				open,
				onOpenChange
			} : {},
			defaultOpen,
			children: [trigger, /* @__PURE__ */ jsx(TooltipPrimitive.Portal, {
				container: finalContainer,
				children: /* @__PURE__ */ jsx(TooltipPrimitive.Content, {
					className: cls("TooltipContent", "max-w-lg leading-relaxed", "z-50 rounded-md px-3 py-2 text-xs leading-none bg-surface-800/95 dark:bg-surface-raised-hover font-medium text-white shadow-2xl select-none duration-400 ease-in transform opacity-100", tooltipClassName),
					style: tooltipStyle,
					sideOffset: sideOffset === void 0 ? 4 : sideOffset,
					align,
					side,
					children: title
				})
			})]
		})
	});
};
var styles$2 = `

.TooltipContent {
  animation-duration: 220ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
}

.TooltipContent[data-state='delayed-open'][data-side='top'] {
  animation-name: slideDownAndFade;
}
.TooltipContent[data-state='delayed-open'][data-side='right'] {
  animation-name: slideLeftAndFade;
}
.TooltipContent[data-state='delayed-open'][data-side='bottom'] {
  animation-name: slideUpAndFade;
}
.TooltipContent[data-state='delayed-open'][data-side='left'] {
  animation-name: slideRightAndFade;
}


@keyframes slideUpAndFade {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideRightAndFade {
  from {
    opacity: 0;
    transform: translateX(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideDownAndFade {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideLeftAndFade {
  from {
    opacity: 0;
    transform: translateX(4px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}`;
//#endregion
//#region src/components/ColorPicker.tsx
var BASE_COLORS = [
	"blue",
	"indigo",
	"violet",
	"purple",
	"fuchsia",
	"pink",
	"rose",
	"red",
	"orange",
	"yellow",
	"green",
	"emerald",
	"teal",
	"cyan",
	"gray"
];
var TONES = [
	"Lighter",
	"Light",
	"Dark",
	"Darker"
];
function getColorDisplayName(hue, tone) {
	const hueName = `${hue.charAt(0).toUpperCase()}${hue.slice(1)}`;
	return tone === "Lighter" ? hueName : `${hueName} ${tone.toLowerCase()}`;
}
/**
* A color picker component that displays a grid of predefined CHIP_COLORS.
* Used for selecting colors for enum values, tags, and other chip-based UI elements.
*
* @group Form components
*/
function ColorPicker({ value, onChange, size = "medium", allowClear = true, disabled = false }) {
	const swatchSize = size === "small" ? "w-5 h-5" : "w-6 h-6";
	const checkSize = size === "small" ? 12 : 14;
	return /* @__PURE__ */ jsxs("div", {
		className: "flex flex-col gap-2",
		children: [allowClear && /* @__PURE__ */ jsxs("button", {
			type: "button",
			disabled,
			onClick: () => onChange(void 0),
			className: cls("flex items-center gap-2 px-2 py-1 rounded-lg text-sm transition-colors duration-150", "hover:bg-surface-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary", disabled && "opacity-50 cursor-not-allowed", !value && "bg-surface-raised font-medium"),
			children: [/* @__PURE__ */ jsx("div", {
				className: cls(swatchSize, "rounded-full border-2 border-dashed border-surface-accent-400 dark:border-surface-accent-600", "flex items-center justify-center"),
				children: !value && /* @__PURE__ */ jsx(CheckIcon$1, { size: checkSize })
			}), /* @__PURE__ */ jsx("span", {
				className: "text-surface-accent-700 dark:text-surface-accent-300",
				children: "Auto (based on ID)"
			})]
		}), TONES.map((tone) => /* @__PURE__ */ jsx("div", {
			className: "flex flex-wrap gap-1.5",
			children: BASE_COLORS.map((base) => {
				const colorKey = tone === "Lighter" ? base : `${base}${tone}`;
				const colorScheme = CHIP_COLORS[colorKey];
				const isSelected = value === colorKey;
				const displayName = getColorDisplayName(base, tone);
				return /* @__PURE__ */ jsx(Tooltip, {
					title: displayName,
					delayDuration: 300,
					children: /* @__PURE__ */ jsx("button", {
						type: "button",
						disabled,
						onClick: () => onChange(colorKey),
						className: cls(swatchSize, "rounded-full transition-all flex items-center justify-center", "hover:scale-110 hover:shadow-md", "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1", disabled && "opacity-50 cursor-not-allowed hover:scale-100", isSelected && "ring-2 ring-primary ring-offset-1"),
						style: { backgroundColor: colorScheme.color },
						"aria-label": displayName,
						"aria-pressed": isSelected,
						children: isSelected && /* @__PURE__ */ jsx(CheckIcon$1, {
							size: checkSize,
							style: { color: colorScheme.text }
						})
					})
				}, colorKey);
			})
		}, tone))]
	});
}
//#endregion
//#region src/components/IconButton.tsx
var buttonClasses = "hover:scale-[1.04] active:scale-95 transition-transform";
var baseClasses = "inline-flex items-center justify-center text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors ease-in-out duration-150 [&>svg]:shrink-0";
var colorClasses$1 = "text-surface-accent-500 visited:text-surface-accent-500 dark:text-surface-accent-300 dark:visited:text-surface-accent-300";
var sizeClasses$1 = {
	medium: "w-10 !h-10 min-w-10 min-h-10 p-2",
	small: "w-8 !h-8 min-w-8 min-h-8 p-2",
	smallest: "w-7 !h-7 min-w-7 min-h-7 p-2",
	large: "w-12 !h-12 min-w-12 min-h-12 p-2"
};
var shapeClasses = {
	circular: "rounded-full",
	square: "rounded-md"
};
/** Icon size used when the caller doesn't set one on the child. */
var defaultIconSize = {
	smallest: 16,
	small: 18,
	medium: 20,
	large: 24
};
var IconButtonInner = ({ children, className, size = "medium", variant = "ghost", shape = "circular", disabled, toggled, component, ...props }, ref) => {
	const bgClasses = variant === "ghost" ? "bg-transparent hover:bg-surface-hover" : "bg-surface-raised hover:bg-surface-raised-hover";
	const Component = component || "button";
	const isNativeButton = Component === "button";
	const sizedChildren = React.Children.map(children, (child) => {
		if (!React.isValidElement(child)) return child;
		if (child.props.size !== void 0) return child;
		return React.cloneElement(child, { size: defaultIconSize[size] });
	});
	return /* @__PURE__ */ jsx(Component, {
		type: isNativeButton ? "button" : void 0,
		role: isNativeButton ? void 0 : "button",
		ref,
		"aria-disabled": disabled || void 0,
		tabIndex: disabled ? -1 : void 0,
		...props,
		className: cls(disabled ? "opacity-50 pointer-events-none" : "cursor-pointer", toggled ? "outline outline-2 outline-primary" : "", "text-inherit dark:text-inherit", colorClasses$1, bgClasses, baseClasses, buttonClasses, shapeClasses[shape], sizeClasses$1[size], className),
		children: sizedChildren
	});
};
var IconButton = React.forwardRef(IconButtonInner);
//#endregion
//#region src/components/InputLabel.tsx
var defaultClasses = {
	root: "origin-left transition-transform block whitespace-nowrap overflow-hidden text-ellipsis max-w-full",
	shrink: "transform translate-y-[2px] scale-75 translate-x-[12px]",
	expanded: "translate-x-[16px] top-0 transform translate-y-[16px] scale-100"
};
var InputLabel = React$1.forwardRef(function InputLabel(inProps, ref) {
	const { shrink, className, ...other } = inProps;
	return /* @__PURE__ */ jsx("label", {
		className: cls("text-sm font-medium peer-disabled:cursor-not-allowed", defaultBorderMixin, cls(defaultClasses.root, {
			[defaultClasses.shrink]: shrink,
			[defaultClasses.expanded]: !shrink
		}, className)),
		"data-shrink": shrink,
		ref,
		...other
	});
});
//#endregion
//#region src/components/DateTimeField.tsx
var DateTimeField = ({ value, label, onChange, disabled, clearable, mode = "date", error, size = "large", className, style, inputClassName, invisible, timezone, "aria-label": ariaLabel }) => {
	const inputRef = useRef(null);
	const [focused, setFocused] = useState(false);
	const inputId = useId();
	const labelId = `${inputId}-label`;
	const [internalValue, setInternalValue] = useState("");
	const [isTyping, setIsTyping] = useState(false);
	const invalidValue = value !== void 0 && value !== null && (!(value instanceof Date) || isNaN(value.getTime()));
	const adornmentSize = size === "large" ? "medium" : size === "medium" ? "small" : "smallest";
	useInjectStyles("DateTimeField", inputStyles);
	const handleClear = (e) => {
		e.preventDefault();
		setInternalValue("");
		setIsTyping(false);
		onChange?.(null);
	};
	const valueAsInputValue = (dateValue, mode) => {
		if (!dateValue || !(dateValue instanceof Date) || isNaN(dateValue.getTime())) return "";
		const options = {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
			timeZone: timezone
		};
		const parts = new Intl.DateTimeFormat("en-CA", options).formatToParts(dateValue);
		const getPart = (type) => parts.find((p) => p.type === type)?.value ?? "";
		const year = getPart("year");
		const month = getPart("month");
		const day = getPart("day");
		if (mode === "date") return `${year}-${month}-${day}`;
		else return `${year}-${month}-${day}T${getPart("hour")}:${getPart("minute")}`;
	};
	const getTimezoneOffsetMinutes = (date, tz) => {
		if (!tz) return -date.getTimezoneOffset();
		const utcFormatter = new Intl.DateTimeFormat("en-CA", {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
			timeZone: "UTC"
		});
		const tzFormatter = new Intl.DateTimeFormat("en-CA", {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
			timeZone: tz
		});
		const utcParts = utcFormatter.formatToParts(date);
		const tzParts = tzFormatter.formatToParts(date);
		const getPart = (parts, type) => parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
		const utcDate = new Date(Date.UTC(getPart(utcParts, "year"), getPart(utcParts, "month") - 1, getPart(utcParts, "day"), getPart(utcParts, "hour"), getPart(utcParts, "minute")));
		return (new Date(Date.UTC(getPart(tzParts, "year"), getPart(tzParts, "month") - 1, getPart(tzParts, "day"), getPart(tzParts, "hour"), getPart(tzParts, "minute"))).getTime() - utcDate.getTime()) / 6e4;
	};
	const handleInputChange = (e) => {
		const inputValue = e.target.value;
		setInternalValue(inputValue);
		setIsTyping(true);
		if (!inputValue) {
			onChange?.(null);
			return;
		}
		try {
			let year, month, day, hours = 0, minutes = 0;
			if (mode === "date") [year, month, day] = inputValue.split("-").map(Number);
			else {
				const [datePart, timePart] = inputValue.split("T");
				[year, month, day] = datePart.split("-").map(Number);
				[hours, minutes] = timePart.split(":").map(Number);
			}
			let resultDate;
			if (!timezone) resultDate = new Date(year, month - 1, day, hours, minutes);
			else {
				const refUtcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes));
				const offsetMinutes = getTimezoneOffsetMinutes(refUtcDate, timezone);
				resultDate = /* @__PURE__ */ new Date(Date.UTC(year, month - 1, day, hours, minutes) - offsetMinutes * 6e4);
			}
			if (isNaN(resultDate.getTime())) throw new Error("Invalid date");
			onChange?.(resultDate);
		} catch (e) {
			return;
		}
	};
	const handleFocus = () => {
		setFocused(true);
		setIsTyping(true);
		setInternalValue(valueAsInputValue(value ?? null, mode));
	};
	const handleBlur = () => {
		setFocused(false);
		setIsTyping(false);
	};
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsx("style", { children: inputStyles }),
		/* @__PURE__ */ jsxs("div", {
			className: cls("rounded-lg relative max-w-full", !invisible && "bg-surface-field border border-hairline", disabled ? fieldBackgroundDisabledMixin : invisible ? "" : fieldBackgroundHoverMixin, {
				"min-h-[28px]": size === "smallest",
				"min-h-[32px]": size === "small",
				"min-h-[40px]": size === "medium",
				"min-h-[48px]": size === "large"
			}, className),
			style,
			onClick: () => {
				if (!disabled) inputRef.current?.focus();
			},
			children: [
				label && /* @__PURE__ */ jsx(InputLabel, {
					id: labelId,
					htmlFor: inputId,
					className: cls("absolute top-1 pointer-events-none", !error ? focused ? "text-primary" : "text-text-secondary dark:text-text-secondary-dark" : "text-red-600 dark:text-red-500", disabled ? "opacity-50" : ""),
					shrink: true,
					children: label
				}),
				/* @__PURE__ */ jsx("input", {
					ref: inputRef,
					id: inputId,
					"aria-labelledby": label ? labelId : void 0,
					"aria-label": label ? void 0 : ariaLabel,
					type: mode === "date_time" ? "datetime-local" : "date",
					value: isTyping ? internalValue : valueAsInputValue(value ?? null, mode),
					onChange: handleInputChange,
					onFocus: handleFocus,
					onBlur: handleBlur,
					disabled,
					className: cls("w-full outline-hidden bg-transparent leading-normal text-sm px-3", clearable ? "pr-14" : "pr-12", "rounded-lg", {
						"min-h-[28px]": size === "smallest",
						"min-h-[32px]": size === "small",
						"min-h-[40px]": size === "medium",
						"min-h-[48px]": size === "large"
					}, label ? "pt-8 pb-2" : {
						smallest: "py-0.5",
						small: "py-1",
						medium: "py-2",
						large: "py-3"
					}[size], inputClassName, disabled && "border border-transparent outline-hidden opacity-50 dark:opacity-50 text-surface-accent-600 dark:text-surface-accent-500")
				}),
				/* @__PURE__ */ jsx(IconButton, {
					size: adornmentSize,
					onClick: (e) => {
						e.stopPropagation();
						inputRef.current?.showPicker();
					},
					className: "absolute right-3 top-1/2 transform -translate-y-1/2 text-surface-accent-500!",
					children: /* @__PURE__ */ jsx(CalendarIcon$1, {})
				}),
				clearable && value && /* @__PURE__ */ jsx(IconButton, {
					size: adornmentSize,
					onClick: handleClear,
					className: "absolute right-14 top-1/2 transform -translate-y-1/2 text-surface-accent-400 ",
					children: /* @__PURE__ */ jsx(XIcon$1, {})
				})
			]
		}),
		invalidValue && /* @__PURE__ */ jsxs("div", {
			className: "flex items-center m-2",
			children: [/* @__PURE__ */ jsx(AlertCircleIcon$1, {
				size: iconSize.small,
				color: "error"
			}), /* @__PURE__ */ jsxs("div", {
				className: "pl-2",
				children: [/* @__PURE__ */ jsx(Typography, {
					variant: "body2",
					children: "Invalid date value for this field"
				}), /* @__PURE__ */ jsx(Typography, {
					variant: "body2",
					children: `The provided value is: ${JSON.stringify(value)}`
				})]
			})]
		})
	] });
};
var inputStyles = `
    /* Hide the default calendar icon in Chrome, Safari, Edge, Opera */
    input[type="date"]::-webkit-calendar-picker-indicator,
    input[type="datetime-local"]::-webkit-calendar-picker-indicator {
        display: none;
        -webkit-appearance: none;
    }
    /* Hide default calendar icon in Firefox */
    input[type="date"],
    input[type="datetime-local"] {
        -moz-appearance:textfield;
    }
  `;
//#endregion
//#region src/hooks/useRestoreInterruptedFocus.ts
/**
* Finish a focus move that a modal's focus trap interrupted.
*
* Radix's `FocusScope` watches a modal for removed nodes, and when one goes
* while `document.activeElement` is `body` it concludes the focused element was
* deleted and focuses the modal itself. But `body` is also what `activeElement`
* reads *during* an ordinary focus move: the browser fires `focusout` before it
* lands on the next element. Anything that unmounts in response — a tooltip that
* opened on focus closing on blur, above all — is removed inside that window.
* The scope focuses the container, the browser abandons the move it was making,
* and Tab from one field lands on the dialog, so the next Tab starts again from
* the header. Shift+Tab the same.
*
* So remember where each move inside the container was going, and if the
* container takes focus before the move lands, send it on. A move that nothing
* interrupts lands on its target and clears the note; a note nobody used is
* dropped by the next task.
*
* The note is taken on `window`, in the capture phase — before any other
* listener for the event runs. The scope's correction arrives in the microtask
* checkpoint after whichever listener removed the node, so a note taken any
* later (on the container, bubbling) is taken after focus has already been
* moved: a field that drops something from its own `focusout` handler beat it.
*
* Internal to the kit's modals — not exported.
*/
function useRestoreInterruptedFocus(container) {
	useEffect(() => {
		if (!container) return;
		let intended = null;
		let expiry;
		const onFocusOut = (event) => {
			if (!(event.target instanceof Node) || !container.contains(event.target)) return;
			const next = event.relatedTarget;
			intended = next instanceof HTMLElement && next !== container && container.contains(next) ? next : null;
			clearTimeout(expiry);
			if (intended) expiry = setTimeout(() => {
				intended = null;
			}, 0);
		};
		const onFocusIn = (event) => {
			const target = intended;
			intended = null;
			if (event.target === container && target?.isConnected && container.contains(target)) target.focus();
		};
		window.addEventListener("focusout", onFocusOut, true);
		container.addEventListener("focusin", onFocusIn);
		return () => {
			clearTimeout(expiry);
			window.removeEventListener("focusout", onFocusOut, true);
			container.removeEventListener("focusin", onFocusIn);
		};
	}, [container]);
}
//#endregion
//#region src/components/Dialog.tsx
var widthClasses = {
	xs: "max-w-xs w-xs",
	sm: "max-w-sm w-sm",
	md: "max-w-md w-md",
	lg: "max-w-lg w-lg",
	xl: "max-w-xl w-xl",
	"2xl": "max-w-2xl w-2xl",
	"3xl": "max-w-3xl w-3xl",
	"4xl": "max-w-4xl w-4xl",
	"5xl": "max-w-5xl w-5xl",
	"6xl": "max-w-6xl w-6xl",
	"7xl": "max-w-7xl w-7xl",
	full: "max-w-full w-full"
};
var Dialog = ({ open, onOpenChange, children, className, containerClassName, fullWidth = true, fullHeight, fullScreen, scrollable = true, maxWidth = "lg", modal = true, onOpenAutoFocus, onEscapeKeyDown, onPointerDownOutside, onInteractOutside, disableInitialFocus = true, portalContainer, "aria-describedby": ariaDescribedby }) => {
	const [displayed, setDisplayed] = useState(false);
	const [popupHost, setPopupHost] = useState(null);
	const [contentEl, setContentEl] = useState(null);
	useRestoreInterruptedFocus(contentEl);
	const contextContainer = usePortalContainer();
	const finalContainer = portalContainer ?? contextContainer ?? void 0;
	useEffect(() => {
		if (!open) {
			const timeout = setTimeout(() => {
				setDisplayed(false);
			}, 100);
			return () => clearTimeout(timeout);
		} else {
			setDisplayed(true);
			return () => {};
		}
	}, [open]);
	return /* @__PURE__ */ jsx(DialogPrimitive.Root, {
		open: displayed || open,
		modal,
		onOpenChange,
		children: /* @__PURE__ */ jsx(DialogPrimitive.Portal, {
			container: finalContainer,
			children: /* @__PURE__ */ jsxs("div", {
				className: cls("fixed inset-0 z-50", containerClassName),
				children: [/* @__PURE__ */ jsx(DialogPrimitive.Overlay, {
					className: cls("fixed inset-0 transition-opacity ease-in-out duration-200 bg-black/50 dark:bg-black/60 backdrop-blur-sm", displayed && open ? "opacity-100" : "opacity-0", "z-50 fixed top-0 left-0 w-full h-full flex justify-center items-center"),
					style: { pointerEvents: displayed ? "auto" : "none" }
				}), /* @__PURE__ */ jsxs(DialogPrimitive.Content, {
					ref: setContentEl,
					onEscapeKeyDown,
					onOpenAutoFocus: (e) => {
						if (disableInitialFocus) e.preventDefault();
						onOpenAutoFocus?.(e);
					},
					onPointerDownOutside,
					onInteractOutside,
					"aria-describedby": ariaDescribedby,
					className: cls("relative h-full outline-none flex justify-center items-center z-60 opacity-100 transition-all duration-200 ease-in-out"),
					children: [/* @__PURE__ */ jsx("div", {
						className: cls(paperMixin, "rounded-2xl", "z-60", "relative", "overflow-hidden", "outline-none focus:outline-none", fullWidth && !fullScreen ? "w-11/12" : void 0, fullHeight && !fullScreen ? "h-full" : void 0, "text-text-primary dark:text-text-primary-dark", "justify-center items-center", fullScreen ? "h-screen w-screen" : "max-h-[90vh] shadow-lg", "ease-in-out duration-200", scrollable && "overflow-y-auto", displayed && open ? "opacity-100 scale-100" : "opacity-0 scale-[0.97]", maxWidth && !fullScreen ? widthClasses[maxWidth] : void 0, className),
						children: /* @__PURE__ */ jsx(PortalContainerProvider, {
							container: popupHost,
							children
						})
					}), /* @__PURE__ */ jsx("div", {
						ref: setPopupHost,
						className: "relative z-70 w-0 h-0"
					})]
				})]
			})
		})
	});
};
//#endregion
//#region src/components/DialogActions.tsx
function DialogActions({ children, position = "sticky", translucent = true, className }) {
	return /* @__PURE__ */ jsx("div", {
		className: cls(defaultBorderMixin, "pt-2 pb-4 px-4 border-t flex flex-row items-center justify-end shrink-0 bottom-0 right-0 left-0 text-right z-2 gap-2", position, "bg-surface-card/60", translucent ? "backdrop-blur-sm" : "", className),
		children
	});
}
//#endregion
//#region src/components/DialogContent.tsx
function DialogContent({ children, className, fullHeight, includeMargin = true }) {
	if (fullHeight) return /* @__PURE__ */ jsx("div", {
		className: "flex-grow flex flex-col h-full relative",
		children
	});
	return /* @__PURE__ */ jsx("div", {
		className: cls("flex-grow", { "my-8 mx-8": includeMargin }, className),
		children
	});
}
//#endregion
//#region src/components/DialogTitle.tsx
function DialogTitle({ children, hidden, className, variant = "subtitle2", gutterBottom = true, includeMargin = true, ...props }) {
	const title = /* @__PURE__ */ jsx(DialogPrimitive.Title, {
		asChild: true,
		children: /* @__PURE__ */ jsx(Typography, {
			variant,
			className: cls({ "mt-8 mx-8": includeMargin }, className),
			gutterBottom,
			...props,
			children
		})
	});
	if (hidden) return /* @__PURE__ */ jsx(VisuallyHidden.Root, { children: title });
	return title;
}
//#endregion
//#region src/components/ExpandablePanel.tsx
function ExpandablePanel({ title, children, invisible = false, expanded, onExpandedChange, initiallyExpanded = true, titleClassName, asField, className, innerClassName }) {
	useInjectStyles("ExpandablePanel", `
.CollapsibleContent {
  overflow: hidden;
}
.CollapsibleContent[data-state='open'] {
  animation: slideDown 220ms ease-out
}
.CollapsibleContent[data-state='closed'] {
  animation: slideUp 220ms ease-out;
}

@keyframes slideDown {
  from {
    height: 0;
  }
  to {
    height: var(--radix-collapsible-content-height);
  }
}

@keyframes slideUp {
  from {
    height: var(--radix-collapsible-content-height);
  }
  to {
    height: 0;
  }
}`);
	const [open, setOpen] = useState(expanded !== void 0 ? expanded : initiallyExpanded);
	const [allowOverflow, setAllowOverflow] = useState(open);
	useEffect(() => {
		if (open) setTimeout(() => {
			setAllowOverflow(true);
		}, 220);
		else setAllowOverflow(false);
	}, [open]);
	useEffect(() => {
		if (expanded !== void 0) setOpen(expanded);
	}, [expanded]);
	return /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs(Collapsible.Root, {
		className: cls(!invisible && "border-hairline border", "rounded-lg", "w-full", className),
		open,
		onOpenChange: (updatedOpen) => {
			onExpandedChange?.(updatedOpen);
			setOpen(updatedOpen);
		},
		children: [/* @__PURE__ */ jsx(Collapsible.Trigger, {
			asChild: true,
			children: /* @__PURE__ */ jsxs("div", {
				className: cls("rounded-t-lg flex items-center justify-between w-full min-h-[52px]", "hover:bg-surface-hover active:bg-surface-active", invisible ? "border-b px-2" : "p-4", open ? "py-6" : "py-4", "transition-all duration-200", invisible && "border-hairline", asField && "bg-surface-field border border-hairline", titleClassName, "cursor-pointer"),
				role: "button",
				tabIndex: 0,
				"aria-expanded": open,
				children: [title, /* @__PURE__ */ jsx(ChevronDownIcon$1, { className: cls("transition", open ? "rotate-180" : "") })]
			})
		}), /* @__PURE__ */ jsx(Collapsible.Content, {
			className: cls("CollapsibleContent"),
			style: { overflow: allowOverflow ? "visible" : "hidden" },
			children: /* @__PURE__ */ jsx("div", {
				className: innerClassName,
				children
			})
		})]
	}) });
}
//#endregion
//#region src/components/FileUpload.tsx
function FileUpload({ accept, onFilesAdded, onFilesRejected, maxSize, disabled, maxFiles, title, uploadDescription, children, preventDropOnDocument = true, size }) {
	const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
		accept,
		noDragEventsBubbling: true,
		maxSize,
		onDrop: onFilesAdded,
		onDropRejected: onFilesRejected,
		disabled,
		maxFiles,
		preventDropOnDocument
	});
	return /* @__PURE__ */ jsxs("div", {
		...getRootProps(),
		role: "button",
		"aria-label": typeof title === "string" ? title : typeof uploadDescription === "string" ? uploadDescription : "Upload file",
		"aria-disabled": disabled || void 0,
		className: cls(fieldBackgroundMixin, "flex gap-2", "p-4 box-border relative items-center border-2 border-solid border-transparent outline-hidden rounded-lg duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] focus:border-primary", {
			"h-44": size === "large",
			"h-28": size === "medium",
			"h-16": size === "small",
			"cursor-pointer": !disabled,
			[fieldBackgroundHoverMixin]: !isDragActive,
			"transition-colors duration-200 ease-[cubic-bezier(0,0,0.2,1)] border-red-500": isDragReject,
			"transition-colors duration-200 ease-[cubic-bezier(0,0,0.2,1)] border-green-500": isDragAccept
		}),
		children: [
			/* @__PURE__ */ jsx(Typography, {
				variant: "caption",
				color: "secondary",
				className: "absolute top-2 left-3.5 cursor-inherit",
				children: title
			}),
			/* @__PURE__ */ jsx("input", { ...getInputProps() }),
			children,
			/* @__PURE__ */ jsx("div", {
				className: "grow h-28 box-border flex flex-col items-center justify-center text-center",
				children: /* @__PURE__ */ jsx(Typography, {
					align: "center",
					variant: "label",
					className: "flex flex-row gap-2 justify-center",
					children: uploadDescription
				})
			})
		]
	});
}
//#endregion
//#region src/components/FilterChip.tsx
var sizeClasses = {
	small: "px-2 py-0.5 text-xs",
	medium: "px-2.5 py-1 text-xs"
};
/**
* A toggle chip used for filter presets and similar multi-select controls.
*
* Reads as a text tab: no fill at rest, the alpha highlight when active. It
* shares no grammar with the tinted enum {@link Chip}, which carries a hue.
*
* @group Interactive components
*/
var FilterChip = React.forwardRef(function FilterChip({ children, active = false, onClick, icon, size = "medium", className, disabled = false, ...rest }, ref) {
	return /* @__PURE__ */ jsxs("button", {
		ref,
		type: "button",
		onClick,
		disabled,
		className: cls("inline-flex items-center gap-1 rounded-md", "font-medium whitespace-nowrap select-none shrink-0", "transition-colors duration-150", "focus-visible:outline-none focus-visible:ring-primary/50", sizeClasses[size], !disabled && "cursor-pointer", active ? "bg-surface-active text-text-primary dark:text-text-primary-dark" : cls("bg-transparent text-text-secondary dark:text-text-secondary-dark", !disabled && "hover:bg-surface-hover"), disabled && "opacity-50 cursor-not-allowed", className),
		...rest,
		children: [icon, children]
	});
});
//#endregion
//#region src/components/InfoLabel.tsx
var colorClasses = {
	info: "bg-blue-500/8 dark:bg-blue-500/12 text-blue-800 dark:text-blue-200 border border-blue-500/20 dark:border-blue-500/25",
	warn: "bg-amber-500/8 dark:bg-amber-500/12 text-amber-800 dark:text-amber-200 border border-amber-500/20 dark:border-amber-500/25"
};
function InfoLabel({ children, mode = "info" }) {
	return /* @__PURE__ */ jsx("div", {
		className: cls("my-3 py-2 px-4 rounded-lg text-sm font-medium leading-snug", colorClasses[mode]),
		children
	});
}
//#endregion
//#region src/components/Label.tsx
var Label = React$1.forwardRef(({ className, border, onClick, ...props }, ref) => /* @__PURE__ */ jsx(LabelPrimitive.Root, {
	ref,
	onClick,
	className: cls("text-sm font-medium peer-disabled:cursor-not-allowed", border && "border border-hairline-strong rounded-md px-3 py-1.5", onClick && "hover:cursor-pointer hover:bg-surface-hover", defaultBorderMixin, className),
	...props
}));
Label.displayName = LabelPrimitive.Root.displayName;
//#endregion
//#region src/components/LoadingButton.tsx
function LoadingButton({ children, loading, disabled, onClick, startIcon, ...props }) {
	return /* @__PURE__ */ jsxs(Button, {
		disabled: loading || disabled,
		onClick,
		component: props.component,
		...props,
		children: [
			loading && /* @__PURE__ */ jsx(CircularProgress, { size: "smallest" }),
			!loading && startIcon,
			children
		]
	});
}
//#endregion
//#region src/components/Markdown.tsx
var proseClasses = {
	small: "prose prose-sm typography-body2",
	medium: "prose typography-body1",
	large: "prose prose-lg",
	xl: "prose prose-xl",
	"2xl": "prose prose-2xl"
};
/**
* Tailwind typography-plugin weight overrides.
* The plugin defaults to very heavy weights (h1: 800, h2: 700, h3/h4: 600,
* strong: 600, code: 600) which clash with the Rebase design-system that uses
* font-light (300) / font-normal (400) for headings.
* We reset them here so rendered markdown matches the rest of the UI.
*/
var proseWeightOverrides = [
	"prose-headings:font-normal",
	"prose-strong:font-semibold",
	"prose-code:font-normal",
	"prose-blockquote:font-normal",
	"prose-a:font-normal"
].join(" ");
var md = new MarkdownIt({ html: false });
/**
* @group Preview components
*/
var Markdown = React.memo(function Markdown({ source, className, size = "medium" }) {
	const html = useMemo(() => {
		return md.render(typeof source === "string" ? source : "");
	}, [source]);
	return /* @__PURE__ */ jsx("div", {
		className: cls(proseClasses[size], "dark:prose-invert prose-headings:font-title", proseWeightOverrides, className),
		dangerouslySetInnerHTML: { __html: html }
	});
}, deepEqual);
//#endregion
//#region src/components/Menu.tsx
var Menu = React.forwardRef(({ children, trigger, open, defaultOpen, side, align, onOpenChange, portalContainer, sideOffset = 4, className }, ref) => {
	const contextContainer = usePortalContainer();
	const finalContainer = portalContainer ?? contextContainer ?? void 0;
	return /* @__PURE__ */ jsxs(DropdownMenu.Root, {
		open,
		defaultOpen,
		onOpenChange,
		children: [/* @__PURE__ */ jsx(DropdownMenu.Trigger, {
			ref,
			asChild: true,
			children: trigger
		}), /* @__PURE__ */ jsx(DropdownMenu.Portal, {
			container: finalContainer,
			children: /* @__PURE__ */ jsx(DropdownMenu.Content, {
				side,
				sideOffset,
				align,
				className: cls(paperMixin, focusedDisabled, "py-2 z-50", className),
				children
			})
		})]
	});
});
Menu.displayName = "Menu";
var MenuItem = React.memo(({ children, dense = false, disabled = false, onClick, className }) => {
	const classNames = cls(onClick && !disabled && "cursor-pointer", disabled && "opacity-50 cursor-not-allowed", "rounded-md text-sm font-medium text-surface-accent-700 dark:text-surface-accent-300 flex items-center gap-4 transition-colors duration-150", !disabled && "hover:bg-surface-hover", dense ? "px-4 py-1.5" : "px-4 py-2", className);
	return /* @__PURE__ */ jsx(DropdownMenu.Item, {
		className: classNames,
		disabled,
		onClick: disabled ? void 0 : onClick,
		children
	});
});
MenuItem.displayName = "MenuItem";
//#endregion
//#region src/components/Menubar.tsx
function Menubar({ children, onSelect, value, defaultValue, onValueChange, className }) {
	return /* @__PURE__ */ jsx(MenubarPrimitive.Root, {
		onSelect,
		value,
		defaultValue,
		onValueChange,
		className: cls("z-10 flex bg-surface-card p-[3px] rounded-lg shadow-sm", className),
		children
	});
}
function MenubarMenu({ children, value }) {
	return /* @__PURE__ */ jsx(MenubarPrimitive.Menu, {
		value,
		children
	});
}
function MenubarTrigger({ children, onSelect, className }) {
	return /* @__PURE__ */ jsx(MenubarPrimitive.Trigger, {
		onSelect,
		className: cls("py-2 px-3 outline-none select-none font-medium leading-none rounded-md text-text-primary dark:text-text-primary-dark text-sm transition-colors duration-150 flex items-center justify-between gap-[2px] data-[highlighted]:bg-surface-hover data-[state=open]:bg-surface-active hover:bg-surface-hover", className),
		children
	});
}
function MenubarPortal({ children, portalContainer }) {
	const contextContainer = usePortalContainer();
	const finalContainer = portalContainer ?? contextContainer ?? void 0;
	return /* @__PURE__ */ jsx(MenubarPrimitive.Portal, {
		container: finalContainer,
		children
	});
}
function MenubarContent({ children, className, align, sideOffset, alignOffset, onSelect, ...rest }) {
	return /* @__PURE__ */ jsx(MenubarPrimitive.Content, {
		onSelect,
		className: cls("z-50 min-w-[220px] bg-surface-card rounded-md p-[6px] shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] [animation-duration:_400ms] [animation-timing-function:_cubic-bezier(0.16,_1,_0.3,_1)] will-change-[transform,opacity]", className),
		align: align ?? "start",
		sideOffset: sideOffset ?? 5,
		alignOffset: alignOffset ?? -3,
		...rest,
		children
	});
}
function MenubarItem({ children, leftPadding, className, disabled, onSelect, ...rest }) {
	return /* @__PURE__ */ jsx(MenubarPrimitive.Item, {
		onSelect,
		className: cls("group text-sm leading-none rounded-md flex items-center h-[32px] px-[10px] py-[2px] relative select-none transition-colors duration-150 outline-none data-[state=open]:bg-surface-active data-[state=open]:text-text-primary data-[state=open]:dark:text-text-primary-dark data-[highlighted]:bg-surface-hover  data-[disabled]:text-text-disabled data-[disabled]:dark:text-text-disabled-dark data-[disabled]:pointer-events-none", leftPadding ? "pl-5" : "", disabled ? "pointer-events-none text-text-secondary dark:text-text-secondary-dark" : "text-text-primary dark:text-text-primary-dark", className),
		disabled,
		...rest,
		children
	});
}
function MenubarSeparator({ children, className, ...rest }) {
	return /* @__PURE__ */ jsx(MenubarPrimitive.Separator, {
		className: cls("h-[1px] bg-hairline m-[5px]", className),
		...rest,
		children
	});
}
function MenubarSub({ children, ...rest }) {
	return /* @__PURE__ */ jsx(MenubarPrimitive.Sub, {
		...rest,
		children
	});
}
function MenubarSubTrigger({ children, className, onSelect, ...rest }) {
	return /* @__PURE__ */ jsx(MenubarPrimitive.SubTrigger, {
		onSelect,
		className: cls("group text-sm leading-none text-text-primary dark:text-text-primary-dark rounded-md flex items-center h-[32px] px-[10px] py-[2px] relative select-none transition-colors duration-150 outline-none data-[state=open]:bg-surface-active data-[state=open]:text-text-primary data-[state=open]:dark:text-text-primary-dark data-[highlighted]:bg-surface-hover  data-[disabled]:text-text-disabled data-[disabled]:dark:text-text-disabled-dark data-[disabled]:pointer-events-none", className),
		...rest,
		children
	});
}
function MenubarSubContent({ children, alignOffset, className, onSelect, ...rest }) {
	return /* @__PURE__ */ jsx(MenubarPrimitive.SubContent, {
		onSelect,
		alignOffset: alignOffset ?? -5,
		className: cls("min-w-[220px] bg-surface-card rounded-md p-[6px] shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] [animation-duration:_400ms] [animation-timing-function:_cubic-bezier(0.16,_1,_0.3,_1)] will-change-[transform,opacity]", className),
		...rest,
		children
	});
}
function MenubarCheckboxItem({ children, checked, onCheckedChange, className, onSelect, ...rest }) {
	return /* @__PURE__ */ jsx(MenubarPrimitive.CheckboxItem, {
		onSelect,
		className: cls("text-sm leading-none text-text-primary dark:text-text-primary-dark rounded-md flex items-center h-[32px] px-[10px] py-[2px] relative select-none transition-colors duration-150 pl-5 outline-none data-[highlighted]:bg-surface-hover data-[disabled]:text-text-disabled data-[disabled]:dark:text-text-disabled-dark data-[disabled]:pointer-events-none", className),
		checked,
		onCheckedChange,
		...rest,
		children
	});
}
function MenubarItemIndicator({ children, className, ...rest }) {
	return /* @__PURE__ */ jsx(MenubarPrimitive.ItemIndicator, {
		className: cls("absolute left-0 w-4 inline-flex items-center justify-center", className),
		...rest,
		children: children ?? /* @__PURE__ */ jsx(CheckIcon$1, { size: iconSize.smallest })
	});
}
function MenubarRadioGroup({ children, className, value, onValueChange, onSelect, ...rest }) {
	return /* @__PURE__ */ jsx(MenubarPrimitive.RadioGroup, {
		className: cls(className),
		value,
		onValueChange,
		onSelect,
		...rest,
		children: children ?? /* @__PURE__ */ jsx(CheckIcon$1, { size: iconSize.small })
	});
}
function MenubarRadioItem({ children, className, value, onSelect, ...rest }) {
	return /* @__PURE__ */ jsx(MenubarPrimitive.RadioItem, {
		onSelect,
		className: cls("text-sm leading-none text-text-primary dark:text-text-primary-dark rounded-md flex items-center h-[32px] px-[10px] py-[2px] relative select-none transition-colors duration-150 pl-5 outline-none data-[highlighted]:bg-surface-hover data-[disabled]:text-text-disabled data-[disabled]:dark:text-text-disabled-dark data-[disabled]:pointer-events-none", className),
		value,
		...rest,
		children: children ?? /* @__PURE__ */ jsx(CheckIcon$1, { size: iconSize.small })
	});
}
function MenubarShortcut({ children, className, ...rest }) {
	return /* @__PURE__ */ jsx("div", {
		className: cls("ml-auto pl-5 group-data-[disabled]:text-text-disabled data-[disabled]:dark:text-text-disabled-dark", className),
		...rest,
		children
	});
}
function MenubarSubTriggerIndicator() {
	return /* @__PURE__ */ jsx("div", {
		className: "ml-auto pl-5 ",
		children: /* @__PURE__ */ jsx(ChevronRightIcon$1, { size: iconSize.small })
	});
}
//#endregion
//#region src/components/Separator.tsx
function Separator({ orientation, decorative, className }) {
	if (orientation === "horizontal") return /* @__PURE__ */ jsx(SeparatorPrimitive.Root, {
		decorative,
		orientation: "horizontal",
		className: cls("bg-hairline data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px my-4", className)
	});
	else return /* @__PURE__ */ jsx(SeparatorPrimitive.Root, {
		className: cls("bg-hairline data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px mx-4", className),
		decorative,
		orientation: "vertical"
	});
}
//#endregion
//#region src/components/common/SelectInputLabel.tsx
/**
* The label above a Select or MultiSelect.
*
* It used `text-surface-accent-500` — a SURFACE scale, borrowed for type. The
* label above a TextField has always used `text-text-secondary`, and the two are
* the same role, so a form mixing the components showed two different label
* colours: `#64748b` blue-grey over a Select, `#757575` neutral over a TextField.
* Measured side by side on /debug/ui, which is where it is most obvious, because
* the reference puts them in adjacent columns.
*
* The text tokens are also the tuned ones: `--color-surface-500` was moved to
* #797979 precisely so muted type clears AA on both grounds. The slate scale
* carries no such guarantee — it is for surfaces.
*/
function SelectInputLabel({ children, error }) {
	return /* @__PURE__ */ jsx("div", {
		className: cls("text-sm font-medium ml-3.5 mb-1", error ? "text-red-600 dark:text-red-500" : "text-text-secondary dark:text-text-secondary-dark"),
		children
	});
}
//#endregion
//#region src/components/MultiSelect.tsx
var MultiSelectContext = React$1.createContext(null);
var MultiSelect = React$1.forwardRef(({ value, size = "large", label, error, onValueChange, invisible, disabled, placeholder, modalPopover = true, includeClear = true, includeSelectAll = true, useChips = true, className, inputClassName, inputRef, children, renderValues, open, onOpenChange, portalContainer, "aria-label": ariaLabel }, ref) => {
	const [isMounted, setIsMounted] = useState(false);
	const [isPopoverOpen, setIsPopoverOpen] = useState(open ?? false);
	const [selectedValues, setSelectedValues] = useState(value ?? []);
	const contextContainer = usePortalContainer();
	const finalContainer = portalContainer ?? contextContainer ?? void 0;
	useEffect(() => {
		setIsMounted(true);
	}, []);
	const onPopoverOpenChange = (open) => {
		setIsPopoverOpen(open);
		onOpenChange?.(open);
	};
	useEffect(() => {
		setIsPopoverOpen(open ?? false);
	}, [open]);
	const allValues = React$1.useMemo(() => children ? Children.map(children, (child) => {
		if (React$1.isValidElement(child)) return child.props.value;
		return null;
	})?.filter(Boolean) ?? [] : [], [children]);
	const optionsMap = React$1.useMemo(() => {
		const map = /* @__PURE__ */ new Map();
		Children.forEach(children, (child) => {
			if (React$1.isValidElement(child)) map.set(String(child.props.value), child.props.children);
		});
		return map;
	}, [children]);
	React$1.useEffect(() => {
		setSelectedValues(value ?? []);
	}, [value]);
	const updateValues = React$1.useCallback((values) => {
		setSelectedValues(values);
		onValueChange?.(values);
	}, [onValueChange]);
	const onItemClick = React$1.useCallback((newValue) => {
		let newSelectedValues;
		if (selectedValues.some((v) => String(v) === String(newValue))) newSelectedValues = selectedValues.filter((v) => String(v) !== String(newValue));
		else newSelectedValues = [...selectedValues, String(newValue)];
		updateValues(newSelectedValues);
	}, [selectedValues, updateValues]);
	const handleInputKeyDown = (event) => {
		if (event.key === "Enter") onPopoverOpenChange(true);
		else if (event.key === "Backspace" && !event.currentTarget.value) {
			const newSelectedValues = [...selectedValues];
			newSelectedValues.pop();
			updateValues(newSelectedValues);
		}
	};
	const toggleOption = (value) => {
		const newSelectedValues = selectedValues.some((v) => String(v) === String(value)) ? selectedValues.filter((v) => String(v) !== String(value)) : [...selectedValues, value];
		updateValues(newSelectedValues);
	};
	const handleClear = () => {
		updateValues([]);
	};
	const handleTogglePopover = () => {
		onPopoverOpenChange(!isPopoverOpen);
	};
	const toggleAll = () => {
		if (selectedValues.length === allValues.length) handleClear();
		else updateValues(allValues);
		onPopoverOpenChange(false);
	};
	useInjectStyles("MultiSelect", `
[data-multi-select-content] [cmdk-group] {
  max-height: 45vh;
  overflow-y: auto;
} `);
	const contextValue = React$1.useMemo(() => ({
		fieldValue: selectedValues,
		onItemClick
	}), [selectedValues, onItemClick]);
	return /* @__PURE__ */ jsxs(MultiSelectContext.Provider, {
		value: contextValue,
		children: [typeof label === "string" ? /* @__PURE__ */ jsx(SelectInputLabel, {
			error,
			children: label
		}) : label, /* @__PURE__ */ jsxs(PopoverPrimitive.Root, {
			open: isMounted && isPopoverOpen,
			onOpenChange: onPopoverOpenChange,
			modal: modalPopover,
			children: [/* @__PURE__ */ jsx(PopoverPrimitive.Trigger, {
				asChild: true,
				children: /* @__PURE__ */ jsx("button", {
					ref: inputRef ?? ref,
					"aria-label": ariaLabel ?? (typeof label === "string" ? label : void 0),
					onClick: handleTogglePopover,
					className: cls(invisible ? {
						smallest: "min-h-[28px]",
						small: "min-h-[32px]",
						medium: "min-h-[40px]",
						large: "min-h-[48px]"
					}[size] : {
						smallest: "min-h-[30px]",
						small: "min-h-[34px]",
						medium: "min-h-[42px]",
						large: "min-h-[50px]"
					}[size], {
						"py-0": size === "smallest",
						"py-0.5": size === "small",
						"py-1": size === "medium" || size === "large"
					}, {
						"px-2": size === "small" || size === "smallest",
						"px-4": size === "medium" || size === "large"
					}, "select-none rounded-lg text-sm", "focus:ring-0 focus-visible:ring-0 outline-none focus:outline-none focus-visible:outline-none", invisible ? fieldBackgroundInvisibleMixin : fieldBackgroundMixin, disabled ? fieldBackgroundDisabledMixin : invisible ? "" : fieldBackgroundHoverMixin, "relative flex items-center", className, inputClassName),
					children: selectedValues.length > 0 ? /* @__PURE__ */ jsxs("div", {
						className: "flex justify-between items-center w-full",
						children: [/* @__PURE__ */ jsxs("div", {
							className: "flex flex-wrap items-center gap-1.5 text-start",
							children: [renderValues && renderValues(selectedValues), !renderValues && selectedValues.map((value) => {
								const optionChildren = optionsMap.get(String(value));
								if (!useChips) return optionChildren;
								return /* @__PURE__ */ jsxs(Chip, {
									size: size === "smallest" || size === "small" ? "smallest" : "small",
									className: cls("flex flex-row items-center p-1"),
									children: [optionChildren, /* @__PURE__ */ jsx(XIcon$1, {
										size: iconSize.smallest,
										onClick: (event) => {
											event.stopPropagation();
											toggleOption(value);
										}
									})]
								}, String(value));
							})]
						}), /* @__PURE__ */ jsxs("div", {
							className: "flex items-center justify-between",
							children: [includeClear && /* @__PURE__ */ jsx(XIcon$1, {
								className: "ml-4",
								size: iconSize.small,
								onClick: (event) => {
									event.stopPropagation();
									handleClear();
								}
							}), /* @__PURE__ */ jsx("div", {
								className: cls("px-2 h-full flex items-center"),
								children: /* @__PURE__ */ jsx(ChevronDownIcon$1, {
									size: size === "large" ? iconSize.medium : iconSize.small,
									className: cls("transition", isPopoverOpen ? "rotate-180" : "")
								})
							})]
						})]
					}) : /* @__PURE__ */ jsxs("div", {
						className: "flex items-center justify-between w-full mx-auto",
						children: [/* @__PURE__ */ jsx("span", {
							className: "text-sm",
							children: placeholder
						}), /* @__PURE__ */ jsx("div", {
							className: cls("px-2 h-full flex items-center"),
							children: /* @__PURE__ */ jsx(ChevronDownIcon$1, {
								size: size === "large" ? iconSize.medium : iconSize.small,
								className: cls("transition", isPopoverOpen ? "rotate-180" : "")
							})
						})]
					})
				})
			}), /* @__PURE__ */ jsx(PopoverPrimitive.Portal, {
				container: finalContainer,
				children: /* @__PURE__ */ jsx(PopoverPrimitive.Content, {
					"data-multi-select-content": true,
					className: cls("z-50 overflow-hidden border bg-surface-card rounded-lg w-[400px]", defaultBorderMixin),
					align: "start",
					sideOffset: 8,
					onEscapeKeyDown: () => onPopoverOpenChange(false),
					children: /* @__PURE__ */ jsxs(Command, { children: [
						/* @__PURE__ */ jsxs("div", {
							className: "flex flex-row items-center",
							children: [/* @__PURE__ */ jsx(Command.Input, {
								className: cls(focusedDisabled, "bg-transparent outline-none flex-1 h-full w-full m-4 flex-grow text-surface-accent-900 dark:text-white"),
								placeholder: "Search...",
								onKeyDown: handleInputKeyDown
							}), selectedValues.length > 0 && /* @__PURE__ */ jsx("div", {
								onClick: handleClear,
								className: "text-sm justify-center cursor-pointer py-3 px-4 text-text-secondary dark:text-text-secondary-dark",
								children: "Clear"
							})]
						}),
						/* @__PURE__ */ jsx(Separator, {
							orientation: "horizontal",
							className: "my-0"
						}),
						/* @__PURE__ */ jsxs(Command.List, { children: [/* @__PURE__ */ jsx(Command.Empty, {
							className: "px-4 py-2 text-sm text-text-secondary dark:text-text-secondary-dark",
							children: "No results found."
						}), /* @__PURE__ */ jsxs(Command.Group, { children: [includeSelectAll && /* @__PURE__ */ jsxs(Command.Item, {
							onSelect: toggleAll,
							className: cls("flex flex-row items-center gap-1.5", "cursor-pointer", "m-1", "ring-offset-transparent", "p-1 rounded-md aria-[selected=true]:outline-none aria-[selected=true]:ring-2 aria-[selected=true]:ring-primary aria-[selected=true]:ring-opacity-75 aria-[selected=true]:ring-primary/75 aria-[selected=true]:ring-offset-2", "aria-[selected=true]:bg-surface-active", "cursor-pointer p-2 rounded-md aria-[selected=true]:bg-surface-active"),
							children: [/* @__PURE__ */ jsx(InnerCheckBox, { checked: selectedValues.length === allValues.length }), /* @__PURE__ */ jsx("span", {
								className: "text-sm text-text-secondary dark:text-text-secondary-dark",
								children: "(Select All)"
							})]
						}, "all"), children] })] })
					] })
				})
			})]
		})]
	});
});
MultiSelect.displayName = "MultiSelect";
var MultiSelectItem = React$1.memo(function MultiSelectItem({ children, value, className }) {
	const context = React$1.useContext(MultiSelectContext);
	if (!context) throw new Error("MultiSelectItem must be used inside a MultiSelect");
	const { fieldValue, onItemClick } = context;
	const isSelected = (fieldValue ?? []).some((v) => String(v) === String(value));
	return /* @__PURE__ */ jsxs(Command.Item, {
		onMouseDown: (e) => {
			e.preventDefault();
			e.stopPropagation();
		},
		onSelect: (_) => {
			onItemClick(value);
		},
		className: cls("flex flex-row items-center gap-1.5", isSelected ? "bg-surface-active" : "", "cursor-pointer", "m-1", "ring-offset-transparent", "p-1 rounded-md aria-[selected=true]:outline-none aria-[selected=true]:ring-2 aria-[selected=true]:ring-primary aria-[selected=true]:ring-opacity-75 aria-[selected=true]:ring-primary/75 aria-[selected=true]:ring-offset-2", "aria-[selected=true]:bg-surface-active", "cursor-pointer p-2 rounded-md aria-[selected=true]:bg-surface-active", "text-surface-accent-700 dark:text-surface-accent-300", className),
		children: [/* @__PURE__ */ jsx(InnerCheckBox, { checked: isSelected }), children]
	});
});
var InnerCheckBox = React$1.memo(function InnerCheckBox({ checked }) {
	return /* @__PURE__ */ jsx("div", {
		className: cls("p-2", "w-8 h-8", "inline-flex items-center justify-center text-sm font-medium focus:outline-none transition-colors ease-in-out duration-150"),
		children: /* @__PURE__ */ jsx("div", {
			className: cls("border-2 relative transition-colors ease-in-out duration-150", "w-4 h-4 rounded-sm flex items-center justify-center", checked ? "bg-primary" : "bg-surface-card", checked ? "text-surface-accent-100 dark:text-surface-accent-900" : "", checked ? "border-transparent" : "border-surface-accent-800 dark:border-surface-accent-200"),
			children: checked && /* @__PURE__ */ jsx(CheckIcon$1, {
				size: iconSize.smallest,
				className: "absolute"
			})
		})
	});
});
//#endregion
//#region src/components/Paper.tsx
function Paper({ children, style, className }) {
	return /* @__PURE__ */ jsx("div", {
		className: cls(paperMixin, className),
		style,
		children
	});
}
//#endregion
//#region src/components/RadioGroup.tsx
var RadioGroup = React$1.forwardRef(({ className, ...props }, ref) => {
	return /* @__PURE__ */ jsx(RadioGroupPrimitive.Root, {
		className: cls("grid gap-2", className),
		...props,
		ref
	});
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;
var RadioGroupItem = React$1.forwardRef(({ className, ...props }, ref) => {
	return /* @__PURE__ */ jsx(RadioGroupPrimitive.Item, {
		ref,
		className: cls("aspect-square h-4 w-4 rounded-full border border-primary text-primary focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50", className),
		...props,
		children: /* @__PURE__ */ jsx(RadioGroupPrimitive.Indicator, {
			className: "flex items-center justify-center",
			children: /* @__PURE__ */ jsx("div", { className: "h-2.5 w-2.5 fill-current text-current bg-primary rounded-full" })
		})
	});
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;
//#endregion
//#region src/components/ResizablePanels.tsx
function ResizablePanels({ firstPanel, secondPanel, showFirstPanel = true, showSecondPanel = true, panelSizePercent, onPanelSizeChange, minPanelSizePx = 200, orientation = "horizontal", animateLayout = true, className, stacked = false }) {
	const containerRef = useRef(null);
	const isResizingRef = useRef(false);
	const firstPanelRef = useRef(null);
	const startPosRef = useRef(0);
	const startSizeRef = useRef(0);
	const [isResizing, setIsResizing] = useState(false);
	const isHorizontal = orientation === "horizontal";
	const handleResizeStart = useCallback((e) => {
		if (!showFirstPanel || !showSecondPanel) return;
		e.preventDefault();
		isResizingRef.current = true;
		setIsResizing(true);
		startPosRef.current = isHorizontal ? e.clientX : e.clientY;
		if (firstPanelRef.current) {
			const rect = firstPanelRef.current.getBoundingClientRect();
			startSizeRef.current = isHorizontal ? rect.width : rect.height;
		}
		document.body.style.cursor = isHorizontal ? "col-resize" : "row-resize";
		document.body.style.userSelect = "none";
	}, [
		isHorizontal,
		showFirstPanel,
		showSecondPanel
	]);
	useEffect(() => {
		const handleMouseMove = (e) => {
			if (!isResizingRef.current || !containerRef.current) return;
			const delta = (isHorizontal ? e.clientX : e.clientY) - startPosRef.current;
			let newSize = startSizeRef.current + delta;
			const containerRect = containerRef.current.getBoundingClientRect();
			const maxSize = (isHorizontal ? containerRect.width : containerRect.height) - minPanelSizePx;
			newSize = Math.max(minPanelSizePx, Math.min(newSize, maxSize));
			if (firstPanelRef.current) firstPanelRef.current.style.flexBasis = `${newSize}px`;
		};
		const handleMouseUp = () => {
			if (isResizingRef.current && containerRef.current && firstPanelRef.current) {
				const containerRect = containerRef.current.getBoundingClientRect();
				const firstPanelRect = firstPanelRef.current.getBoundingClientRect();
				const containerSize = isHorizontal ? containerRect.width : containerRect.height;
				const finalSize = isHorizontal ? firstPanelRect.width : firstPanelRect.height;
				if (containerSize > 0) {
					const newPercent = finalSize / containerSize * 100;
					firstPanelRef.current.style.flexBasis = `${newPercent}%`;
					onPanelSizeChange(Math.max(0, Math.min(100, newPercent)));
				}
				isResizingRef.current = false;
				setIsResizing(false);
				document.body.style.cursor = "";
				document.body.style.userSelect = "";
			}
		};
		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, [
		onPanelSizeChange,
		isHorizontal,
		minPanelSizePx
	]);
	const appliedBasis = !showFirstPanel ? "0%" : showSecondPanel ? `${panelSizePercent}%` : "100%";
	return /* @__PURE__ */ jsxs("div", {
		ref: containerRef,
		className: cls("relative w-full h-full flex overflow-hidden", !stacked && (isHorizontal ? "flex-row" : "flex-col"), className),
		children: [
			/* @__PURE__ */ jsx("div", {
				ref: firstPanelRef,
				className: cls(stacked ? "absolute inset-0" : "relative flex-shrink-0", "flex flex-col overflow-hidden", !stacked && !isResizing && animateLayout && "transition-[flex-basis] duration-150 ease-out", !showFirstPanel && "hidden"),
				style: stacked ? void 0 : {
					flexBasis: appliedBasis,
					minWidth: isHorizontal && showFirstPanel && showSecondPanel ? `${minPanelSizePx}px` : void 0,
					minHeight: !isHorizontal && showFirstPanel && showSecondPanel ? `${minPanelSizePx}px` : void 0,
					maxWidth: showSecondPanel ? void 0 : "100%",
					maxHeight: showSecondPanel ? void 0 : "100%"
				},
				children: firstPanel
			}),
			!stacked && showFirstPanel && showSecondPanel && /* @__PURE__ */ jsx("div", {
				className: cls("relative z-10 flex flex-shrink-0 items-center justify-center border-transparent", isHorizontal ? "border-l w-px h-full cursor-col-resize" : "border-t h-px w-full cursor-row-resize"),
				onMouseDown: handleResizeStart,
				children: /* @__PURE__ */ jsx("div", {
					className: cls("absolute flex items-center justify-center group", isHorizontal ? "w-4 h-full cursor-col-resize top-0" : "h-4 w-full cursor-row-resize left-0"),
					children: /* @__PURE__ */ jsx("div", { className: cls("bg-primary/60 dark:bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200", isHorizontal ? "w-1 h-8" : "h-1 w-8") })
				})
			}),
			/* @__PURE__ */ jsx("div", {
				className: cls(stacked ? "absolute inset-0" : "flex-grow relative min-w-0 min-h-0", "flex flex-col overflow-hidden", !showSecondPanel && "hidden"),
				children: secondPanel
			})
		]
	});
}
//#endregion
//#region src/components/SearchBar.tsx
function SearchBar({ onClick, onTextSearch, placeholder = "Search", expandable = false, size = "medium", innerClassName, className, autoFocus, disabled, loading, inputRef, initialValue }) {
	const [searchText, setSearchText] = useState(initialValue ?? "");
	const [active, setActive] = useState(false);
	const deferredValues = useDebounceValue(searchText, 200);
	/**
	* Debounce on SearchIcon text update
	*/
	React.useEffect(() => {
		if (!onTextSearch) return;
		if (deferredValues) onTextSearch(deferredValues);
		else onTextSearch(void 0);
	}, [deferredValues]);
	const clearText = useCallback(() => {
		if (!onTextSearch) return;
		setSearchText("");
		onTextSearch(void 0);
	}, [onTextSearch]);
	const heightClass = {
		smallest: "h-[28px]",
		small: "h-[32px]",
		medium: "h-[40px]",
		large: "h-[48px]"
	}[size];
	const iconPaddingClass = size === "smallest" || size === "small" ? "px-2" : size === "medium" ? "px-3" : "px-4";
	const inputPaddingClass = size === "smallest" || size === "small" ? "pl-8" : size === "medium" ? "pl-10" : "pl-12";
	return /* @__PURE__ */ jsxs("div", {
		role: "search",
		"aria-label": "Search",
		onClick,
		className: cls("relative transition-all", heightClass, "bg-surface-field border", defaultBorderMixin, "focus-within:ring-1 focus-within:ring-primary/60 focus-within:border-primary focus-within:shadow-[0_0_0_3px_rgba(0,112,244,0.1)]", "rounded-full overflow-hidden", className),
		children: [
			/* @__PURE__ */ jsx("div", {
				className: cls("absolute p-0 h-full pointer-events-none flex items-center justify-center top-0", iconPaddingClass),
				children: loading ? /* @__PURE__ */ jsx(CircularProgress, { size: "smallest" }) : /* @__PURE__ */ jsx(SearchIcon$1, {
					className: "text-text-disabled dark:text-text-disabled-dark",
					size: size === "smallest" ? 14 : size === "small" ? 18 : 20
				})
			}),
			/* @__PURE__ */ jsx("input", {
				value: searchText ?? "",
				ref: inputRef,
				onClick,
				placeholder,
				"aria-label": placeholder,
				readOnly: !onTextSearch,
				onChange: onTextSearch ? (event) => {
					setSearchText(event.target.value);
				} : void 0,
				autoFocus,
				onFocus: () => setActive(true),
				onBlur: () => setActive(false),
				className: cls((disabled || loading) && "pointer-events-none", "placeholder-text-disabled dark:placeholder-text-disabled-dark", "relative flex items-center transition-all bg-transparent outline-none focus:outline-none focus:ring-0 appearance-none border-none focus:border-transparent", inputPaddingClass, "h-full w-full text-current", size === "small" ? "text-sm" : "", expandable ? active ? "w-[220px]" : "w-[180px]" : "", innerClassName)
			}),
			searchText ? /* @__PURE__ */ jsx(IconButton, {
				className: `${size === "small" ? "mr-0 top-0" : "mr-1 top-0"} absolute right-0 z-10`,
				size: "small",
				"aria-label": "Clear search",
				onClick: clearText,
				children: /* @__PURE__ */ jsx(XIcon$1, { size: iconSize.smallest })
			}) : /* @__PURE__ */ jsx("div", { style: { width: 26 } })
		]
	});
}
//#endregion
//#region src/components/Select.tsx
var Select = forwardRef(({ inputRef, open, name, fullWidth = false, id, onOpenChange, value, onChange, onValueChange, className, inputClassName, viewportClassName, placeholder, renderValue, label, size = "large", error, disabled, padding = true, position = "item-aligned", endAdornment, invisible, children, dataType = "string", portalContainer: manualContainer, "aria-label": ariaLabel, ...props }, ref) => {
	const [openInternal, setOpenInternal] = useState(open ?? false);
	useEffect(() => {
		setOpenInternal(open ?? false);
	}, [open]);
	const contextContainer = usePortalContainer();
	const finalContainer = manualContainer ?? contextContainer ?? void 0;
	const onValueChangeInternal = useCallback((newValue) => {
		let typedValue = newValue;
		if (dataType === "boolean") {
			if (newValue === "true") typedValue = true;
			else if (newValue === "false") typedValue = false;
		} else if (dataType === "number") {
			if (!isNaN(Number(newValue)) && newValue.trim() !== "") typedValue = Number(newValue);
		}
		onValueChange?.(typedValue);
		if (onChange) onChange({ target: {
			name,
			value: typedValue
		} });
	}, [
		onChange,
		onValueChange,
		name,
		dataType
	]);
	const hasValue = Array.isArray(value) ? value.length > 0 : value != null && value !== "" && value !== void 0;
	const stringValue = value !== void 0 ? String(value) : void 0;
	const displayChildren = useMemo(() => {
		if (!hasValue || renderValue) return null;
		let found = null;
		Children.forEach(children, (child) => {
			if (React.isValidElement(child) && String(child.props.value) === String(value)) found = child.props.children;
		});
		return found;
	}, [
		children,
		hasValue,
		renderValue,
		value
	]);
	return /* @__PURE__ */ jsxs(SelectPrimitive.Root, {
		name,
		value: stringValue,
		open: openInternal,
		disabled,
		onValueChange: onValueChangeInternal,
		onOpenChange: (open) => {
			onOpenChange?.(open);
			setOpenInternal(open);
		},
		...props,
		children: [
			typeof label === "string" ? /* @__PURE__ */ jsx(SelectInputLabel, {
				error,
				children: label
			}) : label,
			/* @__PURE__ */ jsxs("div", {
				className: cls("select-none rounded-lg text-sm", invisible ? fieldBackgroundInvisibleMixin : fieldBackgroundMixin, disabled ? fieldBackgroundDisabledMixin : invisible ? "" : fieldBackgroundHoverMixin, "relative flex items-center", className, {
					"min-h-[28px]": size === "smallest",
					"min-h-[32px]": size === "small",
					"min-h-[40px]": size === "medium",
					"min-h-[48px]": size === "large",
					"w-fit": !fullWidth,
					"w-full": fullWidth
				}),
				children: [/* @__PURE__ */ jsxs(SelectPrimitive.Trigger, {
					ref: inputRef,
					id,
					asChild: false,
					type: "button",
					"aria-label": ariaLabel ?? (typeof label === "string" ? label : typeof renderValue === "string" ? renderValue : "Select an option"),
					"aria-invalid": error || void 0,
					className: cls("h-full", padding ? {
						"px-4": size === "large",
						"px-3": size === "medium",
						"px-2": size === "small" || size === "smallest"
					} : "", "outline-hidden focus:outline-hidden", "outline-none focus:outline-none", "select-none rounded-lg text-sm", error ? "text-red-500 dark:text-red-600" : "focus:text-text-primary dark:focus:text-text-primary-dark", error ? "border border-red-500 dark:border-red-600" : "", disabled ? "text-surface-accent-600 dark:text-surface-accent-400" : "text-surface-accent-800 dark:text-white", "relative flex flex-row items-center", {
						"min-h-[28px]": size === "smallest",
						"min-h-[32px]": size === "small",
						"min-h-[40px]": size === "medium",
						"min-h-[48px]": size === "large",
						"w-full": fullWidth,
						"w-fit": !fullWidth
					}, inputClassName),
					children: [/* @__PURE__ */ jsx("div", {
						ref,
						className: cls("flex-grow max-w-full flex flex-row gap-2 items-center", "overflow-visible", {
							"min-h-[28px]": size === "smallest",
							"min-h-[32px]": size === "small",
							"min-h-[40px]": size === "medium",
							"min-h-[48px]": size === "large"
						}),
						children: /* @__PURE__ */ jsx(SelectPrimitive.Value, {
							onClick: (e) => {
								e.preventDefault();
								e.stopPropagation();
							},
							placeholder,
							className: "w-full",
							children: hasValue && value !== void 0 && renderValue ? renderValue(value) : displayChildren || placeholder
						})
					}), /* @__PURE__ */ jsx(SelectPrimitive.Icon, {
						asChild: true,
						children: /* @__PURE__ */ jsx(ChevronDownIcon$1, {
							size: size === "large" ? iconSize.medium : iconSize.small,
							className: cls("transition", open ? "rotate-180" : "", {
								"px-2": size === "large",
								"px-1": size === "medium" || size === "small"
							})
						})
					})]
				}), endAdornment && /* @__PURE__ */ jsx("div", {
					className: cls("h-full flex items-center absolute right-0 pr-12"),
					onClick: (e) => {
						e.preventDefault();
						e.stopPropagation();
					},
					children: endAdornment
				})]
			}),
			/* @__PURE__ */ jsx(SelectPrimitive.Portal, {
				container: finalContainer,
				children: /* @__PURE__ */ jsx(SelectPrimitive.Content, {
					position,
					className: cls(focusedDisabled, "z-50 relative overflow-hidden border bg-surface-card p-2 rounded-lg shadow-lg animate-in fade-in-0 zoom-in-95 duration-150", defaultBorderMixin),
					children: /* @__PURE__ */ jsx(SelectPrimitive.Viewport, {
						className: cls("p-1", viewportClassName),
						style: { maxHeight: "var(--radix-select-content-available-height)" },
						children
					})
				})
			})
		]
	});
});
Select.displayName = "Select";
var SelectItem = React.memo(function SelectItem({ value, children, disabled, className }) {
	const stringValue = String(value);
	return /* @__PURE__ */ jsxs(SelectPrimitive.Item, {
		value: stringValue,
		disabled,
		className: cls("w-full", "relative flex items-center p-2 rounded-md text-sm text-surface-accent-700 dark:text-surface-accent-300", "focus:z-10", "data-[state=checked]:bg-surface-active focus:bg-surface-hover", "data-[state=checked]:focus:bg-surface-active", disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer", "[&>*]:w-full", "overflow-visible", className),
		children: [/* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children }), /* @__PURE__ */ jsx("div", {
			className: "absolute left-1 data-[state=checked]:block hidden",
			children: /* @__PURE__ */ jsx(CheckIcon$1, { size: iconSize.smallest })
		})]
	}, stringValue);
});
var SelectGroup = React.memo(function SelectGroup({ label, children, className }) {
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(SelectPrimitive.Group, {
		className: cls("text-xs text-surface-accent-900 dark:text-white uppercase tracking-wider font-semibold mt-6 first:mt-2", "px-2 py-2", className),
		children: label
	}), children] });
});
//#endregion
//#region src/components/Slider.tsx
function SliderThumb(props) {
	return /* @__PURE__ */ jsxs(TooltipPrimitive.Root, {
		open: props.hovered,
		children: [/* @__PURE__ */ jsx(TooltipPrimitive.Trigger, {
			asChild: true,
			children: /* @__PURE__ */ jsx(SliderPrimitive.Thumb, { className: cls({
				"border-primary bg-primary outline-none": !props.props.disabled,
				"border-surface-raised-hover bg-surface-raised-hover": props.props.disabled
			}, props.classes, "focus-visible:ring-4 focus-visible:ring-primary focus-visible:ring-opacity-50 focus-visible:ring-primary/50", "hover:ring-4 hover:ring-primary hover:ring-opacity-25 hover:ring-primary/25", "block rounded-full transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50") })
		}), /* @__PURE__ */ jsx(TooltipPrimitive.Content, {
			side: "top",
			sideOffset: 5,
			className: cls("TooltipContent", "max-w-lg leading-relaxed", "z-50 rounded-md px-3 py-2 text-xs leading-none bg-surface-800/95 dark:bg-surface-raised-hover font-medium text-white shadow-2xl select-none duration-400 ease-in transform opacity-100"),
			children: props.props.value?.[props.index]
		})]
	});
}
var Slider = React$1.forwardRef(({ className, size = "regular", ...props }, ref) => {
	const [hovered, setHovered] = React$1.useState(false);
	const thumbSizeClasses = size === "small" ? "h-4 w-4" : "h-6 w-6";
	return /* @__PURE__ */ jsx(TooltipPrimitive.Provider, {
		delayDuration: 200,
		children: /* @__PURE__ */ jsxs(SliderPrimitive.Root, {
			ref,
			onMouseEnter: () => setHovered(true),
			onMouseLeave: () => setHovered(false),
			className: cls("relative flex w-full touch-none select-none items-center", className),
			...props,
			children: [/* @__PURE__ */ jsx(SliderPrimitive.Track, {
				style: { height: size === "small" ? 4 : 8 },
				className: "relative w-full grow overflow-hidden rounded-full bg-surface-active",
				children: /* @__PURE__ */ jsx(SliderPrimitive.Range, { className: cls("absolute h-full", {
					"bg-primary": !props.disabled,
					"bg-surface-raised-hover": props.disabled
				}) })
			}), (props.value ?? [0]).map((_, index) => /* @__PURE__ */ jsx(SliderThumb, {
				index,
				props,
				hovered,
				classes: thumbSizeClasses
			}, index))]
		})
	});
});
Slider.displayName = "Slider";
//#endregion
//#region src/components/Sheet.tsx
var Sheet = ({ children, side = "right", title, modal = true, includeBackgroundOverlay = true, open, onOpenChange, onPointerDownOutside, onInteractOutside, transparent, className, style, overlayClassName, overlayZIndex = "z-50", overlayStyle, portalContainer, ...props }) => {
	const [displayed, setDisplayed] = useState(false);
	const [contentEl, setContentEl] = useState(null);
	useRestoreInterruptedFocus(contentEl);
	const contextContainer = usePortalContainer();
	const finalContainer = portalContainer ?? contextContainer ?? void 0;
	useEffect(() => {
		const raf = requestAnimationFrame(() => {
			setDisplayed(open);
		});
		return () => cancelAnimationFrame(raf);
	}, [open]);
	const transformValue = {
		top: "-translate-y-full",
		bottom: "translate-y-full",
		left: "-translate-x-full",
		right: "translate-x-full"
	};
	const borderClass = {
		top: "border-b",
		bottom: "border-t",
		left: "border-r",
		right: "border-l"
	};
	return /* @__PURE__ */ jsx(DialogPrimitive.Root, {
		open: displayed || open,
		modal,
		onOpenChange,
		children: /* @__PURE__ */ jsxs(DialogPrimitive.Portal, {
			container: finalContainer,
			children: [includeBackgroundOverlay && /* @__PURE__ */ jsx(DialogPrimitive.Overlay, {
				className: cls("outline-none", "fixed inset-0 bg-surface-scrim", "backdrop-blur-sm transition-all duration-100 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in", overlayZIndex, overlayClassName),
				style: overlayStyle
			}), /* @__PURE__ */ jsxs(DialogPrimitive.Content, {
				...props,
				ref: setContentEl,
				onOpenAutoFocus: (event) => event.preventDefault(),
				onPointerDownOutside,
				onInteractOutside,
				className: cls("outline-none", borderClass[side], defaultBorderMixin, "text-surface-accent-900 dark:text-white", "fixed transform z-50 transition-[transform,opacity] ease-in-out", !displayed ? "duration-150" : "duration-100", "outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus:ring-0", transparent ? "" : "shadow-md bg-surface-card", side === "top" || side === "bottom" ? "w-full" : "h-full", side === "left" || side === "top" ? "left-0 top-0" : "right-0 bottom-0", displayed && open ? "opacity-100" : "opacity-50", !displayed || !open ? transformValue[side] : "", className),
				style,
				children: [/* @__PURE__ */ jsx(DialogPrimitive.Title, {
					className: "sr-only outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus:ring-0",
					children: title ?? "Sheet"
				}), /* @__PURE__ */ jsx(PortalContainerProvider, {
					container: contentEl,
					children
				})]
			})]
		})
	});
};
//#endregion
//#region src/components/TextareaAutosize.tsx
function getStyleValue(value) {
	return parseInt(value, 10) || 0;
}
var styles$1 = { shadow: {
	visibility: "hidden",
	position: "absolute",
	overflow: "hidden",
	height: 0,
	top: 0,
	left: 0,
	transform: "translateZ(0)"
} };
function isEmpty(obj) {
	return obj === void 0 || obj === null || Object.keys(obj).length === 0 || obj.outerHeightStyle === 0 && !obj.overflow;
}
var TextareaAutosize = React$1.forwardRef(function TextareaAutosize(props, ref) {
	const { onChange, onScroll, onResize, maxRows, minRows = 1, style, value, onFocus, onBlur, sizeRef, ignoreBoxSizing, ...other } = props;
	const { current: isControlled } = React$1.useRef(value != null);
	const inputRef = React$1.useRef(null);
	const handleRef = useForkRef(ref, inputRef);
	const shadowRef = React$1.useRef(null);
	const renders = React$1.useRef(0);
	const [state, setState] = React$1.useState({ outerHeightStyle: 0 });
	const getUpdatedState = React$1.useCallback(() => {
		const input = inputRef.current;
		if (typeof window === "undefined") return { outerHeightStyle: 0 };
		const computedStyle = window.getComputedStyle(input);
		if (computedStyle.width === "0px") return { outerHeightStyle: 0 };
		const sizeReferenceElement = sizeRef?.current ?? shadowRef.current;
		const inputShallow = shadowRef.current;
		sizeReferenceElement.style.width = computedStyle.width;
		inputShallow.value = input.value || props.placeholder || "x";
		if (inputShallow.value.slice(-1) === "\n") inputShallow.value += " ";
		const boxSizing = computedStyle.boxSizing;
		const padding = getStyleValue(computedStyle.paddingBottom) + getStyleValue(computedStyle.paddingTop);
		const border = getStyleValue(computedStyle.borderBottomWidth) + getStyleValue(computedStyle.borderTopWidth);
		const minHeight = getStyleValue(computedStyle.minHeight);
		const innerHeight = sizeReferenceElement.scrollHeight;
		inputShallow.value = "x";
		const singleRowHeight = sizeReferenceElement.scrollHeight;
		let outerHeight = innerHeight;
		if (minRows) outerHeight = Math.max(Number(minRows) * singleRowHeight, outerHeight);
		if (maxRows) outerHeight = Math.min(Number(maxRows) * singleRowHeight, outerHeight);
		outerHeight = Math.max(outerHeight, singleRowHeight, minHeight);
		return {
			outerHeightStyle: outerHeight + (!ignoreBoxSizing && boxSizing === "border-box" ? padding + border : 0),
			overflow: Math.abs(outerHeight - innerHeight) <= 1
		};
	}, [
		maxRows,
		minRows,
		props.placeholder
	]);
	const updateState = React$1.useCallback((prevState, newState) => {
		const { outerHeightStyle, overflow } = newState;
		if (renders.current < 20 && (outerHeightStyle > 0 && Math.abs((prevState.outerHeightStyle || 0) - outerHeightStyle) > 1 || prevState.overflow !== overflow)) {
			renders.current += 1;
			return {
				overflow,
				outerHeightStyle
			};
		}
		if (process.env.NODE_ENV !== "production") {
			if (renders.current === 20) console.error(["MUI: Too many re-renders. The layout is unstable.", "TextareaAutosize limits the number of renders to prevent an infinite loop."].join("\n"));
		}
		return prevState;
	}, []);
	const syncHeight = React$1.useCallback(() => {
		const newState = getUpdatedState();
		if (isEmpty(newState)) return;
		if (onResize) onResize(newState);
		setState((prevState) => {
			return updateState(prevState, newState);
		});
	}, [
		getUpdatedState,
		onResize,
		updateState
	]);
	const syncHeightWithFlushSync = React$1.useCallback(() => {
		const newState = getUpdatedState();
		if (isEmpty(newState)) return;
		ReactDOM.flushSync(() => {
			setState((prevState) => {
				return updateState(prevState, newState);
			});
		});
	}, [getUpdatedState, updateState]);
	React$1.useEffect(() => {
		const handleResize = debounce(() => {
			renders.current = 0;
			if (inputRef.current) syncHeightWithFlushSync();
		});
		let resizeObserver;
		const input = inputRef.current;
		const containerWindow = window;
		if (typeof window === "undefined") return;
		containerWindow.addEventListener("resize", handleResize);
		if (typeof ResizeObserver !== "undefined") {
			resizeObserver = new ResizeObserver(handleResize);
			resizeObserver.observe(input);
		}
		return () => {
			handleResize.clear();
			containerWindow.removeEventListener("resize", handleResize);
			if (resizeObserver) resizeObserver.disconnect();
		};
	}, [syncHeightWithFlushSync]);
	useLayoutEffect(() => {
		syncHeight();
	});
	React$1.useEffect(() => {
		renders.current = 0;
	}, [value]);
	const handleChange = (event) => {
		renders.current = 0;
		if (!isControlled) syncHeight();
		if (onChange) onChange(event);
	};
	return /* @__PURE__ */ jsxs(React$1.Fragment, { children: [/* @__PURE__ */ jsx("textarea", {
		value,
		onChange: handleChange,
		className: props.className,
		ref: handleRef,
		onFocus,
		onBlur,
		rows: minRows,
		style: {
			height: state.outerHeightStyle,
			overflow: state.overflow ? "hidden" : void 0,
			...style
		},
		onScroll,
		...other
	}), /* @__PURE__ */ jsx("textarea", {
		"aria-hidden": true,
		className: cls(props.className, props.shadowClassName),
		readOnly: true,
		ref: shadowRef,
		tabIndex: -1,
		style: {
			padding: 0,
			...styles$1.shadow,
			...style
		}
	})] });
});
function useForkRef(...refs) {
	/**
	* This will create a new function if the refs passed to this hook change and are all defined.
	* This means react will call the old forkRef with `null` and the new forkRef
	* with the ref. Cleanup naturally emerges from this behavior.
	*/
	return React$1.useMemo(() => {
		if (refs.every((ref) => ref == null)) return null;
		return (instance) => {
			refs.forEach((ref) => {
				setRef(ref, instance);
			});
		};
	}, refs);
}
function setRef(ref, value) {
	if (typeof ref === "function") ref(value);
	else if (ref) ref.current = value;
}
//#endregion
//#region src/components/TextField.tsx
var TextField = forwardRef(({ value, onChange, label, type = "text", multiline = false, invisible, maxRows, minRows, disabled, error, endAdornment, autoFocus, placeholder, size = "large", className, style, inputClassName, inputStyle, inputRef: inputRefProp, ...inputProps }, ref) => {
	const fallbackRef = useRef(null);
	const inputRef = inputRefProp ?? fallbackRef;
	const autoId = useId();
	const inputId = inputProps.id ?? autoId;
	const labelId = `${inputId}-label`;
	const [focused, setFocused] = React.useState(false);
	const hasValue = value !== void 0 && value !== null && value !== "";
	useEffect(() => {
		const element = inputRef && "current" in inputRef ? inputRef.current : null;
		if (element && document.activeElement === element) setFocused(true);
	}, []);
	useEffect(() => {
		if (type !== "number") return;
		const handleWheel = (event) => {
			if (event.target instanceof HTMLElement) event.target.blur();
		};
		const element = "current" in inputRef ? inputRef.current : inputRef;
		element?.addEventListener("wheel", handleWheel);
		return () => {
			element?.removeEventListener("wheel", handleWheel);
		};
	}, [inputRef, type]);
	const input = multiline ? /* @__PURE__ */ jsx("textarea", {
		...inputProps,
		ref: inputRef,
		id: inputId,
		"aria-labelledby": label ? labelId : inputProps["aria-labelledby"],
		"aria-invalid": error || void 0,
		"aria-disabled": disabled || void 0,
		placeholder: focused || hasValue || !label ? placeholder : void 0,
		autoFocus,
		rows: typeof minRows === "string" ? parseInt(minRows) : minRows ?? 3,
		value: value ?? "",
		onChange,
		onFocus: () => setFocused(true),
		onBlur: () => setFocused(false),
		style: inputStyle,
		className: cls(invisible ? focusedInvisibleMixin : "", "rounded-lg resize-none w-full outline-none text-sm bg-transparent min-h-[64px] px-3", label ? "pt-8 pb-2" : "py-2", disabled && "outline-none opacity-50 text-surface-accent-600 dark:text-surface-accent-500", inputClassName)
	}) : /* @__PURE__ */ jsx("input", {
		...inputProps,
		ref: inputRef,
		id: inputId,
		"aria-labelledby": label ? labelId : inputProps["aria-labelledby"],
		"aria-invalid": error || void 0,
		"aria-disabled": disabled || void 0,
		disabled,
		style: inputStyle,
		className: cls("w-full outline-none bg-transparent leading-normal px-3 text-sm", "rounded-lg", "focused:text-text-primary focused:dark:text-text-primary-dark", invisible ? focusedInvisibleMixin : "", {
			"min-h-[28px]": size === "smallest",
			"min-h-[32px]": size === "small",
			"min-h-[40px]": size === "medium",
			"min-h-[48px]": size === "large"
		}, label ? {
			smallest: "pt-4 pb-1",
			small: "pt-5 pb-1",
			medium: "pt-6 pb-2",
			large: "pt-8 pb-2"
		}[size] : size === "smallest" ? "py-0.5" : size === "small" ? "py-1" : "py-2", endAdornment ? "pr-12" : "pr-3", disabled && "outline-none opacity-65 dark:opacity-60 text-surface-accent-800 dark:text-white", inputClassName),
		placeholder: focused || hasValue || !label ? placeholder : void 0,
		autoFocus,
		onFocus: () => setFocused(true),
		onBlur: () => setFocused(false),
		type,
		value: type === "number" && Number.isNaN(value) ? "" : value ?? "",
		onChange
	});
	return /* @__PURE__ */ jsxs("div", {
		ref,
		className: cls("rounded-lg relative max-w-full", invisible ? fieldBackgroundInvisibleMixin : fieldBackgroundMixin, disabled ? fieldBackgroundDisabledMixin : invisible ? "" : fieldBackgroundHoverMixin, error ? "border border-red-500 dark:border-red-600" : "", {
			"min-h-[28px]": size === "smallest",
			"min-h-[32px]": size === "small",
			"min-h-[40px]": size === "medium",
			"min-h-[48px]": size === "large"
		}, className),
		style,
		children: [
			label && /* @__PURE__ */ jsx(InputLabel, {
				id: labelId,
				htmlFor: inputId,
				className: cls("pointer-events-none absolute", size === "large" ? "top-1" : "top-[-1px]", !error ? focused ? "text-primary dark:text-primary" : "text-text-secondary dark:text-text-secondary-dark" : "text-red-500 dark:text-red-600", disabled ? "text-text-disabled dark:text-text-disabled-dark" : ""),
				shrink: hasValue || focused,
				children: label
			}),
			input,
			endAdornment && /* @__PURE__ */ jsx("div", {
				className: cls("flex flex-row justify-center items-center absolute h-full right-0 top-0", {
					"mr-4": size === "large",
					"mr-3": size === "medium",
					"mr-2": size === "small" || size === "smallest"
				}),
				children: endAdornment
			})
		]
	});
});
TextField.displayName = "TextField";
//#endregion
//#region src/components/Tabs.tsx
var TabsContext = createContext({ variant: "standard" });
function Tabs({ value, onValueChange, className, innerClassName, variant = "standard", children }) {
	const scrollContainerRef = useRef(null);
	const [showLeftScroll, setShowLeftScroll] = useState(false);
	const [showRightScroll, setShowRightScroll] = useState(false);
	const [isScrollable, setIsScrollable] = useState(false);
	const checkScroll = () => {
		if (scrollContainerRef.current) {
			const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
			setShowLeftScroll(scrollLeft > 0);
			setShowRightScroll(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
			setIsScrollable(scrollWidth > clientWidth);
		}
	};
	useEffect(() => {
		checkScroll();
		window.addEventListener("resize", checkScroll);
		let observer;
		if (scrollContainerRef.current) {
			observer = new ResizeObserver(checkScroll);
			observer.observe(scrollContainerRef.current);
			if (scrollContainerRef.current.firstElementChild) observer.observe(scrollContainerRef.current.firstElementChild);
		}
		return () => {
			window.removeEventListener("resize", checkScroll);
			observer?.disconnect();
		};
	}, [children]);
	const scroll = (direction) => {
		if (scrollContainerRef.current) {
			const container = scrollContainerRef.current;
			const scrollAmount = Math.max(container.clientWidth / 2, 200);
			const targetScroll = container.scrollLeft + (direction === "left" ? -scrollAmount : scrollAmount);
			container.scrollTo({
				left: targetScroll,
				behavior: "smooth"
			});
		}
	};
	const contextValue = useMemo(() => ({ variant }), [variant]);
	return /* @__PURE__ */ jsx(TabsContext.Provider, {
		value: contextValue,
		children: /* @__PURE__ */ jsxs(TabsPrimitive.Root, {
			value,
			onValueChange,
			className: cls("relative flex flex-row items-center min-w-0 w-full", className),
			children: [
				isScrollable && /* @__PURE__ */ jsx("button", {
					type: "button",
					disabled: !showLeftScroll,
					"aria-label": "Scroll tabs left",
					onClick: () => scroll("left"),
					className: cls("absolute left-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center rounded-md transition-all h-8 w-6", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface-400", !showLeftScroll ? "pointer-events-none opacity-0" : "text-surface-600 dark:text-surface-400 hover:bg-surface-hover", "bg-surface-card border shadow-sm", "border-hairline"),
					children: /* @__PURE__ */ jsx(ChevronLeftIcon$1, { size: iconSize.smallest })
				}),
				/* @__PURE__ */ jsx("div", {
					ref: scrollContainerRef,
					className: "flex-1 overflow-x-auto no-scrollbar min-w-0",
					onScroll: checkScroll,
					style: {
						scrollbarWidth: "none",
						msOverflowStyle: "none"
					},
					children: /* @__PURE__ */ jsx(TabsPrimitive.List, {
						className: cls(variant === "standard" && "inline-flex h-9 items-center justify-start rounded-md bg-surface-field p-1 text-surface-600 dark:text-surface-400 gap-2", variant === "boxy" && "flex items-end h-full", variant === "pill" && "flex items-center gap-0.5", innerClassName),
						children
					})
				}),
				isScrollable && /* @__PURE__ */ jsx("button", {
					type: "button",
					disabled: !showRightScroll,
					"aria-label": "Scroll tabs right",
					onClick: () => scroll("right"),
					className: cls("absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center rounded-md transition-all h-8 w-6", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface-400", !showRightScroll ? "pointer-events-none opacity-0" : "text-surface-600 dark:text-surface-400 hover:bg-surface-hover", "bg-surface-card border shadow-sm", "border-hairline"),
					children: /* @__PURE__ */ jsx(ChevronRightIcon$1, { size: iconSize.smallest })
				})
			]
		})
	});
}
function Tab({ value, className, innerClassName, children, disabled }) {
	const { variant } = useContext(TabsContext);
	return /* @__PURE__ */ jsx(TabsPrimitive.Trigger, {
		value,
		disabled,
		className: cls("inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all", "focus-visible:outline-none focus-visible:ring-surface-400", "disabled:pointer-events-none disabled:opacity-50", variant === "standard" && "rounded-md px-3 py-1 data-[state=active]:bg-surface-lifted data-[state=active]:text-surface-900 data-[state=active]:shadow-sm dark:data-[state=active]:text-surface-50", variant === "boxy" && cls("flex-shrink-0 flex items-center gap-1.5 px-3.5 h-9 cursor-pointer text-xs font-medium transition-colors group relative box-border", "rounded-t-lg -mb-px border border-transparent border-b-0", "data-[state=active]:bg-surface-card data-[state=active]:border-hairline", "data-[state=active]:text-text-primary dark:data-[state=active]:text-text-primary-dark", "text-text-secondary dark:text-text-secondary-dark hover:bg-surface-hover"), variant === "pill" && cls("px-2 py-0.5 rounded-md text-[10px] font-medium transition-colors", "data-[state=active]:bg-primary/10 data-[state=active]:text-primary dark:data-[state=active]:bg-primary/20 dark:data-[state=active]:text-primary", "text-text-disabled dark:text-text-disabled-dark hover:text-text-secondary dark:hover:text-text-secondary-dark"), className, variant === "standard" && innerClassName),
		children
	});
}
//#endregion
//#region src/components/Table.tsx
var Table = React.memo(({ children, className, style, ...rest }) => /* @__PURE__ */ jsx("table", {
	className: cls("text-left text-text-primary dark:text-text-primary-dark rounded-lg overflow-x-auto", className),
	style,
	...rest,
	children
}));
var TableBody = React.memo(({ children, className, ...rest }) => /* @__PURE__ */ jsx("tbody", {
	className: cls("bg-surface-card text-sm divide-y divide-hairline", className),
	...rest,
	children
}));
var TableHeader = React.memo(({ children, className, ...rest }) => /* @__PURE__ */ jsx("thead", {
	...rest,
	children: /* @__PURE__ */ jsx("tr", {
		className: cls(defaultBorderMixin, "text-sm font-medium text-surface-700 dark:text-surface-accent-300", "bg-surface-sheet border-b", className),
		children
	})
}));
var TableRow = React.memo(({ children, className, onClick, style, ...rest }) => /* @__PURE__ */ jsx("tr", {
	onClick,
	style,
	className: cls("bg-surface-card", onClick ? "transition-colors duration-150 hover:bg-surface-card-hover cursor-pointer" : "", className),
	...rest,
	children
}));
var TableCell = React.memo(({ children, header = false, scope = "", align, className, style, colspan, ...rest }) => {
	const ref = useRef(null);
	return /* @__PURE__ */ jsx(header || getParentName(ref.current) === "TableHeader" ? "th" : "td", {
		scope,
		colSpan: colspan,
		ref,
		style,
		className: cls("px-4 py-3 text-clip ", align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left", className),
		...rest,
		children
	});
});
function getParentName(element) {
	if (element) {
		const key = Object.keys(element).find((key) => {
			return key.startsWith("__reactFiber$") || key.startsWith("__reactInternalInstance$");
		});
		if (key) {
			const domFiber = element[key];
			const getComponentFiber = (fiber) => {
				let parentFiber = fiber.return;
				while (parentFiber && typeof parentFiber.type === "string") parentFiber = parentFiber.return;
				return parentFiber;
			};
			let fiber = getComponentFiber(domFiber);
			fiber = getComponentFiber(fiber);
			return fiber?.elementType?.name;
		}
	}
}
Table.displayName = "Table";
TableBody.displayName = "TableBody";
TableHeader.displayName = "TableHeader";
TableRow.displayName = "TableRow";
TableCell.displayName = "TableCell";
//#endregion
//#region src/components/Popover.tsx
function Popover({ trigger, children, open, onOpenChange, side, sideOffset = 5, align, alignOffset, arrowPadding, sticky, hideWhenDetached, avoidCollisions, enabled = true, modal = false, portalContainer, className, onMouseEnter, onMouseLeave }) {
	useInjectStyles("Popover", popoverStyles);
	const contextContainer = usePortalContainer();
	const finalContainer = portalContainer ?? contextContainer ?? void 0;
	if (!enabled) return /* @__PURE__ */ jsx(Fragment, { children: trigger });
	return /* @__PURE__ */ jsxs(PopoverPrimitive.Root, {
		open,
		onOpenChange,
		modal,
		children: [/* @__PURE__ */ jsx(PopoverPrimitive.Trigger, {
			asChild: true,
			children: trigger
		}), /* @__PURE__ */ jsx(PopoverPrimitive.Portal, {
			container: finalContainer,
			children: /* @__PURE__ */ jsxs(PopoverPrimitive.Content, {
				className: cls(paperMixin, "PopoverContent z-50", className),
				side,
				sideOffset,
				align,
				alignOffset,
				arrowPadding,
				sticky,
				hideWhenDetached,
				avoidCollisions,
				onMouseEnter,
				onMouseLeave,
				children: [children, /* @__PURE__ */ jsx(PopoverPrimitive.Arrow, { className: "fill-white dark:fill-surface-900" })]
			})
		})]
	});
}
var popoverStyles = `
/* ... (styles remain unchanged) ... */
.PopoverContent {
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
}
.PopoverContent[data-state='open'][data-side='top'] {
  animation-name: slideDownAndFade;
}
.PopoverContent[data-state='open'][data-side='right'] {
  animation-name: slideLeftAndFade;
}
.PopoverContent[data-state='open'][data-side='bottom'] {
  animation-name: slideUpAndFade;
}
.PopoverContent[data-state='open'][data-side='left'] {
  animation-name: slideRightAndFade;
}

@keyframes slideUpAndFade {
  from {
    opacity: 0;
    transform: translateY(2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideRightAndFade {
  from {
    opacity: 0;
    transform: translateX(-2px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideDownAndFade {
  from {
    opacity: 0;
    transform: translateY(-2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideLeftAndFade {
  from {
    opacity: 0;
    transform: translateX(2px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
`;
//#endregion
//#region src/components/Badge.tsx
var getColor = (color) => {
	switch (color) {
		case "primary": return "bg-primary";
		case "secondary": return "bg-secondary";
		case "warning": return "bg-orange-500";
		case "error": return "bg-red-500";
		default: return "bg-surface-raised-hover";
	}
};
var Badge = React.forwardRef(({ color = "primary", invisible = false, children }, ref) => {
	return /* @__PURE__ */ jsxs("div", {
		ref,
		className: "relative inline-block w-fit",
		children: [children, /* @__PURE__ */ jsx("span", { className: `absolute top-0.5 right-0.5 transform translate-x-1/2 -translate-y-1/2 rounded-full
        ${getColor(color)}
        transition-all duration-200 ease-out
        ${invisible ? "w-0 h-0" : "w-2 h-2"}` })]
	});
});
Badge.displayName = "Badge";
//#endregion
//#region src/components/DebouncedTextField.tsx
function DebouncedTextField(props) {
	const [internalValue, setInternalValue] = useState(props.value ?? "");
	const lastSentValueRef = useRef(props.value ?? "");
	const timerRef = useRef(void 0);
	useEffect(() => {
		const externalValue = props.value ?? "";
		if (externalValue !== lastSentValueRef.current) {
			setInternalValue(externalValue);
			lastSentValueRef.current = externalValue;
		}
	}, [props.value]);
	useEffect(() => {
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current);
		};
	}, []);
	const flushChange = useCallback((value, event) => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = void 0;
		}
		if (value !== props.value && props.onChange) {
			lastSentValueRef.current = value;
			const e = {
				...event,
				target: {
					...event?.target,
					value,
					name: props.name
				}
			};
			props.onChange(e);
		}
	}, [
		props.value,
		props.onChange,
		props.name
	]);
	const internalOnChange = useCallback((event) => {
		const newValue = event.target.value;
		setInternalValue(newValue);
		if (timerRef.current) clearTimeout(timerRef.current);
		const eventCopy = {
			...event,
			target: {
				...event?.target,
				name: event?.target?.name
			}
		};
		timerRef.current = setTimeout(() => {
			flushChange(newValue, eventCopy);
		}, 150);
	}, [flushChange]);
	const internalOnBlur = useCallback((event) => {
		flushChange(internalValue, event);
		props.onBlur?.(event);
	}, [
		internalValue,
		flushChange,
		props.onBlur
	]);
	return /* @__PURE__ */ jsx(TextField, {
		...props,
		onChange: internalOnChange,
		onBlur: internalOnBlur,
		value: internalValue
	});
}
//#endregion
//#region src/components/Skeleton.tsx
var styles = `
@keyframes shimmer {
  0% {
    transform: translateX(-150%);
  }
  100% {
    transform: translateX(150%);
  }
}
`;
function Skeleton({ width, height, className }) {
	useInjectStyles("Skeleton", styles);
	return /* @__PURE__ */ jsx("span", {
		style: {
			width: width === void 0 ? void 0 : `${width}px`,
			height: height === void 0 ? void 0 : `${height}px`
		},
		className: cls("block relative overflow-hidden", "bg-surface-raised rounded-md", "max-w-full max-h-full", width === void 0 ? "w-full" : void 0, height === void 0 ? "h-3" : void 0, className),
		children: /* @__PURE__ */ jsx("span", {
			className: "absolute inset-0 bg-gradient-to-r from-transparent via-white/50 dark:via-white/8 to-transparent",
			style: { animation: "shimmer 1.8s ease-in-out infinite" }
		})
	});
}
//#endregion
//#region src/components/ToggleButtonGroup.tsx
/**
* A toggle button group component for selecting one option from a set.
* Displays options as buttons in a horizontal row with active state styling.
*/
function ToggleButtonGroup({ value, onValueChange, options, className }) {
	return /* @__PURE__ */ jsx("div", {
		role: "group",
		"aria-label": "Toggle options",
		className: cls("inline-flex flex-row bg-surface-field rounded-lg p-1 gap-1", className),
		children: options.map((option) => /* @__PURE__ */ jsxs("button", {
			type: "button",
			onClick: (e) => {
				e.stopPropagation();
				if (!option.disabled) onValueChange(option.value);
			},
			disabled: option.disabled,
			"aria-pressed": value === option.value,
			"aria-disabled": option.disabled || void 0,
			className: cls("flex flex-row items-center justify-center gap-2 h-6 px-3.5 rounded-md transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50", value === option.value ? "bg-surface-lifted text-text-primary dark:text-text-primary-dark shadow-sm" : "text-text-secondary dark:text-text-secondary-dark hover:bg-surface-hover", option.disabled && "opacity-50 cursor-not-allowed"),
			children: [option.icon, /* @__PURE__ */ jsx("span", {
				className: "text-xs font-medium",
				children: option.label
			})]
		}, option.value))
	});
}
//#endregion
//#region src/components/VirtualTable/VirtualTableHeader.tsx
/**
* What the next click on this column's sort control will do, spelled out for
* the tooltip and for screen readers. An unlabelled arrow that means three
* different things depending on the state it is in is a control you have to
* click to learn.
*/
function sortActionTitle(sort, sortPosition) {
	const rank = sortPosition !== void 0 ? ` (sort key ${sortPosition + 1})` : "";
	const addKey = " — shift-click to add it under the current sort instead";
	if (sort === "asc") return `Sorted ascending${rank}. Click to sort descending.${addKey}`;
	if (sort === "desc") return `Sorted descending${rank}. Click to remove this sort.${addKey}`;
	return `Click to sort ascending.${addKey}`;
}
var VirtualTableHeader = React.memo(function VirtualTableHeader({ resizeHandleRef, columnIndex, isResizingIndex, sort, sortPosition, onColumnSort, onFilterUpdate, filter, column, onClickResizeColumn, createFilterField, AdditionalHeaderWidget, isDragging, isDraggable }) {
	const [onHover, setOnHover] = useState(false);
	const [openFilter, setOpenFilter] = React.useState(false);
	const [hidden, setHidden] = React.useState(false);
	const handleSettingsClick = useCallback((_event) => {
		setOpenFilter(true);
	}, []);
	const update = useCallback((filterForProperty, newOpenFilterState) => {
		onFilterUpdate(column, filterForProperty);
		if (newOpenFilterState !== void 0) setOpenFilter(newOpenFilterState);
	}, [column, onFilterUpdate]);
	const hovered = !(isResizingIndex !== columnIndex && isResizingIndex > 0) && (onHover || isResizingIndex === columnIndex);
	return /* @__PURE__ */ jsx(ErrorBoundary, { children: /* @__PURE__ */ jsxs("div", {
		className: cls("flex py-0 px-3 h-full text-xs uppercase font-semibold relative select-none items-center", isDragging ? "bg-primary-bg" : "bg-surface-sheet", "text-text-secondary hover:text-text-primary dark:text-text-secondary-dark dark:hover:text-text-primary-dark", !isDragging && "hover:bg-surface-hover", column.frozen ? "sticky left-0 z-10" : "relative z-0", isDraggable && "cursor-grab"),
		style: {
			left: column.frozen ? 0 : void 0,
			minWidth: column.width,
			maxWidth: column.width
		},
		onMouseEnter: () => setOnHover(true),
		onMouseMove: () => setOnHover(true),
		onMouseLeave: () => setOnHover(false),
		children: [
			/* @__PURE__ */ jsx("div", {
				className: "overflow-hidden grow",
				children: /* @__PURE__ */ jsxs("div", {
					className: `flex items-center justify-${column.headerAlign} flex-row`,
					children: [column.icon && /* @__PURE__ */ jsx("div", {
						className: "flex-shrink-0 flex items-center justify-center mr-1",
						children: React.isValidElement(column.icon) ? React.cloneElement(column.icon, {
							size: 14,
							className: cls(column.icon.props?.className, "flex-shrink-0")
						}) : column.icon
					}), /* @__PURE__ */ jsx("div", {
						className: "truncate -webkit-box w-full mr-1 overflow-hidden",
						style: {
							WebkitBoxOrient: "vertical",
							WebkitLineClamp: 2,
							justifyContent: column.align
						},
						children: column.title
					})]
				})
			}),
			/* @__PURE__ */ jsxs(Fragment, { children: [AdditionalHeaderWidget && /* @__PURE__ */ jsx(AdditionalHeaderWidget, { onHover: onHover || openFilter }), column.sortable && (sort || hovered || openFilter) && /* @__PURE__ */ jsxs("div", {
				className: "flex-shrink-0 flex items-center",
				children: [/* @__PURE__ */ jsx(Badge, {
					color: "secondary",
					invisible: !sort,
					children: /* @__PURE__ */ jsxs(IconButton, {
						size: "small",
						className: onHover || openFilter ? "bg-surface-card" : void 0,
						title: sortActionTitle(sort, sortPosition),
						"aria-label": sortActionTitle(sort, sortPosition),
						onClick: (event) => {
							onColumnSort(column.key, event.shiftKey);
						},
						children: [
							!sort && /* @__PURE__ */ jsx(ArrowUpIcon$1, {
								size: 14,
								className: "flex-shrink-0"
							}),
							sort === "asc" && /* @__PURE__ */ jsx(ArrowUpIcon$1, {
								size: 14,
								className: "flex-shrink-0"
							}),
							sort === "desc" && /* @__PURE__ */ jsx(ArrowUpIcon$1, {
								size: 14,
								className: "flex-shrink-0 rotate-180"
							})
						]
					})
				}), sort && sortPosition !== void 0 && /* @__PURE__ */ jsx("span", {
					className: "flex-shrink-0 -ml-0.5 mr-0.5 text-[10px] font-semibold leading-none tabular-nums text-text-secondary dark:text-text-secondary-dark",
					"aria-hidden": true,
					children: sortPosition + 1
				})]
			})] }),
			column.filter && createFilterField && /* @__PURE__ */ jsx("div", {
				className: "flex-shrink-0",
				children: /* @__PURE__ */ jsx(Badge, {
					color: "secondary",
					invisible: !filter,
					children: /* @__PURE__ */ jsx(Popover, {
						open: openFilter,
						onOpenChange: setOpenFilter,
						className: hidden ? "hidden" : void 0,
						modal: true,
						trigger: /* @__PURE__ */ jsx(IconButton, {
							className: onHover || openFilter ? "bg-surface-card" : void 0,
							size: "small",
							onClick: handleSettingsClick,
							children: /* @__PURE__ */ jsx(FilterIcon$1, {
								size: 14,
								className: "flex-shrink-0"
							})
						}),
						children: /* @__PURE__ */ jsx(FilterForm, {
							column,
							filter,
							onHover,
							onFilterUpdate: update,
							createFilterField,
							hidden,
							setHidden
						})
					})
				})
			}),
			column.resizable && /* @__PURE__ */ jsx("div", {
				ref: resizeHandleRef,
				"data-no-dnd": "true",
				className: cls("absolute h-full w-[6px] top-0 right-0 cursor-col-resize", hovered && "bg-hairline-strong"),
				onPointerDown: (e) => {
					e.stopPropagation();
					if (onClickResizeColumn) onClickResizeColumn(columnIndex, column);
				}
			})
		]
	}) });
}, deepEqual);
function FilterForm({ column, onFilterUpdate, filter, onHover, createFilterField, hidden, setHidden }) {
	const id = column.key;
	const [filterInternal, setFilterInternal] = useState(filter);
	useEffect(() => {
		setFilterInternal(filter);
	}, [filter]);
	if (!column.filter) return null;
	const submit = () => {
		onFilterUpdate(filterInternal, false);
	};
	const reset = (_e) => {
		onFilterUpdate(void 0, false);
	};
	const filterIsSet = !!filter;
	const filterField = createFilterField({
		id,
		filterValue: filterInternal,
		setFilterValue: setFilterInternal,
		column,
		hidden,
		setHidden
	});
	if (!filterField) return null;
	return /* @__PURE__ */ jsxs("form", {
		noValidate: true,
		onSubmit: (e) => {
			e.stopPropagation();
			e.preventDefault();
			submit();
		},
		className: "text-surface-900 dark:text-white",
		children: [
			/* @__PURE__ */ jsx("div", {
				className: cls(defaultBorderMixin, "py-4 px-6 typography-label border-b"),
				children: column.title ?? id
			}),
			filterField && /* @__PURE__ */ jsx("div", {
				className: "m-4 w-[400px]",
				children: filterField
			}),
			/* @__PURE__ */ jsxs("div", {
				className: "flex justify-end m-4",
				children: [/* @__PURE__ */ jsx(Button, {
					className: "mr-4",
					disabled: !filterIsSet,
					variant: "text",
					type: "reset",
					"aria-label": "filter clear",
					onClick: reset,
					children: "Clear"
				}), /* @__PURE__ */ jsx(Button, {
					type: "submit",
					children: "Filter"
				})]
			})
		]
	});
}
//#endregion
//#region src/components/VirtualTable/VirtualTableHeaderRow.tsx
var SortableColumnHeader = ({ column, columnIndex, columnRefs, isResizing, onFilterUpdate, filter, sortIndex, sortCount, onColumnSort, onClickResizeColumn, createFilterField, isDragging, isDraggable }) => {
	const [isPressing, setIsPressing] = useState(false);
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
		id: column.key,
		disabled: !isDraggable || column.frozen
	});
	const style = {
		transform: transform ? `translateX(${transform.x}px)` : void 0,
		transition: isDragging ? void 0 : transition,
		minWidth: column.width,
		maxWidth: column.width,
		width: column.width
	};
	const combinedListeners = isDraggable ? {
		...listeners,
		onPointerDown: (e) => {
			setIsPressing(true);
			listeners?.onPointerDown?.(e);
		},
		onPointerUp: () => setIsPressing(false),
		onPointerCancel: () => setIsPressing(false)
	} : {};
	React.useEffect(() => {
		if (!isDragging) setIsPressing(false);
	}, [isDragging]);
	return /* @__PURE__ */ jsx("div", {
		ref: setNodeRef,
		style,
		className: cls("flex-shrink-0 h-full", column.frozen && "sticky left-0 z-10"),
		...attributes,
		...combinedListeners,
		children: /* @__PURE__ */ jsx(VirtualTableHeader, {
			resizeHandleRef: columnRefs[columnIndex],
			columnIndex,
			isResizingIndex: isResizing,
			onFilterUpdate,
			filter,
			sort: sortIndex.get(column.key)?.direction,
			sortPosition: sortCount > 1 ? sortIndex.get(column.key)?.position : void 0,
			onColumnSort,
			onClickResizeColumn,
			column,
			createFilterField,
			AdditionalHeaderWidget: column.AdditionalHeaderWidget,
			isDragging: isDragging || isPressing,
			isDraggable
		})
	});
};
var VirtualTableHeaderRow = ({ columns, sortIndex, onColumnSort, onFilterUpdate, filter, onColumnResize, onColumnResizeEnd, createFilterField, AddColumnComponent, onColumnsOrderChange, data, cellRenderer: CellRenderer, rowHeight = 54, draggingColumnId, headerHeight = 40 }) => {
	const columnRefs = useMemo(() => columns.map(() => createRef()), [columns.length]);
	const [isResizing, setIsResizing] = useState(-1);
	const adjustWidthColumn = useCallback((index, width, end) => {
		const column = columns[index];
		const minWidth = 100;
		const maxWidth = 800;
		const newWidth = width > maxWidth ? maxWidth : width < minWidth ? minWidth : width;
		const params = {
			width: newWidth,
			key: column.key,
			column: {
				...column,
				width: newWidth
			}
		};
		if (!end) onColumnResize(params);
		else onColumnResizeEnd(params);
	}, [
		columns,
		onColumnResize,
		onColumnResizeEnd
	]);
	const getEventNewWidth = useCallback((e) => {
		if (isResizing >= 0) {
			const left = columnRefs[isResizing].current?.parentElement?.getBoundingClientRect().left;
			if (!left) return;
			return e.clientX - left;
		}
	}, [columnRefs, isResizing]);
	const setCursorDocument = useCallback((isResizing) => {
		document.body.style.cursor = isResizing ? "col-resize" : "auto";
	}, []);
	const handleOnMouseMove = useCallback((e) => {
		e.stopPropagation();
		e.preventDefault();
		const newWidth = getEventNewWidth(e);
		if (newWidth) adjustWidthColumn(isResizing, newWidth, false);
	}, [
		adjustWidthColumn,
		getEventNewWidth,
		isResizing
	]);
	const handleOnMouseUp = useCallback((e) => {
		e.stopPropagation();
		e.preventDefault();
		const newWidth = getEventNewWidth(e);
		if (newWidth) adjustWidthColumn(isResizing, newWidth, true);
		setIsResizing(-1);
		setCursorDocument(false);
	}, [
		adjustWidthColumn,
		getEventNewWidth,
		isResizing,
		setCursorDocument
	]);
	const removeResizingListeners = useCallback(() => {
		document.removeEventListener("mousemove", handleOnMouseMove);
		document.removeEventListener("mouseup", handleOnMouseUp);
	}, [handleOnMouseMove, handleOnMouseUp]);
	const attachResizeListeners = useCallback(() => {
		document.addEventListener("mousemove", handleOnMouseMove);
		document.addEventListener("mouseup", handleOnMouseUp);
	}, [handleOnMouseMove, handleOnMouseUp]);
	useEffect(() => {
		if (isResizing >= 0) attachResizeListeners();
		else removeResizingListeners();
		return () => {
			removeResizingListeners();
		};
	}, [
		attachResizeListeners,
		isResizing,
		removeResizingListeners
	]);
	const onClickResizeColumn = useCallback((index) => {
		setIsResizing(index);
		setCursorDocument(true);
	}, [setCursorDocument]);
	return /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs("div", {
		className: cls(defaultBorderMixin, "z-20 sticky min-w-full flex w-fit flex-row top-0 left-0 border-b bg-surface-sheet"),
		style: { height: headerHeight },
		children: [columns.map((column, columnIndex) => {
			const columnFilter = column && filter && filter[column.key] ? filter[column.key] : void 0;
			const filterForThisProperty = columnFilter ? Array.isArray(columnFilter[0]) ? columnFilter[0] : columnFilter : void 0;
			const isDraggable = !column.frozen && !!onColumnsOrderChange;
			const isDragging = draggingColumnId === column.key;
			return /* @__PURE__ */ jsx(ErrorBoundary, { children: /* @__PURE__ */ jsx(SortableColumnHeader, {
				column,
				columnIndex,
				columnRefs,
				isResizing,
				onFilterUpdate,
				filter: filterForThisProperty,
				sortIndex,
				sortCount: sortIndex.size,
				onColumnSort,
				onClickResizeColumn,
				createFilterField,
				isDragging,
				isDraggable
			}) }, "header_" + column.key);
		}), AddColumnComponent && /* @__PURE__ */ jsx(AddColumnComponent, {})]
	}) });
};
/**
* The same fix `VirtualTable` carries, one level down: `React.memo` takes the
* props type as its own type argument, so the annotation on the call pins the
* row type at the boundary and discards the `<T>` the inner function declares.
* Pinned, the row a caller passes in and the row its `onRowClick` and
* `rowClassName` receive were unrelated types, which is what every consumer was
* paying for with a cast.
*/
var VirtualTableRow = React.memo(function VirtualTableRow({ rowData, rowIndex, children, onRowClick, rowHeight, style, hoverRow, rowClassName }) {
	const onClick = useCallback((event) => {
		if (onRowClick && rowData) onRowClick({
			rowData,
			rowIndex,
			event
		});
	}, [
		onRowClick,
		rowData,
		rowIndex
	]);
	return /* @__PURE__ */ jsx("div", {
		className: cls("group flex min-w-full text-sm border-b border-hairline bg-surface-card", rowClassName && rowData ? rowClassName(rowData) : "", {
			"hover:!bg-surface-card-hover": hoverRow,
			"cursor-pointer ": onRowClick
		}),
		onClick,
		style: {
			...style,
			height: rowHeight,
			width: "fit-content"
		},
		children
	});
}, deepEqual);
//#endregion
//#region src/components/VirtualTable/VirtualTableCell.tsx
var VirtualTableCell = React.memo(function VirtualTableCell(props) {
	return props.rowData ? props.cellRenderer({
		cellData: props.cellData,
		rowData: props.rowData,
		rowIndex: props.rowIndex,
		isScrolling: false,
		column: props.column,
		columns: props.columns,
		columnIndex: props.columnIndex,
		width: props.column.width,
		sortableNodeRef: props.sortableNodeRef,
		sortableStyle: props.sortableStyle,
		sortableAttributes: props.sortableAttributes,
		isDragging: props.isDragging,
		isDraggable: props.isDraggable,
		frozen: props.frozen,
		extraData: props.extraData
	}) : null;
}, (a, b) => {
	return deepEqual(a.rowData, b.rowData) && deepEqual(a.column, b.column) && deepEqual(a.cellData, b.cellData) && a.rowIndex === b.rowIndex && a.columnIndex === b.columnIndex && a.isDragging === b.isDragging && a.isDraggable === b.isDraggable && a.frozen === b.frozen && deepEqual(a.extraData, b.extraData);
});
//#endregion
//#region src/components/VirtualTable/VirtualTable.tsx
var VirtualListContext = createContext(null);
VirtualListContext.displayName = "VirtualListContext";
var innerElementType = forwardRef(({ children, ...rest }, ref) => {
	return /* @__PURE__ */ jsx(VirtualListContext.Consumer, { children: (virtualTableProps) => {
		const customView = virtualTableProps.customView;
		const headerHeight = virtualTableProps.headerHeight ?? 48;
		return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("div", {
			id: "virtual-table",
			style: {
				position: "relative",
				height: "100%",
				zIndex: 0
			},
			children: /* @__PURE__ */ jsxs("div", {
				ref,
				...rest,
				style: {
					...rest?.style,
					height: (typeof rest?.style?.height === "number" ? rest.style.height : parseFloat(rest?.style?.height || "0")) + headerHeight,
					minHeight: "100%",
					position: "relative"
				},
				children: [/* @__PURE__ */ jsx("div", {
					style: {
						position: "sticky",
						top: 0,
						zIndex: 20
					},
					children: /* @__PURE__ */ jsx(VirtualTableHeaderRow, { ...virtualTableProps })
				}), !customView && children]
			})
		}), customView && /* @__PURE__ */ jsx("div", {
			style: {
				position: "sticky",
				top: virtualTableProps.headerHeight ?? 48,
				flexGrow: 1,
				height: `calc(100% - ${virtualTableProps.headerHeight ?? 48}px)`,
				marginTop: `calc(${virtualTableProps.headerHeight ?? 48}px - 100vh)`,
				left: 0
			},
			children: customView
		})] });
	} });
});
/**
* `React.memo` is not generic-preserving: it takes the props type as its own
* type argument, so annotating the call with
* `VirtualTableProps<Record<string, unknown>>` pinned `T` at the boundary and
* discarded the `<T>` the inner function declares. The exported component was
* therefore not generic at all — every consumer got `Record<string, unknown>`,
* `columns` and `cellRenderer` were unrelated to `data`, and a caller writing
* `<VirtualTable<Post> …>` was told the component expects no type arguments.
*
* Re-asserting the call signature is the standard way to carry a type parameter
* across `memo`; it is the only cast here, and it restores the generic the
* implementation always had. Runtime behaviour is unchanged — this is the same
* memoized component under a type that describes it.
*/
var VirtualTable = React.memo(function VirtualTable({ data, onResetPagination, onEndReached, endOffset = 600, rowHeight = 54, headerHeight = 40, columns: columnsProp, onRowClick, onColumnResize, onColumnResizeEnd, filter: filterInput, checkFilterCombination, onFilterUpdate, sortBy, error, emptyComponent, onSortByUpdate, onScroll: onScrollProp, loading, cellRenderer, hoverRow, createFilterField, rowClassName, style, className, endAdornment, AddColumnComponent, initialScroll = 0, onColumnsOrderChange, extraData }) {
	const sortIndex = useMemo(() => {
		const index = /* @__PURE__ */ new Map();
		(sortBy ?? []).forEach(([key, direction], position) => {
			if (!index.has(key)) index.set(key, {
				direction,
				position
			});
		});
		return index;
	}, [sortBy]);
	const [columns, setColumns] = useState(columnsProp);
	const tableRef = useRef(null);
	const endReachCallbackThreshold = useRef(0);
	const debouncedScroll = useDebounceCallback(onScrollProp, 200);
	const [draggingColumnId, setDraggingColumnId] = useState(null);
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
	const handleDragStart = useCallback((event) => {
		setDraggingColumnId(event.active.id);
	}, []);
	const handleDragEnd = useCallback((event) => {
		const { active, over } = event;
		setDraggingColumnId(null);
		if (over && active.id !== over.id && onColumnsOrderChange) {
			const oldIndex = columns.findIndex((col) => col.key === active.id);
			const newIndex = columns.findIndex((col) => col.key === over.id);
			if (oldIndex !== -1 && newIndex !== -1) {
				const newColumns = arrayMove(columns, oldIndex, newIndex);
				setColumns(newColumns);
				onColumnsOrderChange(newColumns);
			}
		}
	}, [columns, onColumnsOrderChange]);
	const handleDragCancel = useCallback(() => {
		setDraggingColumnId(null);
	}, []);
	useEffect(() => {
		if (tableRef.current && initialScroll) {
			const { scrollLeft } = tableRef.current;
			tableRef.current.scrollTo(scrollLeft, initialScroll);
		}
	}, [tableRef, initialScroll]);
	useEffect(() => {
		setColumns(columnsProp);
	}, [columnsProp]);
	const [measureRef, bounds] = useMeasure({
		polyfill: ResizeObserver,
		scroll: true,
		offsetSize: true
	});
	const onColumnResizeInternal = useCallback((params) => {
		setColumns((prevColumns) => prevColumns.map((column) => column.key === params.column.key ? params.column : column));
	}, []);
	const onColumnResizeEndInternal = useCallback((params) => {
		setColumns(columns.map((column) => column.key === params.column.key ? params.column : column));
		if (onColumnResize) onColumnResize(params);
		if (onColumnResizeEnd) onColumnResizeEnd(params);
	}, [
		columns,
		onColumnResize,
		onColumnResizeEnd
	]);
	const filterRef = useRef(void 0);
	useEffect(() => {
		filterRef.current = filterInput;
	}, [filterInput]);
	const scrollToTop = useCallback(() => {
		endReachCallbackThreshold.current = 0;
		if (tableRef.current) tableRef.current.scrollTo(tableRef.current?.scrollLeft, 0);
	}, []);
	/**
	* Click a header to sort by that column: ascending, descending, then
	* off. Shift-click to keep the sort you already have and add this
	* column under it — the same idiom spreadsheets use, and the only way
	* to build "by role, newest first" out of two clicks.
	*
	* Shift cycles the column it lands on through the same three states, so
	* a key added by mistake is removed by shift-clicking it twice more,
	* without disturbing the keys around it.
	*/
	const onColumnSort = useCallback((key, additive = false) => {
		const current = sortIndex.get(key);
		const next = current === void 0 ? "asc" : current.direction === "asc" ? "desc" : void 0;
		const existing = sortBy ?? [];
		let newSortBy;
		if (!additive) newSortBy = next ? [[key, next]] : void 0;
		else if (next === void 0) newSortBy = existing.filter(([existingKey]) => existingKey !== key);
		else if (current === void 0) newSortBy = [...existing, [key, next]];
		else newSortBy = existing.map((entry) => entry[0] === key ? [key, next] : entry);
		if (newSortBy?.length === 0) newSortBy = void 0;
		const filter = filterRef.current;
		if (filter) {
			if (checkFilterCombination && !checkFilterCombination(filter, newSortBy)) {
				if (onFilterUpdate) onFilterUpdate(void 0);
			}
		}
		if (onResetPagination) onResetPagination();
		if (onSortByUpdate) onSortByUpdate(newSortBy);
		scrollToTop();
	}, [
		checkFilterCombination,
		onFilterUpdate,
		onResetPagination,
		onSortByUpdate,
		scrollToTop,
		sortBy,
		sortIndex
	]);
	const maxScroll = Math.max((data?.length ?? 0) * rowHeight - bounds.height, 0);
	const onEndReachedInternal = useCallback((scrollOffset) => {
		if (onEndReached && (data?.length ?? 0) > 0 && scrollOffset > endReachCallbackThreshold.current + endOffset) {
			endReachCallbackThreshold.current = scrollOffset;
			onEndReached();
		}
	}, [data?.length, onEndReached]);
	const onScroll = useCallback(({ scrollDirection, scrollOffset, scrollUpdateWasRequested }) => {
		if (onScrollProp) debouncedScroll({
			scrollDirection,
			scrollOffset,
			scrollUpdateWasRequested
		});
		if (!scrollUpdateWasRequested && scrollOffset >= maxScroll - endOffset) onEndReachedInternal(scrollOffset);
	}, [maxScroll, onEndReachedInternal]);
	const onFilterUpdateInternal = useCallback((column, filterForProperty) => {
		endReachCallbackThreshold.current = 0;
		const filter = filterRef.current;
		let newFilterValue = filter ? { ...filter } : {};
		if (!filterForProperty) delete newFilterValue[column.key];
		else newFilterValue[column.key] = filterForProperty;
		if (!(!checkFilterCombination || checkFilterCombination(newFilterValue, sortBy))) newFilterValue = filterForProperty ? { [column.key]: filterForProperty } : {};
		if (onFilterUpdate) onFilterUpdate(newFilterValue);
	}, [
		checkFilterCombination,
		onFilterUpdate,
		sortBy
	]);
	const empty = !loading && (data?.length ?? 0) === 0;
	const customView = error && (data?.length ?? 0) === 0 ? /* @__PURE__ */ jsxs(CenteredView, {
		maxWidth: "2xl",
		className: "flex flex-col gap-2",
		children: [/* @__PURE__ */ jsx(Typography, {
			variant: "h6",
			children: "Error"
		}), error?.message && /* @__PURE__ */ jsx(SafeLinkRenderer, { text: error.message })]
	}) : empty ? loading ? /* @__PURE__ */ jsx("div", {
		className: "flex items-center justify-center py-12 px-8",
		children: /* @__PURE__ */ jsx(CircularProgress, { size: "small" })
	}) : /* @__PURE__ */ jsx("div", {
		className: "flex flex-col overflow-auto items-center justify-center py-12 px-8",
		children: emptyComponent
	}) : void 0;
	const virtualListController = useMemo(() => ({
		data,
		rowHeight,
		headerHeight,
		cellRenderer,
		columns,
		onRowClick,
		customView,
		onColumnResize: onColumnResizeInternal,
		onColumnResizeEnd: onColumnResizeEndInternal,
		filter: filterRef.current,
		onColumnSort,
		onFilterUpdate: onFilterUpdateInternal,
		sortIndex,
		hoverRow: hoverRow ?? false,
		createFilterField,
		rowClassName,
		endAdornment,
		AddColumnComponent,
		onColumnsOrderChange: onColumnsOrderChange ? (newColumns) => {
			setColumns(newColumns);
			onColumnsOrderChange(newColumns);
		} : void 0,
		draggingColumnId,
		extraData
	}), [
		data,
		rowHeight,
		cellRenderer,
		columns,
		onRowClick,
		customView,
		onColumnResizeInternal,
		onColumnResizeEndInternal,
		filterInput,
		onColumnSort,
		onFilterUpdateInternal,
		sortIndex,
		hoverRow,
		createFilterField,
		rowClassName,
		endAdornment,
		AddColumnComponent,
		onColumnsOrderChange,
		draggingColumnId,
		extraData
	]);
	const sortableColumnKeys = columns.filter((col) => !col.frozen).map((col) => col.key);
	const tableContent = /* @__PURE__ */ jsx("div", {
		ref: measureRef,
		style,
		className: cls("h-full w-full", className, draggingColumnId && "overflow-hidden"),
		children: /* @__PURE__ */ jsx(VirtualListContext.Provider, {
			value: virtualListController,
			children: /* @__PURE__ */ jsx(MemoizedList, {
				outerRef: tableRef,
				width: bounds.width,
				height: bounds.height,
				itemCount: (data?.length ?? 0) + (endAdornment ? 1 : 0),
				onScroll: draggingColumnId ? void 0 : onScroll,
				includeAddColumn: Boolean(AddColumnComponent),
				itemSize: rowHeight
			}, rowHeight)
		})
	});
	if (onColumnsOrderChange) return /* @__PURE__ */ jsx(DndContext, {
		sensors,
		collisionDetection: closestCenter,
		onDragStart: handleDragStart,
		onDragEnd: handleDragEnd,
		onDragCancel: handleDragCancel,
		children: /* @__PURE__ */ jsx(SortableContext, {
			items: sortableColumnKeys,
			strategy: horizontalListSortingStrategy,
			children: tableContent
		})
	});
	return tableContent;
}, deepEqual);
var SortableCellWrapper = ({ columnKey, width, isDragging, isDraggable, frozen, children }) => {
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
		id: columnKey,
		disabled: !isDraggable || frozen
	});
	const { tabIndex: _tabIndex, ...attrsWithoutTabIndex } = attributes;
	return /* @__PURE__ */ jsx("div", {
		ref: setNodeRef,
		style: {
			transform: transform ? `translateX(${transform.x}px)` : void 0,
			transition: isDragging ? void 0 : transition,
			minWidth: width,
			maxWidth: width,
			width
		},
		className: cls("flex-shrink-0", frozen && "sticky left-0 z-10 bg-surface-card"),
		...attrsWithoutTabIndex,
		children
	});
};
function MemoizedList({ outerRef, width, height, itemCount, onScroll, itemSize, includeAddColumn }) {
	return /* @__PURE__ */ jsx(FixedSizeList, {
		outerRef,
		innerElementType,
		width,
		height,
		overscanCount: 4,
		itemCount,
		onScroll,
		itemSize,
		children: useCallback(({ index, style }) => {
			return /* @__PURE__ */ jsx(VirtualListContext.Consumer, { children: ({ onRowClick, data, columns, rowHeight = 54, headerHeight = 40, cellRenderer, hoverRow, rowClassName, endAdornment, draggingColumnId, onColumnsOrderChange, extraData }) => {
				if (endAdornment && index === (data ?? []).length) return /* @__PURE__ */ jsx("div", {
					style: {
						...style,
						height: "auto",
						position: "sticky",
						bottom: 0,
						zIndex: 1
					},
					children: endAdornment
				});
				const rowData = data ? data[index] : void 0;
				return /* @__PURE__ */ jsxs(VirtualTableRow, {
					rowData,
					rowIndex: index,
					onRowClick,
					columns,
					hoverRow,
					rowClassName,
					style: {
						...style,
						top: `calc(${style.top}px + ${headerHeight ?? 48}px)`
					},
					rowHeight,
					children: [columns.map((column, columnIndex) => {
						const cellData = rowData && rowData[column.key];
						const isDragging = draggingColumnId === column.key;
						const isDraggable = !column.frozen && !!onColumnsOrderChange;
						return onColumnsOrderChange ? /* @__PURE__ */ jsx(SortableCellWrapper, {
							columnKey: column.key,
							width: column.width,
							isDragging,
							isDraggable,
							frozen: column.frozen,
							children: /* @__PURE__ */ jsx(VirtualTableCell, {
								dataKey: column.key,
								cellRenderer,
								column,
								columns,
								rowData,
								cellData,
								rowIndex: index,
								columnIndex,
								extraData
							})
						}, `cell_wrapper_${column.key}`) : /* @__PURE__ */ jsx("div", {
							className: cls("flex-shrink-0 relative", column.frozen && "sticky left-0 z-10 bg-surface-card"),
							style: {
								minWidth: column.width,
								maxWidth: column.width,
								width: column.width
							},
							children: /* @__PURE__ */ jsx(VirtualTableCell, {
								dataKey: column.key,
								cellRenderer,
								column,
								columns,
								rowData,
								cellData,
								rowIndex: index,
								columnIndex,
								extraData
							})
						}, `cell_wrapper_${column.key}`);
					}), includeAddColumn && /* @__PURE__ */ jsx("div", { className: "w-20" })]
				}, `row_${index}`);
			} });
		}, [])
	});
}
var SafeLinkRenderer = ({ text }) => {
	const urlRegex = /https?:\/\/[^\s]+/g;
	const urls = text.match(urlRegex) || [];
	return /* @__PURE__ */ jsx("div", {
		className: "break-all",
		children: text.split(urlRegex).map((part, i) => /* @__PURE__ */ jsxs(React.Fragment, { children: [part, urls[i] && /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("a", {
			href: urls[i],
			className: "underline",
			target: "_blank",
			rel: "noopener noreferrer",
			children: "Link"
		}), /* @__PURE__ */ jsx("br", {})] })] }, i))
	});
};
//#endregion
//#region src/components/VirtualTable/selection/SelectionStore.ts
function createVirtualTableSelectionStore() {
	let selectedCell = void 0;
	const listeners = /* @__PURE__ */ new Set();
	function getEntity() {
		return selectedCell;
	}
	function subscribe(listener) {
		listeners.add(listener);
		return () => listeners.delete(listener);
	}
	function select(cell) {
		selectedCell = cell;
		listeners.forEach((l) => l());
	}
	return {
		getEntity,
		subscribe,
		select
	};
}
function useVirtualTableCellSelected(store, columnKey, id) {
	const selectorRef = useRef({
		columnKey,
		id
	});
	selectorRef.current = {
		columnKey,
		id
	};
	const getEntity = useCallback(() => {
		const cell = store.getEntity();
		if (!cell) return false;
		const s = selectorRef.current;
		return cell.columnKey === s.columnKey && cell.id === s.id;
	}, [store]);
	return useSyncExternalStore(store.subscribe, getEntity, getEntity);
}
//#endregion
//#region src/components/VirtualTable/selection/SelectionContext.tsx
var SelectionContext = createContext(null);
var VirtualTableSelectionProvider = ({ store, children }) => {
	const value = React.useMemo(() => ({
		selectionStore: store,
		select: store.select
	}), [store]);
	return /* @__PURE__ */ jsx(SelectionContext.Provider, {
		value,
		children
	});
};
var useVirtualTableSelection = () => {
	const context = useContext(SelectionContext);
	if (!context) throw new Error("useVirtualTableSelection must be used within a VirtualTableSelectionProvider");
	return context;
};
//#endregion
//#region src/components/VirtualTable/fields/VirtualTableInput.tsx
function VirtualTableInput(props) {
	const ref = React.useRef(null);
	const { disabled, value, multiline = false, updateValue, focused } = props;
	const prevValue = useRef(value);
	const [internalValue, setInternalValue] = useState(value);
	const focusedState = useRef(false);
	useEffect(() => {
		if (prevValue.current !== value && value !== internalValue) setInternalValue(value);
		prevValue.current = value;
	}, [value]);
	const doUpdate = React.useCallback(() => {
		if (!value && !internalValue) return;
		if (internalValue !== value && internalValue !== prevValue.current) {
			prevValue.current = internalValue;
			updateValue(internalValue);
		}
	}, [
		internalValue,
		updateValue,
		value
	]);
	useDebouncedCallback(internalValue, doUpdate, !focused, 400);
	useEffect(() => {
		if (ref.current && focused && !focusedState.current) {
			focusedState.current = true;
			ref.current.focus({ preventScroll: true });
			ref.current.selectionStart = ref.current.value.length;
			ref.current.selectionEnd = ref.current.value.length;
		} else focusedState.current = focused;
	}, [focused, ref]);
	return /* @__PURE__ */ jsx(TextareaAutosize, {
		className: focusedDisabled,
		ref,
		disabled,
		readOnly: disabled,
		style: {
			padding: 0,
			margin: 0,
			width: "100%",
			color: "unset",
			fontWeight: "unset",
			fontSize: "unset",
			fontFamily: "unset",
			background: "unset",
			border: "unset",
			resize: "none",
			outline: "none"
		},
		value: internalValue ?? "",
		onChange: (evt) => {
			const newValue = evt.target.value;
			if (multiline || !newValue.endsWith("\n")) setInternalValue(newValue);
		},
		onFocus: () => {
			focusedState.current = true;
		},
		onBlur: () => {
			focusedState.current = false;
			doUpdate();
			if (props.onBlur) props.onBlur();
		}
	});
}
//#endregion
//#region src/components/VirtualTable/fields/VirtualTableNumberInput.tsx
function VirtualTableNumberInput(props) {
	const { align = "left", value, updateValue, focused } = props;
	const propStringValue = value && typeof value === "number" ? value.toString() : "";
	const [internalValue, setInternalValue] = useState(propStringValue);
	const prevValue = useRef(value);
	useEffect(() => {
		if (prevValue.current !== value && String(value) !== internalValue) setInternalValue(value ? value.toString() : null);
		prevValue.current = value;
	}, [value]);
	useDebouncedCallback(internalValue, React.useCallback(() => {
		if (internalValue !== propStringValue) if (internalValue !== void 0 && internalValue !== null) {
			const numberValue = parseFloat(internalValue);
			if (isNaN(numberValue)) return;
			updateValue(numberValue);
		} else updateValue(null);
	}, [internalValue, value]), !focused, 400);
	useEffect(() => {
		if (!focused && propStringValue !== internalValue) setInternalValue(value !== void 0 && value !== null ? value.toString() : null);
	}, [value, focused]);
	const inputRef = React.useRef(null);
	useEffect(() => {
		if (inputRef.current && focused) inputRef.current.focus({ preventScroll: true });
	}, [focused, inputRef]);
	const regexp = /^-?[0-9]+[,.]?[0-9]*$/;
	return /* @__PURE__ */ jsx(TextField, {
		inputRef,
		invisible: true,
		size: "small",
		className: "w-full",
		inputClassName: cls("p-0 m-0 bg-transparent border-none outline-hidden font-normal leading-normal text-unset", focusedDisabled),
		inputStyle: { textAlign: align },
		value: internalValue ?? "",
		onChange: (evt) => {
			const newValue = evt.target.value.replace(",", ".");
			if (newValue.length === 0) setInternalValue(null);
			if (regexp.test(newValue) || newValue.startsWith("-")) setInternalValue(newValue);
		}
	});
}
//#endregion
//#region src/components/VirtualTable/fields/VirtualTableSwitch.tsx
function VirtualTableSwitch(props) {
	const { internalValue, updateValue, focused } = props;
	const ref = React.useRef(null);
	useEffect(() => {
		if (ref.current && focused) ref.current.focus({ preventScroll: true });
	}, [focused, ref]);
	return /* @__PURE__ */ jsx(BooleanSwitch, {
		ref,
		size: "small",
		value: Boolean(internalValue),
		onValueChange: updateValue
	});
}
//#endregion
//#region src/components/VirtualTable/fields/VirtualTableDateField.tsx
function VirtualTableDateField(props) {
	const { name, disabled, error, mode, timezone, internalValue, updateValue, small = false, locale } = props;
	return /* @__PURE__ */ jsx(DateTimeField, {
		value: internalValue ?? void 0,
		onChange: (dateValue) => updateValue(dateValue ?? null),
		disabled,
		error: Boolean(error),
		"aria-label": name,
		invisible: true,
		size: small ? "small" : "medium",
		inputClassName: cls("w-full h-full", focusedDisabled),
		className: cls("w-full h-full", focusedDisabled),
		mode,
		timezone,
		locale
	});
}
//#endregion
//#region src/components/VirtualTable/fields/VirtualTableSelect.tsx
function VirtualTableSelect(props) {
	const { options, value, disabled, small = false, focused, updateValue, multiple = false } = props;
	const validValue = Array.isArray(value) && multiple || !Array.isArray(value) && !multiple;
	const ref = useRef(null);
	useEffect(() => {
		if (ref.current && focused) ref.current?.focus({ preventScroll: true });
	}, [focused, ref]);
	const onChange = useCallback((updatedValue) => {
		if (multiple) {
			const isNumber = options.some((o) => typeof o.value === "number");
			const newValue = updatedValue.map((v) => {
				return typeof options.find((o) => String(o.value) === v)?.value === "number" ? parseFloat(v) : v;
			});
			if (isNumber) updateValue(newValue);
			else updateValue(newValue);
		} else {
			const opt = options.find((o) => String(o.value) === updatedValue);
			updateValue(typeof opt?.value === "number" ? parseFloat(updatedValue) : updatedValue || null);
		}
	}, [
		multiple,
		updateValue,
		options
	]);
	const renderValue = (val) => {
		const option = options.find((o) => o.value === val);
		return /* @__PURE__ */ jsx(Chip, {
			size: small ? "small" : "medium",
			children: option ? option.label : String(val)
		});
	};
	const handleOpenChange = useCallback((open) => {
		if (!open && ref.current) setTimeout(() => {
			ref.current?.focus({ preventScroll: true });
		}, 0);
	}, []);
	return multiple ? /* @__PURE__ */ jsx(MultiSelect, {
		inputRef: ref,
		className: "w-full h-full p-0 bg-transparent outline-none",
		position: "item-aligned",
		disabled,
		includeClear: false,
		useChips: false,
		value: validValue ? value.map((v) => String(v)) : [],
		onValueChange: onChange,
		onOpenChange: handleOpenChange,
		children: options.map((opt) => /* @__PURE__ */ jsx(MultiSelectItem, {
			value: String(opt.value),
			children: /* @__PURE__ */ jsx(Chip, {
				size: small ? "small" : "medium",
				children: opt.label
			})
		}, String(opt.value)))
	}) : /* @__PURE__ */ jsx(Select, {
		inputRef: ref,
		fullWidth: true,
		className: "w-full h-full p-0 bg-transparent outline-none [&_button]:ring-0 [&_button]:ring-offset-0",
		inputClassName: "ring-0 ring-offset-0 focus:ring-0 focus-visible:ring-0 outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-offset-0",
		position: "item-aligned",
		disabled,
		padding: false,
		value: validValue ? String(value) : "",
		onValueChange: onChange,
		onOpenChange: handleOpenChange,
		renderValue,
		children: options.map((opt) => /* @__PURE__ */ jsx(SelectItem, {
			value: String(opt.value),
			children: /* @__PURE__ */ jsx(Chip, {
				size: small ? "small" : "medium",
				children: opt.label
			})
		}, String(opt.value)))
	});
}
//#endregion
//#region src/views/ListView.tsx
function getEstimatedRowHeight(size) {
	switch (size) {
		case "xs": return 44;
		case "s": return 52;
		case "m": return 64;
		case "l": return 76;
		case "xl": return 88;
		default: return 64;
	}
}
var OVERSCAN_COUNT = 8;
var LOAD_MORE_THRESHOLD = 400;
function getScrollParent$1(element) {
	let parent = element?.parentElement ?? null;
	while (parent) {
		const style = getComputedStyle(parent);
		if (style.overflowY === "auto" || style.overflowY === "scroll" || style.overflow === "auto" || style.overflow === "scroll") return parent;
		parent = parent.parentElement;
	}
	return document.documentElement;
}
function ListView({ data, dataLoading = false, noMoreToLoad = false, dataLoadingError, itemCount, setItemCount, pageSize = 50, paginationEnabled = true, onItemClick, selectedIds, highlightedIds, selectionEnabled = true, onSelectionChange, emptyComponent, size = "m", selectedEntityId, header, renderRow }) {
	const containerRef = useRef(null);
	const rowsRef = useRef(null);
	const isLoadingMore = useRef(false);
	useEffect(() => {
		if (!dataLoading) isLoadingMore.current = false;
	}, [dataLoading]);
	const estimatedRowHeight = getEstimatedRowHeight(size);
	const [effectiveScrollTop, setEffectiveScrollTop] = useState(0);
	const [viewportHeight, setViewportHeight] = useState(800);
	const paginationStateRef = useRef({
		paginationEnabled,
		noMoreToLoad,
		itemCount,
		pageSize
	});
	useEffect(() => {
		paginationStateRef.current = {
			paginationEnabled,
			noMoreToLoad,
			itemCount,
			pageSize
		};
	}, [
		paginationEnabled,
		noMoreToLoad,
		itemCount,
		pageSize
	]);
	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const scrollEl = getScrollParent$1(el);
		if (!scrollEl) return;
		let rafId = null;
		const update = () => {
			rafId = null;
			const scrollRect = scrollEl.getBoundingClientRect();
			const listTopRelative = (rowsRef.current ?? el).getBoundingClientRect().top - scrollRect.top;
			setEffectiveScrollTop(Math.max(0, -listTopRelative));
			setViewportHeight(scrollRect.height);
			const { paginationEnabled: pe, noMoreToLoad: nm, itemCount: ic, pageSize: ps } = paginationStateRef.current;
			if (pe && !nm && !isLoadingMore.current && scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight < LOAD_MORE_THRESHOLD) {
				isLoadingMore.current = true;
				setItemCount?.((ic ?? ps) + ps);
			}
		};
		const onScroll = () => {
			if (rafId === null) rafId = requestAnimationFrame(update);
		};
		scrollEl.addEventListener("scroll", onScroll, { passive: true });
		const ro = new ResizeObserver(() => update());
		ro.observe(scrollEl);
		update();
		return () => {
			scrollEl.removeEventListener("scroll", onScroll);
			ro.disconnect();
			if (rafId !== null) cancelAnimationFrame(rafId);
		};
	}, [setItemCount]);
	const totalHeight = data.length * estimatedRowHeight;
	const startIndex = Math.max(0, Math.floor(effectiveScrollTop / estimatedRowHeight) - OVERSCAN_COUNT);
	const endIndex = Math.min(data.length, Math.ceil((effectiveScrollTop + viewportHeight) / estimatedRowHeight) + OVERSCAN_COUNT);
	const visibleData = data.slice(startIndex, endIndex);
	const offsetY = startIndex * estimatedRowHeight;
	const footerHeight = dataLoading ? 48 : !dataLoading && noMoreToLoad && data.length > 0 ? 32 : 0;
	const isInitialLoading = dataLoading && data.length === 0;
	const isEmpty = !dataLoading && data.length === 0 && !dataLoadingError;
	const getItemId = useCallback((item) => {
		if (item && typeof item === "object" && "id" in item) {
			const id = item.id;
			return typeof id === "number" ? id : String(id);
		}
		return String(item);
	}, []);
	return /* @__PURE__ */ jsx("div", {
		ref: containerRef,
		className: cls("w-full", selectedEntityId === void 0 && `rounded-lg overflow-hidden border border-hairline`),
		children: dataLoadingError && data.length === 0 ? /* @__PURE__ */ jsx("div", {
			className: "flex items-center justify-center p-8",
			children: /* @__PURE__ */ jsxs(Typography, {
				className: "text-red-500",
				children: ["Error loading data: ", dataLoadingError.message]
			})
		}) : isInitialLoading ? /* @__PURE__ */ jsx("div", {
			className: "flex items-center justify-center py-12 px-8",
			children: /* @__PURE__ */ jsx(CircularProgress, { size: "small" })
		}) : isEmpty ? /* @__PURE__ */ jsx("div", {
			className: "w-full flex items-center justify-center py-12 px-8",
			children: emptyComponent ?? /* @__PURE__ */ jsx(Typography, {
				variant: "label",
				color: "secondary",
				children: "No entries found"
			})
		}) : /* @__PURE__ */ jsxs(Fragment, { children: [header, /* @__PURE__ */ jsxs("div", {
			ref: rowsRef,
			className: "my-1.5",
			style: {
				height: totalHeight + footerHeight,
				position: "relative"
			},
			children: [
				/* @__PURE__ */ jsx("div", {
					style: {
						position: "absolute",
						top: offsetY,
						left: 0,
						right: 0
					},
					children: visibleData.map((item, i) => {
						const actualIndex = startIndex + i;
						const isLast = actualIndex === data.length - 1;
						const id = getItemId(item);
						const selected = selectedIds?.has(id) ?? false;
						const highlighted = highlightedIds?.has(id) ?? false;
						const handleClick = (e) => {
							if ((e.metaKey || e.ctrlKey) && selectionEnabled) {
								e.preventDefault();
								onSelectionChange?.(item, !selected);
								return;
							}
							onItemClick?.(item);
						};
						const handleSelectionChange = (val) => {
							onSelectionChange?.(item, val);
						};
						return /* @__PURE__ */ jsx(React.Fragment, { children: renderRow({
							item,
							index: actualIndex,
							style: {
								height: estimatedRowHeight,
								overflow: "hidden"
							},
							className: "py-0.5",
							selected,
							highlighted,
							isLast,
							onClick: handleClick,
							onSelectionChange: handleSelectionChange
						}) }, id || actualIndex);
					})
				}),
				dataLoading && /* @__PURE__ */ jsx("div", {
					className: "flex items-center justify-center py-3",
					style: {
						position: "absolute",
						top: totalHeight,
						left: 0,
						right: 0
					},
					children: /* @__PURE__ */ jsx(CircularProgress, { size: "small" })
				}),
				!dataLoading && noMoreToLoad && data.length > 0 && /* @__PURE__ */ jsx("div", {
					className: "flex items-center justify-center py-2",
					style: {
						position: "absolute",
						top: totalHeight,
						left: 0,
						right: 0
					},
					children: /* @__PURE__ */ jsxs(Typography, {
						variant: "caption",
						color: "secondary",
						children: [
							"All ",
							data.length,
							" entries loaded"
						]
					})
				})
			]
		})] })
	});
}
//#endregion
//#region src/views/CardView.tsx
function getGridColumnsClass(size) {
	switch (size) {
		case "xs": return "grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10";
		case "s": return "grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8";
		case "m": return "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6";
		case "l": return "grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4";
		case "xl": return "grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3";
		default: return "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6";
	}
}
function getScrollParent(element) {
	if (!element) return null;
	let parent = element.parentElement;
	while (parent) {
		const overflowY = window.getComputedStyle(parent).overflowY;
		if (overflowY === "auto" || overflowY === "scroll") return parent;
		parent = parent.parentElement;
	}
	return document.documentElement;
}
function CardView({ data, dataLoading = false, noMoreToLoad = false, dataLoadingError, itemCount, setItemCount, pageSize = 50, paginationEnabled = true, onItemClick, selectedIds, highlightedIds, selectionEnabled = true, onSelectionChange, onScroll, initialScroll, size = "m", renderCard, emptyComponent }) {
	const containerRef = useRef(null);
	const hasRestoredScroll = useRef(false);
	const isLoadingMore = useRef(false);
	const paginationStateRef = useRef({
		paginationEnabled,
		noMoreToLoad,
		itemCount,
		pageSize
	});
	useEffect(() => {
		paginationStateRef.current = {
			paginationEnabled,
			noMoreToLoad,
			itemCount,
			pageSize
		};
	}, [
		paginationEnabled,
		noMoreToLoad,
		itemCount,
		pageSize
	]);
	useEffect(() => {
		if (!dataLoading) isLoadingMore.current = false;
	}, [dataLoading]);
	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const scrollEl = getScrollParent(el);
		if (!scrollEl) return;
		let rafId = null;
		const update = () => {
			rafId = null;
			const { paginationEnabled: pe, noMoreToLoad: nm, itemCount: ic, pageSize: ps } = paginationStateRef.current;
			if (pe && !nm && !isLoadingMore.current && scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight < 400) {
				isLoadingMore.current = true;
				setItemCount?.((ic ?? ps) + ps);
			}
		};
		const onScrollEvent = () => {
			if (rafId === null) rafId = requestAnimationFrame(update);
		};
		scrollEl.addEventListener("scroll", onScrollEvent, { passive: true });
		const ro = new ResizeObserver(() => update());
		ro.observe(scrollEl);
		update();
		return () => {
			scrollEl.removeEventListener("scroll", onScrollEvent);
			ro.disconnect();
			if (rafId !== null) cancelAnimationFrame(rafId);
		};
	}, [setItemCount]);
	useEffect(() => {
		if (!containerRef.current || !initialScroll || hasRestoredScroll.current || data.length === 0) return;
		const scrollEl = getScrollParent(containerRef.current);
		if (!scrollEl) return;
		let attempts = 0;
		const maxAttempts = 5;
		const tryRestore = () => {
			if (scrollEl.scrollHeight >= initialScroll || attempts >= maxAttempts) {
				scrollEl.scrollTop = initialScroll;
				hasRestoredScroll.current = true;
			} else {
				attempts++;
				requestAnimationFrame(tryRestore);
			}
		};
		requestAnimationFrame(tryRestore);
	}, [initialScroll, data.length]);
	const lastScrollOffset = useRef(0);
	useEffect(() => {
		const el = containerRef.current;
		if (!el || !onScroll) return;
		const scrollEl = getScrollParent(el);
		if (!scrollEl) return;
		const handleScroll = () => {
			const currentOffset = scrollEl.scrollTop;
			const direction = currentOffset > lastScrollOffset.current ? "forward" : "backward";
			lastScrollOffset.current = currentOffset;
			onScroll({
				scrollDirection: direction,
				scrollOffset: currentOffset,
				scrollUpdateWasRequested: false
			});
		};
		scrollEl.addEventListener("scroll", handleScroll, { passive: true });
		return () => scrollEl.removeEventListener("scroll", handleScroll);
	}, [onScroll]);
	const getItemId = useCallback((item) => {
		if (item && typeof item === "object" && "id" in item) {
			const id = item.id;
			return typeof id === "number" ? id : String(id);
		}
		return String(item);
	}, []);
	const gridColumnsClass = getGridColumnsClass(size);
	const isInitialLoading = dataLoading && data.length === 0 && !dataLoadingError;
	const isEmpty = !dataLoading && data.length === 0 && !dataLoadingError;
	return /* @__PURE__ */ jsx("div", {
		ref: containerRef,
		className: "w-full p-4",
		children: dataLoadingError && data.length === 0 ? /* @__PURE__ */ jsx("div", {
			className: "h-full flex items-center justify-center p-8",
			children: /* @__PURE__ */ jsxs(Typography, {
				className: "text-red-500",
				children: ["Error loading data: ", dataLoadingError.message]
			})
		}) : isInitialLoading ? /* @__PURE__ */ jsx("div", {
			className: "flex items-center justify-center py-12 px-8",
			children: /* @__PURE__ */ jsx(CircularProgress, { size: "small" })
		}) : isEmpty ? /* @__PURE__ */ jsx("div", {
			className: "w-full flex items-center justify-center py-12 px-8",
			children: emptyComponent ?? /* @__PURE__ */ jsx(Typography, {
				variant: "label",
				color: "secondary",
				children: "No entries found"
			})
		}) : /* @__PURE__ */ jsxs("div", {
			className: "max-w-7xl mx-auto",
			children: [/* @__PURE__ */ jsx("div", {
				className: cls("grid gap-4", gridColumnsClass),
				children: data.map((item, index) => {
					const id = getItemId(item);
					const selected = selectedIds?.has(id) ?? false;
					const highlighted = highlightedIds?.has(id) ?? false;
					const handleClick = (e) => {
						if (e && (e.metaKey || e.ctrlKey) && selectionEnabled) {
							e.preventDefault();
							onSelectionChange?.(item, !selected);
							return;
						}
						onItemClick?.(item);
					};
					const handleSelectionChange = (val) => {
						onSelectionChange?.(item, val);
					};
					return /* @__PURE__ */ jsx(React.Fragment, { children: renderCard(item, {
						selected,
						highlighted,
						onSelectionChange: handleSelectionChange,
						onClick: handleClick
					}) }, id || index);
				})
			}), /* @__PURE__ */ jsxs("div", {
				className: "flex items-center justify-center py-8",
				children: [dataLoading && /* @__PURE__ */ jsx(CircularProgress, { size: "small" }), !dataLoading && noMoreToLoad && data.length > 0 && /* @__PURE__ */ jsxs(Typography, {
					variant: "caption",
					color: "secondary",
					children: [
						"All ",
						data.length,
						" entries loaded"
					]
				})]
			})]
		})
	});
}
//#endregion
//#region src/views/Kanban/BoardSortableList.tsx
function BoardSortableList({ columnId, items, ItemComponent, isDragging, isDragOverColumn, loading = false, hasMore = false, error, onLoadMore }) {
	const { setNodeRef } = useDroppable({
		id: columnId,
		data: { type: "ITEM-LIST" }
	});
	const sentinelRef = useRef(null);
	const isLoadingRef = useRef(loading);
	isLoadingRef.current = loading;
	const lastLoadTimeRef = useRef(0);
	useEffect(() => {
		if (!sentinelRef.current || !hasMore || !onLoadMore) return;
		const sentinel = sentinelRef.current;
		const observer = new IntersectionObserver((entries) => {
			const now = Date.now();
			if (entries[0].isIntersecting && hasMore && !isLoadingRef.current && now - lastLoadTimeRef.current > 500) {
				lastLoadTimeRef.current = now;
				onLoadMore();
			}
		}, { threshold: .1 });
		observer.observe(sentinel);
		const rect = sentinel.getBoundingClientRect();
		const containerRect = sentinel.parentElement?.getBoundingClientRect();
		if (containerRect && rect.top < containerRect.bottom && rect.bottom > containerRect.top) {
			const now = Date.now();
			if (hasMore && !isLoadingRef.current && now - lastLoadTimeRef.current > 500) {
				lastLoadTimeRef.current = now;
				onLoadMore();
			}
		}
		return () => observer.disconnect();
	}, [hasMore, onLoadMore]);
	return /* @__PURE__ */ jsx("div", {
		ref: setNodeRef,
		className: useMemo(() => cls("flex flex-col p-2 transition-opacity duration-100 transition-bg ease-linear w-full overflow-y-auto flex-1 min-h-0 rounded-md", isDragging && isDragOverColumn ? "bg-surface-active" : isDragging ? "bg-surface-field hover:bg-surface-hover" : "bg-surface-field"), [isDragging, isDragOverColumn]),
		style: { minHeight: 80 },
		children: /* @__PURE__ */ jsx(SortableContext, {
			items: items.map((i) => i.id),
			strategy: verticalListSortingStrategy,
			children: items.length === 0 && !loading ? /* @__PURE__ */ jsx("div", {
				className: "flex-1 flex items-center justify-center px-3 text-center",
				children: /* @__PURE__ */ jsx("span", {
					className: cls("text-xs", error ? "text-red-600 dark:text-red-400" : "text-surface-400 dark:text-surface-500"),
					children: error ? "Could not load this column" : "No items"
				})
			}) : /* @__PURE__ */ jsxs(Fragment, { children: [items.map((item, index) => /* @__PURE__ */ jsx(SortableItem, {
				item,
				index,
				columnId,
				ItemComponent
			}, item.id)), (loading || hasMore) && /* @__PURE__ */ jsx("div", {
				ref: sentinelRef,
				className: "flex items-center justify-center py-2 min-h-6",
				children: loading && /* @__PURE__ */ jsx(CircularProgress, { size: "smallest" })
			})] })
		})
	});
}
var SortableItem = memo(function SortableItem({ item, index, columnId, ItemComponent }) {
	const { setNodeRef, attributes, listeners, isDragging: isItemBeingDragged, transform, transition } = useSortable({
		id: item.id,
		data: {
			type: "ITEM",
			columnId
		}
	});
	return /* @__PURE__ */ jsx("div", {
		ref: setNodeRef,
		style: useMemo(() => ({
			transform: CSS.Transform.toString(transform),
			transition,
			zIndex: isItemBeingDragged ? 2 : 1,
			opacity: isItemBeingDragged ? 0 : 1
		}), [
			transform,
			transition,
			isItemBeingDragged
		]),
		...attributes,
		...listeners,
		children: /* @__PURE__ */ jsx(ItemComponent, {
			item,
			isDragging: isItemBeingDragged,
			index
		})
	});
});
//#endregion
//#region src/views/Kanban/BoardColumnTitle.tsx
function BoardColumnTitle({ children, className, color, ...props }) {
	const colorScheme = useMemo(() => {
		if (!color) return void 0;
		if (typeof color === "string") return getColorSchemeForKey(color);
		return color;
	}, [color]);
	return /* @__PURE__ */ jsxs(Typography, {
		variant: "subtitle2",
		component: "h4",
		className: cls("py-3 px-3 transition-colors duration-200 flex-grow select-none relative outline-none focus:outline focus:outline-2 focus:outline-offset-2 flex items-center gap-3", className),
		...props,
		children: [colorScheme && /* @__PURE__ */ jsx("div", {
			className: "w-3 h-3 rounded-full flex-shrink-0",
			style: { backgroundColor: colorScheme.darkColor ?? colorScheme.color }
		}), children]
	});
}
//#endregion
//#region src/views/Kanban/BoardColumn.tsx
var BoardColumn = memo(function BoardColumn({ id, title, items, ItemComponent, isDragging, isDragOverColumn, allowReorder = false, loading = false, hasMore = false, error, onLoadMore, onAddItem, totalCount, color, style }) {
	const { setNodeRef, attributes, listeners, isDragging: isColumnBeingDragged, transform, transition } = useSortable({
		id,
		data: { type: "COLUMN" },
		disabled: !allowReorder
	});
	const combinedStyle = useMemo(() => ({
		...style,
		transform: CSS.Translate.toString(transform),
		transition,
		zIndex: isColumnBeingDragged ? 2 : 1
	}), [
		style,
		transform,
		transition,
		isColumnBeingDragged
	]);
	const dragListeners = allowReorder ? listeners : {};
	const columnClassName = useMemo(() => cls("border h-full w-80 min-w-80 mx-2 flex flex-col rounded-md border-hairline", isColumnBeingDragged ? "ring-2 ring-primary" : ""), [isColumnBeingDragged]);
	const headerClassName = useMemo(() => cls("flex items-center justify-between px-2 rounded-t-md transition-colors duration-200 ease-in-out", isColumnBeingDragged ? "bg-surface-active" : "bg-surface-field hover:bg-surface-field-hover", allowReorder ? "cursor-grab" : ""), [isColumnBeingDragged, allowReorder]);
	const itemIds = useMemo(() => items.map((i) => i.id), [items]);
	return /* @__PURE__ */ jsxs("div", {
		ref: setNodeRef,
		style: combinedStyle,
		...attributes,
		className: columnClassName,
		children: [/* @__PURE__ */ jsxs("div", {
			...dragListeners,
			className: headerClassName,
			children: [/* @__PURE__ */ jsxs("div", {
				className: "flex items-center gap-2",
				children: [/* @__PURE__ */ jsx(BoardColumnTitle, {
					"aria-label": `${title} item list`,
					color,
					children: title
				}), totalCount !== void 0 && /* @__PURE__ */ jsx("span", {
					className: "text-xs text-surface-500 dark:text-surface-400",
					children: totalCount
				})]
			}), onAddItem && /* @__PURE__ */ jsx(IconButton, {
				size: "small",
				onClick: (e) => {
					e.stopPropagation();
					onAddItem();
				},
				className: "opacity-60 hover:opacity-100",
				children: /* @__PURE__ */ jsx(PlusIcon, { size: iconSize.small })
			})]
		}), /* @__PURE__ */ jsx(SortableContext, {
			items: itemIds,
			strategy: verticalListSortingStrategy,
			children: /* @__PURE__ */ jsx(BoardSortableList, {
				columnId: id,
				items,
				ItemComponent,
				isDragging,
				isDragOverColumn,
				loading,
				hasMore,
				error,
				onLoadMore
			})
		})]
	});
});
//#endregion
//#region src/views/Kanban/placement.ts
/**
* Where a dropped card ends up in its destination column.
*
* Pulled out of the drop handler because it is the part that was wrong twice:
* once appending a card that had been dropped into the middle of a column, and
* once refusing to place a card at all when it was released over an empty
* column rather than over another card. It is pure, so it can be checked
* without driving a pointer.
*/
function placeDroppedCard({ targetItems, movedItem, overId, changedColumn }) {
	const indexInTarget = targetItems.findIndex((i) => i.id === movedItem.id);
	const overIndex = targetItems.findIndex((i) => i.id === overId);
	if (indexInTarget === -1) {
		const placed = [...targetItems];
		placed.splice(overIndex === -1 ? targetItems.length : overIndex, 0, movedItem);
		return placed;
	}
	if (overIndex !== -1) return arrayMove(targetItems, indexInTarget, overIndex);
	if (changedColumn) return targetItems;
	return arrayMove(targetItems, indexInTarget, targetItems.length - 1);
}
//#endregion
//#region src/views/Kanban/Board.tsx
function Board({ data, columns: columnsProp, columnLabels, columnColors, className, assignColumn, allowColumnReorder = false, onColumnReorder, onItemsReorder, ItemComponent, columnLoadingState, onLoadMoreColumn, onAddItemToColumn, AddColumnComponent }) {
	const [activeItem, setActiveItem] = useState(null);
	const [isDragging, setIsDragging] = useState(false);
	const [dragOverColumnId, setDragOverColumnId] = useState(null);
	/**
	* Which column the card was picked up from.
	*
	* `handleDragOver` moves the card between columns in `itemMapState` while
	* the pointer is still down — that is what makes the gap open up under the
	* cursor. So by the time the drop is handled, looking the card up by id
	* finds it in its *destination*, and the board was reporting the move as
	* source === target: a cross-column drop looked like a plain reorder and
	* the column property was never written. The card snapped back on the next
	* fetch.
	*/
	const dragSourceColumnRef = useRef(null);
	const [itemMapState, setItemMapState] = useState(() => {
		const dataColumnMap = data.reduce((prev, item) => ({
			...prev,
			[item.id]: assignColumn(item)
		}), {});
		return columnsProp.reduce((previous, column) => ({
			...previous,
			[column]: data.filter((item) => dataColumnMap[item.id] === column)
		}), {});
	});
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
	useEffect(() => {
		if (isDragging) return;
		const dataColumnMap = data.reduce((prev, item) => ({
			...prev,
			[item.id]: assignColumn(item)
		}), {});
		const newItemMap = columnsProp.reduce((previous, column) => ({
			...previous,
			[column]: data.filter((item) => dataColumnMap[item.id] === column)
		}), {});
		setItemMapState((prevMap) => {
			let changed = false;
			for (const col of columnsProp) {
				const prevItems = prevMap[col] ?? [];
				const newItems = newItemMap[col] ?? [];
				if (prevItems.length !== newItems.length) {
					changed = true;
					continue;
				}
				for (let i = 0; i < prevItems.length; i++) if (prevItems[i].id !== newItems[i].id || prevItems[i].data !== newItems[i].data) {
					changed = true;
					break;
				}
			}
			if (!changed) return prevMap;
			const updated = {};
			for (const col of columnsProp) {
				const prevItems = prevMap[col] ?? [];
				const newItems = newItemMap[col] ?? [];
				const prevById = /* @__PURE__ */ new Map();
				for (const item of prevItems) prevById.set(item.id, item);
				updated[col] = newItems.map((newItem) => {
					const prev = prevById.get(newItem.id);
					if (prev && prev.data === newItem.data) return prev;
					return newItem;
				});
			}
			return updated;
		});
	}, [
		data,
		columnsProp,
		assignColumn
	]);
	const findColumnByItemId = (id) => {
		return Object.keys(itemMapState).find((col) => itemMapState[col]?.some((i) => i.id === id));
	};
	const handleDragStart = (event) => {
		setIsDragging(true);
		setDragOverColumnId(null);
		const { active } = event;
		if (active.data.current?.type === "ITEM") {
			const columnId = findColumnByItemId(active.id);
			dragSourceColumnRef.current = columnId ?? null;
			if (columnId) {
				const item = itemMapState[columnId]?.find((i) => i.id === active.id);
				setActiveItem(item || null);
			}
		}
	};
	const handleDragOver = (event) => {
		const { active, over } = event;
		if (!over) {
			setDragOverColumnId(null);
			return;
		}
		let currentHoveredColumnId = null;
		const overId = over.id;
		const overDataType = over.data.current?.type;
		if (overDataType === "ITEM-LIST" || overDataType === "COLUMN") currentHoveredColumnId = overId;
		else if (overDataType === "ITEM") currentHoveredColumnId = findColumnByItemId(overId) || null;
		else if (columnsProp.includes(overId)) currentHoveredColumnId = overId;
		setDragOverColumnId(currentHoveredColumnId);
		if (active.data.current?.type !== "ITEM") return;
		const activeId = active.id;
		const activeItemColumn = findColumnByItemId(activeId);
		let overColumnForMove = findColumnByItemId(overId);
		if (!overColumnForMove && overDataType === "ITEM-LIST") overColumnForMove = overId;
		if (!overColumnForMove && columnsProp.includes(overId)) overColumnForMove = overId;
		if (!activeItemColumn || !overColumnForMove) return;
		if (activeItemColumn === overColumnForMove) return;
		if (itemMapState[overColumnForMove]?.some((i) => i.id === activeId)) return;
		setItemMapState((currentMap) => {
			const activeItems = [...currentMap[activeItemColumn] || []];
			const overItems = [...currentMap[overColumnForMove] || []];
			const activeIndex = activeItems.findIndex((i) => i.id === activeId);
			if (activeIndex === -1) return currentMap;
			let overIndex;
			if (overDataType === "ITEM-LIST" || columnsProp.includes(overId) && !findColumnByItemId(overId)) overIndex = overItems.length;
			else {
				overIndex = overItems.findIndex((i) => i.id === overId);
				if (overIndex !== -1) {
					const modifier = (active.rect.current.translated?.top ?? 0) + (active.rect.current.translated?.height ?? 0) / 2 > (over?.rect.top ?? 0) + (over?.rect.height ?? 0) / 2 ? 1 : 0;
					overIndex = overIndex >= 0 ? overIndex + modifier : overItems.length;
				} else overIndex = overItems.length;
			}
			const newItemMap = { ...currentMap };
			const [moved] = activeItems.splice(activeIndex, 1);
			overItems.splice(overIndex, 0, moved);
			newItemMap[activeItemColumn] = activeItems;
			newItemMap[overColumnForMove] = overItems;
			return newItemMap;
		});
	};
	const handleDragCancel = () => {
		setIsDragging(false);
		setActiveItem(null);
		setDragOverColumnId(null);
		dragSourceColumnRef.current = null;
	};
	const handleDragEnd = (event) => {
		const { active, over } = event;
		const sourceColumn = dragSourceColumnRef.current;
		setIsDragging(false);
		setActiveItem(null);
		setDragOverColumnId(null);
		dragSourceColumnRef.current = null;
		if (!over) return;
		const activeId = active.id;
		const overId = over.id;
		if (active.data.current?.type === "COLUMN") {
			if (activeId !== overId) {
				const oldIndex = columnsProp.indexOf(activeId);
				const newIndex = columnsProp.indexOf(overId);
				if (oldIndex !== -1 && newIndex !== -1) {
					const reordered = arrayMove(columnsProp, oldIndex, newIndex);
					onColumnReorder?.(reordered);
				}
			}
			return;
		}
		const currentCol = findColumnByItemId(activeId);
		let targetCol = findColumnByItemId(overId);
		if (!targetCol) {
			if (over.data.current?.type === "ITEM-LIST" || columnsProp.includes(overId)) targetCol = overId;
		}
		targetCol = targetCol ?? currentCol;
		if (!currentCol || !targetCol) return;
		const targetItems = itemMapState[targetCol] || [];
		const movedItem = (itemMapState[currentCol] || []).find((i) => i.id === activeId);
		if (!movedItem) return;
		const finalTargetItems = placeDroppedCard({
			targetItems,
			movedItem,
			overId,
			changedColumn: (sourceColumn ?? currentCol) !== targetCol
		});
		onItemsReorder?.(finalTargetItems, {
			itemId: activeId,
			sourceColumn: sourceColumn ?? currentCol,
			targetColumn: targetCol
		});
	};
	return /* @__PURE__ */ jsxs(DndContext, {
		sensors,
		collisionDetection: closestCorners,
		onDragStart: handleDragStart,
		onDragOver: handleDragOver,
		onDragEnd: handleDragEnd,
		onDragCancel: handleDragCancel,
		children: [/* @__PURE__ */ jsxs("div", {
			className: cls("flex flex-row h-full w-full overflow-x-auto p-4 select-none items-start", className),
			children: [/* @__PURE__ */ jsx(SortableContext, {
				items: columnsProp,
				children: columnsProp.map((col, index) => {
					const colItems = itemMapState[col] || [];
					const loadingState = columnLoadingState?.[col];
					return /* @__PURE__ */ jsx(BoardColumn, {
						id: String(col),
						title: columnLabels?.[col] ?? String(col),
						items: colItems,
						index,
						ItemComponent,
						isDragging,
						isDragOverColumn: dragOverColumnId === String(col),
						allowReorder: allowColumnReorder,
						loading: loadingState?.loading,
						hasMore: loadingState?.hasMore,
						error: loadingState?.error,
						totalCount: loadingState?.totalCount,
						color: columnColors?.[col],
						onLoadMore: onLoadMoreColumn ? () => onLoadMoreColumn(col) : void 0,
						onAddItem: onAddItemToColumn ? () => onAddItemToColumn(col) : void 0
					}, String(col));
				})
			}), AddColumnComponent]
		}), typeof document !== "undefined" && createPortal(/* @__PURE__ */ jsx(DragOverlay, {
			dropAnimation: null,
			zIndex: 9999,
			children: activeItem && /* @__PURE__ */ jsx(ItemComponent, {
				item: activeItem,
				isDragging: true,
				isClone: true
			})
		}), document.body)]
	});
}
//#endregion
//#region src/views/KanbanView.tsx
function KanbanView(props) {
	return /* @__PURE__ */ jsx(Board, { ...props });
}
//#endregion
//#region src/views/CollectionView/CollectionViewToolbar.tsx
var VIEW_MODE_ICONS = {
	list: /* @__PURE__ */ jsx(LayoutList, { size: iconSize.small }),
	table: /* @__PURE__ */ jsx(Table2, { size: iconSize.small }),
	cards: /* @__PURE__ */ jsx(LayoutGrid, { size: iconSize.small }),
	kanban: /* @__PURE__ */ jsx(Kanban, { size: iconSize.small })
};
var VIEW_MODE_LABELS = {
	list: "List",
	table: "Table",
	cards: "Cards",
	kanban: "Board"
};
var SIZE_LABELS = {
	xs: "Extra Small",
	s: "Small",
	m: "Medium",
	l: "Large",
	xl: "Extra Large"
};
/**
* Toolbar for the headless CollectionView.
*
* Renders a search bar, view mode toggle (popover with toggle button group),
* optional size selector, optional kanban property selector, and action slots.
*
* @group Collection View
*/
function CollectionViewToolbar({ viewMode, onViewModeChange, enabledViews = [
	"list",
	"table",
	"cards",
	"kanban"
], customViews, size = "m", onSizeChange, searchString, onSearchChange, loading, actionsStart, actionsEnd, compact, kanbanPropertyOptions, selectedKanbanProperty, onKanbanPropertyChange }) {
	const [popoverOpen, setPopoverOpen] = useState(false);
	const viewOptions = enabledViews.map((mode) => {
		const custom = customViews?.find((entry) => entry.key === mode);
		return {
			value: mode,
			label: VIEW_MODE_LABELS[mode] ?? custom?.name ?? mode,
			icon: VIEW_MODE_ICONS[mode] ?? custom?.icon ?? /* @__PURE__ */ jsx(LayoutList, { size: iconSize.small })
		};
	});
	const handleViewModeChange = useCallback((mode) => {
		onViewModeChange?.(mode);
	}, [onViewModeChange]);
	const handleSizeChange = useCallback((value) => {
		onSizeChange?.(value);
	}, [onSizeChange]);
	const handleKanbanPropertyChange = useCallback((value) => {
		onKanbanPropertyChange?.(value);
	}, [onKanbanPropertyChange]);
	const showViewToggle = onViewModeChange && enabledViews.length > 1;
	const showSizeSelector = onSizeChange && viewMode !== "kanban";
	const showKanbanSelector = kanbanPropertyOptions && kanbanPropertyOptions.length > 0 && viewMode === "kanban" && onKanbanPropertyChange;
	const showPopover = showViewToggle || showSizeSelector || showKanbanSelector;
	return /* @__PURE__ */ jsxs("div", {
		className: cls("flex items-center gap-2 px-2 flex-shrink-0", "border-b border-hairline", compact ? "py-0.5 gap-1" : "py-1"),
		children: [
			actionsStart,
			onSearchChange && /* @__PURE__ */ jsx(SearchBar, {
				onTextSearch: onSearchChange,
				size: "small",
				initialValue: searchString,
				loading,
				expandable: true
			}),
			/* @__PURE__ */ jsx("div", { className: "flex-1" }),
			actionsEnd,
			showPopover && /* @__PURE__ */ jsx(Popover, {
				open: popoverOpen,
				onOpenChange: setPopoverOpen,
				side: "bottom",
				align: "end",
				trigger: /* @__PURE__ */ jsx(IconButton, {
					size: "small",
					onClick: () => setPopoverOpen((prev) => !prev),
					children: /* @__PURE__ */ jsx(Settings2, { size: iconSize.small })
				}),
				children: /* @__PURE__ */ jsxs("div", {
					className: "p-3 min-w-[200px] flex flex-col gap-3",
					children: [
						showViewToggle && /* @__PURE__ */ jsxs("div", {
							className: "flex flex-col gap-1",
							children: [/* @__PURE__ */ jsx(Typography, {
								variant: "caption",
								color: "secondary",
								className: "font-medium uppercase tracking-wider",
								children: "View"
							}), /* @__PURE__ */ jsx(ToggleButtonGroup, {
								value: viewMode,
								onValueChange: handleViewModeChange,
								options: viewOptions
							})]
						}),
						showSizeSelector && /* @__PURE__ */ jsxs("div", {
							className: "flex flex-col gap-1",
							children: [/* @__PURE__ */ jsx(Typography, {
								variant: "caption",
								color: "secondary",
								className: "font-medium uppercase tracking-wider",
								children: "Size"
							}), /* @__PURE__ */ jsx(Select, {
								value: size,
								onValueChange: handleSizeChange,
								size: "small",
								children: Object.keys(SIZE_LABELS).map((sizeKey) => /* @__PURE__ */ jsx(SelectItem, {
									value: sizeKey,
									children: SIZE_LABELS[sizeKey]
								}, sizeKey))
							})]
						}),
						showKanbanSelector && /* @__PURE__ */ jsxs("div", {
							className: "flex flex-col gap-1",
							children: [/* @__PURE__ */ jsx(Typography, {
								variant: "caption",
								color: "secondary",
								className: "font-medium uppercase tracking-wider",
								children: "Group by"
							}), /* @__PURE__ */ jsx(Select, {
								value: selectedKanbanProperty,
								onValueChange: handleKanbanPropertyChange,
								size: "small",
								children: kanbanPropertyOptions.map((option) => /* @__PURE__ */ jsx(SelectItem, {
									value: option.key,
									children: option.label
								}, option.key))
							})]
						})
					]
				})
			})
		]
	});
}
//#endregion
//#region src/views/CollectionView/DefaultCellRenderer.tsx
/**
* Resolve an enum key to its display label and optional color.
*/
function resolveEnumLabel(enumValues, value) {
	if (!enumValues) return void 0;
	const key = String(value);
	if (enumValues instanceof Map) {
		const config = enumValues.get(value);
		if (!config) return void 0;
		if (typeof config === "string") return { label: config };
		return {
			label: config.label,
			color: config.color
		};
	}
	const config = enumValues[key];
	if (!config) return void 0;
	if (typeof config === "string") return { label: config };
	return {
		label: config.label,
		color: config.color
	};
}
/**
* Format a date value using Intl.DateTimeFormat.
*/
function formatDate(value, includeTime) {
	if (value == null) return "—";
	const date = value instanceof Date ? value : new Date(String(value));
	if (isNaN(date.getTime())) return String(value);
	const options = includeTime ? {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit"
	} : {
		year: "numeric",
		month: "short",
		day: "numeric"
	};
	return new Intl.DateTimeFormat(void 0, options).format(date);
}
/**
* Type guard for geopoint-like objects.
*/
function isGeoPoint(value) {
	if (typeof value !== "object" || value === null) return false;
	const obj = value;
	return typeof obj.lat === "number" && typeof obj.lng === "number";
}
/**
* Default cell renderer for the headless CollectionView.
* Handles all built-in property types without depending on any Rebase core types.
*
* @group Preview components
*/
function DefaultCellRenderer({ row, propertyKey, property, value, size, width, height }) {
	if (value === null || value === void 0) return /* @__PURE__ */ jsx(Typography, {
		variant: "body2",
		color: "secondary",
		className: "px-1",
		children: "—"
	});
	if (property.Preview) {
		const PreviewComponent = property.Preview;
		return /* @__PURE__ */ jsx(PreviewComponent, {
			row,
			propertyKey,
			property,
			value,
			size,
			width,
			height
		});
	}
	switch (property.type) {
		case "string": return renderString(property, value);
		case "number": return renderNumber(property, value);
		case "boolean": return renderBoolean(value);
		case "date": return renderDate(property, value);
		case "array": return renderArray(property, value);
		case "map": return renderMap(value);
		case "reference":
		case "relation": return renderReference(value);
		case "geopoint": return renderGeopoint(value);
		default: return /* @__PURE__ */ jsx(Typography, {
			variant: "body2",
			color: "secondary",
			noWrap: true,
			className: "px-1",
			children: JSON.stringify(value)
		});
	}
}
function renderString(property, value) {
	const strValue = String(value);
	if (property.enum) {
		const resolved = resolveEnumLabel(property.enum, value);
		return /* @__PURE__ */ jsx(Chip, {
			size: "small",
			colorScheme: resolved?.color ? {
				color: resolved.color,
				text: "#fff"
			} : void 0,
			children: resolved?.label ?? strValue
		});
	}
	if (property.previewAsTag) return /* @__PURE__ */ jsx(Chip, {
		size: "small",
		children: strValue
	});
	if (property.url) return /* @__PURE__ */ jsx(Typography, {
		variant: "body2",
		noWrap: true,
		component: "a",
		href: typeof property.url === "string" ? property.url : strValue,
		target: "_blank",
		rel: "noopener noreferrer",
		className: cls("px-1 text-primary hover:underline cursor-pointer"),
		children: strValue
	});
	if (property.email) return /* @__PURE__ */ jsx(Typography, {
		variant: "body2",
		noWrap: true,
		component: "a",
		href: `mailto:${strValue}`,
		className: cls("px-1 text-primary hover:underline cursor-pointer"),
		children: strValue
	});
	if (property.markdown) return /* @__PURE__ */ jsx("div", {
		className: "px-1 max-h-[100px] overflow-hidden",
		children: /* @__PURE__ */ jsx(Markdown, {
			source: strValue,
			size: "small"
		})
	});
	if (property.storage) return /* @__PURE__ */ jsxs("div", {
		className: cls("px-1 flex items-center justify-center", "w-[40px] h-[40px] rounded overflow-hidden"),
		children: [strValue ? /* @__PURE__ */ jsx("img", {
			src: strValue,
			alt: "",
			className: "w-full h-full object-cover",
			onError: (e) => {
				const target = e.currentTarget;
				target.style.display = "none";
				const fallback = target.nextElementSibling;
				if (fallback instanceof HTMLElement) fallback.style.display = "flex";
			}
		}) : null, /* @__PURE__ */ jsx("div", {
			className: cls("w-full h-full items-center justify-center rounded", "bg-surface-raised text-text-secondary dark:text-text-secondary-dark", strValue ? "hidden" : "flex"),
			children: /* @__PURE__ */ jsx(ImageIcon$1, { size: iconSize.small })
		})]
	});
	return /* @__PURE__ */ jsx(Tooltip, {
		title: strValue.length > 50 ? strValue : void 0,
		side: "bottom",
		children: /* @__PURE__ */ jsx(Typography, {
			variant: "body2",
			noWrap: true,
			className: "px-1",
			children: strValue
		})
	});
}
function renderNumber(property, value) {
	const numStr = String(value);
	if (property.enum) {
		const resolved = resolveEnumLabel(property.enum, value);
		return /* @__PURE__ */ jsx(Chip, {
			size: "small",
			colorScheme: resolved?.color ? {
				color: resolved.color,
				text: "#fff"
			} : void 0,
			children: resolved?.label ?? numStr
		});
	}
	return /* @__PURE__ */ jsx(Typography, {
		variant: "body2",
		noWrap: true,
		align: "right",
		className: "px-1 tabular-nums",
		children: numStr
	});
}
function renderBoolean(value) {
	return /* @__PURE__ */ jsx("div", {
		className: "px-1 flex items-center",
		children: /* @__PURE__ */ jsx(BooleanSwitch, {
			value: !!value,
			size: "small",
			disabled: true
		})
	});
}
function renderDate(property, value) {
	return /* @__PURE__ */ jsx(Typography, {
		variant: "body2",
		noWrap: true,
		className: "px-1 tabular-nums",
		children: formatDate(value, property.mode === "date_time")
	});
}
function renderArray(property, value) {
	if (!Array.isArray(value)) return /* @__PURE__ */ jsx(Typography, {
		variant: "body2",
		color: "secondary",
		className: "px-1",
		children: "—"
	});
	if (property.of?.enum && value.length > 0) return /* @__PURE__ */ jsx("div", {
		className: "px-1 flex flex-wrap gap-1 overflow-hidden",
		children: value.map((item, i) => {
			const resolved = resolveEnumLabel(property.of.enum, item);
			return /* @__PURE__ */ jsx(Chip, {
				size: "smallest",
				colorScheme: resolved?.color ? {
					color: resolved.color,
					text: "#fff"
				} : void 0,
				children: resolved?.label ?? String(item)
			}, i);
		})
	});
	if (value.length > 0 && value.every((item) => typeof item === "string")) return /* @__PURE__ */ jsx(Typography, {
		variant: "body2",
		noWrap: true,
		className: "px-1",
		children: value.join(", ")
	});
	return /* @__PURE__ */ jsxs(Typography, {
		variant: "body2",
		color: "secondary",
		className: "px-1",
		children: [
			value.length,
			" ",
			value.length === 1 ? "item" : "items"
		]
	});
}
function renderMap(value) {
	if (typeof value !== "object" || value === null) return /* @__PURE__ */ jsx(Typography, {
		variant: "body2",
		color: "secondary",
		className: "px-1",
		children: "—"
	});
	const fieldCount = Object.keys(value).length;
	return /* @__PURE__ */ jsxs(Typography, {
		variant: "body2",
		color: "secondary",
		className: "px-1",
		children: [
			fieldCount,
			" ",
			fieldCount === 1 ? "field" : "fields"
		]
	});
}
function renderReference(value) {
	return /* @__PURE__ */ jsx(Typography, {
		variant: "body2",
		noWrap: true,
		className: "px-1",
		children: String(value)
	});
}
function renderGeopoint(value) {
	if (isGeoPoint(value)) return /* @__PURE__ */ jsxs(Typography, {
		variant: "body2",
		noWrap: true,
		className: "px-1 tabular-nums",
		children: [
			value.lat.toFixed(6),
			", ",
			value.lng.toFixed(6)
		]
	});
	return /* @__PURE__ */ jsx(Typography, {
		variant: "body2",
		color: "secondary",
		className: "px-1",
		children: String(value)
	});
}
//#endregion
//#region src/views/CollectionView/utils.ts
/**
* Traverse an object by dot-separated path.
* Returns `undefined` if any segment is missing.
*/
function getValueInPath(obj, path) {
	if (!obj) return void 0;
	const parts = path.split(".");
	let current = obj;
	for (const part of parts) {
		if (current === null || current === void 0 || typeof current !== "object") return void 0;
		current = current[part];
	}
	return current;
}
//#endregion
//#region src/views/CollectionView/CollectionTableView.tsx
var DEFAULT_COLUMN_WIDTHS = {
	string: 200,
	number: 140,
	date: 160,
	boolean: 120,
	array: 200,
	map: 200,
	reference: 200,
	relation: 200,
	geopoint: 180
};
var FALLBACK_COLUMN_WIDTH = 200;
var SIZE_TO_ROW_HEIGHT = {
	xs: 36,
	s: 44,
	m: 52,
	l: 64,
	xl: 80
};
var SORTABLE_TYPES = /* @__PURE__ */ new Set([
	"string",
	"number",
	"date"
]);
var VIEW_SIZE_TO_RENDERER_SIZE = {
	xs: "tiny",
	s: "small",
	m: "medium",
	l: "large",
	xl: "large"
};
/**
* Headless table view that wraps {@link VirtualTable}.
*
* Converts property configurations into virtual table columns and wires up
* sorting, pagination, and cell rendering without coupling to any data layer.
*
* @group Collection View
*/
function CollectionTableView({ dataController, properties, propertiesOrder, displayedColumnIds, onRowClick, cellRenderer, size = "m", emptyComponent, hoverRow = true, className, onColumnResize, onColumnsOrderChange }) {
	const columns = useMemo(() => {
		const order = propertiesOrder ?? Object.keys(properties);
		return (displayedColumnIds ? order.filter((key) => displayedColumnIds.includes(key)) : order.filter((key) => {
			const prop = properties[key];
			return prop && !prop.hideFromCollection;
		})).map((key) => {
			const prop = properties[key];
			if (!prop) return null;
			return {
				key,
				width: prop.columnWidth || DEFAULT_COLUMN_WIDTHS[prop.type] || FALLBACK_COLUMN_WIDTH,
				title: prop.name,
				sortable: SORTABLE_TYPES.has(prop.type),
				resizable: true
			};
		}).filter((col) => col !== null);
	}, [
		properties,
		propertiesOrder,
		displayedColumnIds
	]);
	const rowHeight = SIZE_TO_ROW_HEIGHT[size];
	const rendererSize = VIEW_SIZE_TO_RENDERER_SIZE[size];
	const tableCellRenderer = useCallback((params) => {
		if (!params.rowData) return null;
		const propertyConfig = properties[params.column.key];
		if (!propertyConfig) return null;
		const value = getValueInPath(params.rowData, params.column.key);
		const cellProps = {
			row: params.rowData,
			propertyKey: params.column.key,
			property: propertyConfig,
			value,
			size: rendererSize,
			width: params.width
		};
		if (cellRenderer) {
			const custom = cellRenderer(cellProps);
			if (custom !== void 0) return /* @__PURE__ */ jsx("div", {
				className: "flex items-center h-full px-1 overflow-hidden",
				children: custom
			});
		}
		return /* @__PURE__ */ jsx("div", {
			className: "flex items-center h-full px-1 overflow-hidden",
			children: /* @__PURE__ */ jsx(DefaultCellRenderer, { ...cellProps })
		});
	}, [
		properties,
		cellRenderer,
		rendererSize
	]);
	const handleRowClick = useCallback(({ rowData }) => {
		onRowClick?.(rowData);
	}, [onRowClick]);
	const handleEndReached = useCallback(() => {
		if (dataController.paginationEnabled && !dataController.noMoreToLoad) {
			const currentCount = dataController.itemCount ?? dataController.pageSize ?? 50;
			const pageSize = dataController.pageSize ?? 50;
			dataController.setItemCount?.(currentCount + pageSize);
		}
	}, [
		dataController.paginationEnabled,
		dataController.noMoreToLoad,
		dataController.itemCount,
		dataController.pageSize,
		dataController.setItemCount
	]);
	const handleColumnResize = useCallback((params) => {
		onColumnResize?.({
			key: params.key,
			width: params.width
		});
	}, [onColumnResize]);
	const handleColumnsOrderChange = useCallback((newColumns) => {
		onColumnsOrderChange?.(newColumns.map((col) => col.key));
	}, [onColumnsOrderChange]);
	return /* @__PURE__ */ jsx(VirtualTable, {
		data: dataController.data,
		columns,
		cellRenderer: tableCellRenderer,
		rowHeight,
		sortBy: dataController.sortBy,
		onSortByUpdate: dataController.setSortBy,
		onRowClick: handleRowClick,
		onEndReached: handleEndReached,
		onColumnResize: handleColumnResize,
		onColumnsOrderChange: handleColumnsOrderChange,
		loading: dataController.loading,
		emptyComponent,
		error: dataController.error,
		hoverRow,
		className: cls("h-full", className),
		onScroll: dataController.onScroll,
		initialScroll: dataController.initialScroll
	});
}
//#endregion
//#region src/views/CollectionView/resolveTitleProperty.ts
/**
* Pick the property that acts as a row's title.
*
* Headless mirror of the ranking in `@rebasepro/common`: identifiers are kept
* out of the title slot structurally (the id property, images, hidden fields),
* and among what is left a plain text field beats a description, an enum or a
* relation. Falls back to the id property only when a collection has nothing
* else to show.
*/
function resolveTitlePropertyKey(order, properties, idProperty, titleProperty) {
	if (titleProperty) return titleProperty;
	const SCORE = {
		RELATION: 10,
		LONG_TEXT: 20,
		ENUM: 30,
		EMAIL: 45,
		PLAIN_TEXT: 60,
		NAMED: 100
	};
	const titleLikeKeys = /* @__PURE__ */ new Set([
		"name",
		"fullname",
		"displayname",
		"title",
		"label",
		"heading",
		"subject",
		"username",
		"nickname"
	]);
	let best;
	order.forEach((key) => {
		const property = properties[key];
		if (!property || property.hideFromCollection) return;
		if (key === idProperty) return;
		if (property.storage || property.url === "image") return;
		let score;
		if (property.type === "relation" || property.type === "reference") score = SCORE.RELATION;
		else if (property.type !== "string") return;
		else if (property.enum) score = SCORE.ENUM;
		else if (property.multiline || property.markdown) score = SCORE.LONG_TEXT;
		else if (property.email) score = SCORE.EMAIL;
		else if (titleLikeKeys.has(key.toLowerCase().replace(/[^a-z0-9]/g, ""))) score = SCORE.NAMED;
		else score = SCORE.PLAIN_TEXT;
		if (!best || score > best.score) best = {
			key,
			score
		};
	});
	return best?.key ?? idProperty;
}
//#endregion
//#region src/views/CollectionView/CollectionCardView.tsx
/**
* Headless card grid view that wraps {@link CardView}.
*
* Renders each item as a card with an optional image, title,
* preview properties, and selection support.
*
* @group Collection View
*/
function CollectionCardView({ dataController, properties, propertiesOrder, idProperty = "id", titleProperty, onRowClick, cellRenderer, size = "m", selectionEnabled, selectionController, highlightedItems, emptyComponent, className }) {
	const order = useMemo(() => propertiesOrder ?? Object.keys(properties), [propertiesOrder, properties]);
	const titlePropertyKey = useMemo(() => resolveTitlePropertyKey(order, properties, idProperty, titleProperty), [
		titleProperty,
		order,
		properties,
		idProperty
	]);
	const imageProperty = useMemo(() => {
		for (const key of order) {
			const prop = properties[key];
			if (prop && prop.type === "string" && prop.storage) return key;
		}
	}, [order, properties]);
	const previewProperties = useMemo(() => {
		const result = [];
		for (const key of order) {
			if (key === titlePropertyKey || key === imageProperty) continue;
			const prop = properties[key];
			if (prop && !prop.hideFromCollection) {
				result.push({
					key,
					property: prop
				});
				if (result.length >= 3) break;
			}
		}
		return result;
	}, [
		order,
		properties,
		titlePropertyKey,
		imageProperty
	]);
	const selectedIds = useMemo(() => {
		if (!selectionController?.selectedItems) return void 0;
		const ids = /* @__PURE__ */ new Set();
		for (const item of selectionController.selectedItems) {
			const id = getValueInPath(item, idProperty);
			if (id !== void 0 && id !== null) ids.add(id);
		}
		return ids;
	}, [selectionController?.selectedItems, idProperty]);
	const highlightedIds = useMemo(() => {
		if (!highlightedItems) return void 0;
		const ids = /* @__PURE__ */ new Set();
		for (const item of highlightedItems) {
			const id = getValueInPath(item, idProperty);
			if (id !== void 0 && id !== null) ids.add(id);
		}
		return ids;
	}, [highlightedItems, idProperty]);
	const handleSelectionChange = useCallback((item, selected) => {
		if (!selectionController) return;
		selectionController.setSelectedItems((prev) => {
			if (selected) return [...prev, item];
			const itemId = getValueInPath(item, idProperty);
			return prev.filter((existing) => {
				return getValueInPath(existing, idProperty) !== itemId;
			});
		});
	}, [selectionController, idProperty]);
	const renderCard = useCallback((item, extra) => {
		const titleValue = getValueInPath(item, titlePropertyKey);
		const imageValue = imageProperty ? getValueInPath(item, imageProperty) : void 0;
		const hasImage = typeof imageValue === "string" && imageValue.length > 0;
		return /* @__PURE__ */ jsxs(Card, {
			onClick: (e) => {
				if (e) extra.onClick(e);
			},
			className: cls("overflow-hidden relative", extra.selected && "bg-primary-bg/30 dark:bg-primary-bg/10 ring-1 ring-primary/75", extra.highlighted && !extra.selected && "ring-1 ring-primary/50"),
			children: [
				imageProperty && (hasImage ? /* @__PURE__ */ jsx("img", {
					src: imageValue,
					alt: "",
					className: "w-full h-32 object-cover rounded-t-lg"
				}) : /* @__PURE__ */ jsx("div", { className: cls("h-24 rounded-t-lg", "bg-surface-field border border-hairline") })),
				selectionEnabled && /* @__PURE__ */ jsx("div", {
					className: "absolute top-2 right-2",
					onClick: (e) => e.stopPropagation(),
					children: /* @__PURE__ */ jsx(Checkbox, {
						checked: extra.selected,
						onCheckedChange: extra.onSelectionChange,
						size: "small"
					})
				}),
				/* @__PURE__ */ jsx(Typography, {
					variant: "subtitle2",
					noWrap: true,
					className: "px-3 pt-2 pb-1",
					children: String(titleValue ?? "")
				}),
				previewProperties.map(({ key, property }) => {
					const value = getValueInPath(item, key);
					if (cellRenderer) {
						const custom = cellRenderer({
							row: item,
							propertyKey: key,
							property,
							value,
							size: "small"
						});
						if (custom !== void 0) return /* @__PURE__ */ jsx("div", {
							className: "px-3 py-0.5",
							children: custom
						}, key);
					}
					return /* @__PURE__ */ jsx("div", {
						className: "px-3 py-0.5",
						children: /* @__PURE__ */ jsx(DefaultCellRenderer, {
							row: item,
							propertyKey: key,
							property,
							value,
							size: "small"
						})
					}, key);
				}),
				/* @__PURE__ */ jsx("div", { className: "pb-2" })
			]
		});
	}, [
		titlePropertyKey,
		imageProperty,
		previewProperties,
		cellRenderer,
		selectionEnabled
	]);
	return /* @__PURE__ */ jsx("div", {
		className: cls("w-full h-full overflow-auto", className),
		children: /* @__PURE__ */ jsx(CardView, {
			data: dataController.data,
			dataLoading: dataController.loading,
			noMoreToLoad: dataController.noMoreToLoad,
			dataLoadingError: dataController.error,
			itemCount: dataController.itemCount,
			setItemCount: dataController.setItemCount,
			pageSize: dataController.pageSize,
			paginationEnabled: dataController.paginationEnabled,
			onItemClick: onRowClick,
			selectedIds,
			highlightedIds,
			selectionEnabled: !!selectionEnabled,
			onSelectionChange: handleSelectionChange,
			size,
			renderCard,
			emptyComponent
		})
	});
}
//#endregion
//#region src/views/CollectionView/CollectionListView.tsx
/**
* Headless list view that wraps {@link ListView}.
*
* Each row displays a title (bold), subtitle with 1–2 secondary property values,
* and optional selection support.
*
* @group Collection View
*/
function CollectionListView({ dataController, properties, propertiesOrder, idProperty = "id", titleProperty, onRowClick, cellRenderer, size = "m", selectionEnabled, selectionController, highlightedItems, emptyComponent, className }) {
	const order = useMemo(() => propertiesOrder ?? Object.keys(properties), [propertiesOrder, properties]);
	const titlePropertyKey = useMemo(() => resolveTitlePropertyKey(order, properties, idProperty, titleProperty), [
		titleProperty,
		order,
		properties,
		idProperty
	]);
	const subtitleProperties = useMemo(() => {
		const result = [];
		for (const key of order) {
			if (key === titlePropertyKey) continue;
			const prop = properties[key];
			if (prop && !prop.hideFromCollection) {
				result.push({
					key,
					property: prop
				});
				if (result.length >= 2) break;
			}
		}
		return result;
	}, [
		order,
		properties,
		titlePropertyKey
	]);
	const selectedIds = useMemo(() => {
		if (!selectionController?.selectedItems) return void 0;
		const ids = /* @__PURE__ */ new Set();
		for (const item of selectionController.selectedItems) {
			const id = getValueInPath(item, idProperty);
			if (id !== void 0 && id !== null) ids.add(id);
		}
		return ids;
	}, [selectionController?.selectedItems, idProperty]);
	const highlightedIds = useMemo(() => {
		if (!highlightedItems) return void 0;
		const ids = /* @__PURE__ */ new Set();
		for (const item of highlightedItems) {
			const id = getValueInPath(item, idProperty);
			if (id !== void 0 && id !== null) ids.add(id);
		}
		return ids;
	}, [highlightedItems, idProperty]);
	const handleSelectionChange = useCallback((item, selected) => {
		if (!selectionController) return;
		selectionController.setSelectedItems((prev) => {
			if (selected) return [...prev, item];
			const itemId = getValueInPath(item, idProperty);
			return prev.filter((existing) => {
				return getValueInPath(existing, idProperty) !== itemId;
			});
		});
	}, [selectionController, idProperty]);
	const renderRow = useCallback((params) => {
		const { item, style, className: rowClassName, selected, highlighted, onClick, onSelectionChange } = params;
		const titleValue = getValueInPath(item, titlePropertyKey);
		return /* @__PURE__ */ jsxs("div", {
			style,
			className: cls("flex items-center gap-3 px-4 cursor-pointer", "hover:bg-surface-hover", "transition-colors duration-100", selected && "bg-primary-bg", highlighted && "bg-surface-raised", rowClassName),
			onClick,
			children: [selectionEnabled && /* @__PURE__ */ jsx("div", {
				className: "flex-shrink-0",
				onClick: (e) => e.stopPropagation(),
				children: /* @__PURE__ */ jsx(Checkbox, {
					checked: selected,
					onCheckedChange: onSelectionChange,
					size: "small"
				})
			}), /* @__PURE__ */ jsxs("div", {
				className: "flex-1 min-w-0 py-1",
				children: [/* @__PURE__ */ jsx(Typography, {
					variant: "subtitle2",
					noWrap: true,
					children: String(titleValue ?? "")
				}), subtitleProperties.length > 0 && /* @__PURE__ */ jsx("div", {
					className: "flex items-center gap-2",
					children: subtitleProperties.map(({ key, property }) => {
						const value = getValueInPath(item, key);
						if (cellRenderer) {
							const custom = cellRenderer({
								row: item,
								propertyKey: key,
								property,
								value,
								size: "tiny"
							});
							if (custom !== void 0) return /* @__PURE__ */ jsx(React.Fragment, { children: custom }, key);
						}
						return /* @__PURE__ */ jsx(React.Fragment, { children: /* @__PURE__ */ jsx(DefaultCellRenderer, {
							row: item,
							propertyKey: key,
							property,
							value,
							size: "tiny"
						}) }, key);
					})
				})]
			})]
		});
	}, [
		titlePropertyKey,
		subtitleProperties,
		cellRenderer,
		selectionEnabled
	]);
	return /* @__PURE__ */ jsx("div", {
		className: cls("w-full h-full overflow-auto", className),
		children: /* @__PURE__ */ jsx(ListView, {
			data: dataController.data,
			dataLoading: dataController.loading,
			noMoreToLoad: dataController.noMoreToLoad,
			dataLoadingError: dataController.error,
			itemCount: dataController.itemCount,
			setItemCount: dataController.setItemCount,
			pageSize: dataController.pageSize,
			paginationEnabled: dataController.paginationEnabled,
			onItemClick: onRowClick,
			selectedIds,
			highlightedIds,
			selectionEnabled: !!selectionEnabled,
			onSelectionChange: handleSelectionChange,
			size,
			renderRow,
			emptyComponent
		})
	});
}
//#endregion
//#region src/views/CollectionView/CollectionKanbanView.tsx
function getEnumLabel(enumConfig, key) {
	if (!enumConfig) return key;
	if (enumConfig instanceof Map) {
		const cfg = enumConfig.get(key);
		if (!cfg) return key;
		return typeof cfg === "string" ? cfg : cfg.label;
	}
	const cfg = enumConfig[key];
	if (!cfg) return key;
	return typeof cfg === "string" ? cfg : cfg.label;
}
/**
* Headless kanban view that wraps {@link KanbanView}.
*
* Groups data by a kanban property's enum values into columns.
* Each card displays a title and 1–2 preview properties.
*
* @group Collection View
*/
function CollectionKanbanView({ dataController, properties, propertiesOrder, idProperty = "id", titleProperty, kanbanProperty, onRowClick, cellRenderer, emptyComponent, onRowCreate, className }) {
	const order = useMemo(() => propertiesOrder ?? Object.keys(properties), [propertiesOrder, properties]);
	const columns = useMemo(() => {
		const kanbanConfig = properties[kanbanProperty];
		if (kanbanConfig?.enum) {
			if (kanbanConfig.enum instanceof Map) return Array.from(kanbanConfig.enum.keys()).map(String);
			return Object.keys(kanbanConfig.enum);
		}
		const seen = /* @__PURE__ */ new Set();
		for (const row of dataController.data) {
			const val = getValueInPath(row, kanbanProperty);
			if (val !== null && val !== void 0) seen.add(String(val));
		}
		return Array.from(seen);
	}, [
		properties,
		kanbanProperty,
		dataController.data
	]);
	const columnLabels = useMemo(() => {
		const kanbanConfig = properties[kanbanProperty];
		const labels = {};
		for (const col of columns) labels[col] = getEnumLabel(kanbanConfig?.enum, col);
		return labels;
	}, [
		properties,
		kanbanProperty,
		columns
	]);
	const titlePropertyKey = useMemo(() => resolveTitlePropertyKey(order, properties, idProperty, titleProperty), [
		titleProperty,
		order,
		properties,
		idProperty
	]);
	const previewProperties = useMemo(() => {
		const result = [];
		for (const key of order) {
			if (key === titlePropertyKey || key === kanbanProperty) continue;
			const prop = properties[key];
			if (prop && !prop.hideFromCollection) {
				result.push({
					key,
					property: prop
				});
				if (result.length >= 2) break;
			}
		}
		return result;
	}, [
		order,
		properties,
		titlePropertyKey,
		kanbanProperty
	]);
	const boardItems = useMemo(() => {
		return dataController.data.map((row, index) => {
			const id = getValueInPath(row, idProperty);
			return {
				id: id !== null && id !== void 0 ? String(id) : String(index),
				data: row
			};
		});
	}, [dataController.data, idProperty]);
	const assignColumn = useCallback((item) => {
		const val = getValueInPath(item.data, kanbanProperty);
		const strVal = val !== null && val !== void 0 ? String(val) : "";
		if (columns.includes(strVal)) return strVal;
		return columns[0] ?? "";
	}, [kanbanProperty, columns]);
	const ItemComponent = useMemo(() => {
		function KanbanCard(props) {
			const { item } = props;
			const titleValue = getValueInPath(item.data, titlePropertyKey);
			return /* @__PURE__ */ jsxs(Card, {
				onClick: () => onRowClick?.(item.data),
				className: cls("p-3", cardClickableMixin, props.isDragging && "bg-primary-bg/30 dark:bg-primary-bg/10 ring-1 ring-primary/75", props.isDragging && "shadow-lg opacity-75"),
				children: [/* @__PURE__ */ jsx(Typography, {
					variant: "subtitle2",
					noWrap: true,
					children: String(titleValue ?? "")
				}), previewProperties.map(({ key, property }) => {
					const value = getValueInPath(item.data, key);
					if (cellRenderer) {
						const custom = cellRenderer({
							row: item.data,
							propertyKey: key,
							property,
							value,
							size: "tiny"
						});
						if (custom !== void 0) return /* @__PURE__ */ jsx("div", {
							className: "mt-1",
							children: custom
						}, key);
					}
					return /* @__PURE__ */ jsx("div", {
						className: "mt-1",
						children: /* @__PURE__ */ jsx(DefaultCellRenderer, {
							row: item.data,
							propertyKey: key,
							property,
							value,
							size: "tiny"
						})
					}, key);
				})]
			});
		}
		return KanbanCard;
	}, [
		titlePropertyKey,
		previewProperties,
		cellRenderer,
		onRowClick
	]);
	const handleAddItemToColumn = useCallback((column) => {
		onRowCreate?.({ [kanbanProperty]: column });
	}, [onRowCreate, kanbanProperty]);
	if (columns.length === 0 && !dataController.loading) return /* @__PURE__ */ jsx("div", {
		className: "flex items-center justify-center p-8",
		children: emptyComponent ?? /* @__PURE__ */ jsx(Typography, {
			variant: "body2",
			color: "secondary",
			children: "No columns available for kanban view."
		})
	});
	return /* @__PURE__ */ jsx(KanbanView, {
		columns,
		columnLabels,
		data: boardItems,
		assignColumn,
		ItemComponent,
		onAddItemToColumn: onRowCreate ? handleAddItemToColumn : void 0,
		className
	});
}
//#endregion
//#region src/views/CollectionView/CollectionView.tsx
var DEFAULT_ENABLED_VIEWS = [
	"list",
	"table",
	"cards",
	"kanban"
];
/**
* A data-agnostic collection view component that orchestrates table, card,
* list, and kanban views — driven entirely by props.
*
* Zero imports from any entity or data layer. This component lives in
* `@rebasepro/ui` and can be used with any data source.
*
* @example
* ```tsx
* import { CollectionView } from "@rebasepro/ui";
*
* <CollectionView
*     dataController={{ data: myRows, loading: false, noMoreToLoad: true }}
*     properties={{
*         name: { type: "string", name: "Name" },
*         email: { type: "string", name: "Email" },
*     }}
*     title="Contacts"
*     onRowClick={(row) => openDetail(row.id)}
* />
* ```
*
* @group Collection View
*/
function CollectionView({ dataController, properties, propertiesOrder, displayedColumnIds, idProperty = "id", titleProperty, title, viewMode: viewModeProp, defaultViewMode = "list", enabledViews = DEFAULT_ENABLED_VIEWS, onViewModeChange: onViewModeChangeProp, size: sizeProp, defaultSize = "m", onSizeChange: onSizeChangeProp, kanbanProperty: kanbanPropertyProp, kanbanPropertyOptions, onKanbanPropertyChange: onKanbanPropertyChangeProp, onRowClick, onRowCreate, canCreate = true, selectionEnabled, selectionController, highlightedItems, onMultipleDelete, cellRenderer, emptyComponent, customViews, toolbarActionsStart, toolbarActionsEnd, hideToolbar, hoverRow = true, onColumnResize, onColumnsOrderChange, className }) {
	const [internalViewMode, setInternalViewMode] = useState(defaultViewMode);
	const viewMode = viewModeProp ?? internalViewMode;
	const onViewModeChange = useCallback((mode) => {
		setInternalViewMode(mode);
		onViewModeChangeProp?.(mode);
	}, [onViewModeChangeProp]);
	const [internalSize, setInternalSize] = useState(defaultSize);
	const size = sizeProp ?? internalSize;
	const onSizeChange = useCallback((s) => {
		setInternalSize(s);
		onSizeChangeProp?.(s);
	}, [onSizeChangeProp]);
	const [internalKanbanProperty, setInternalKanbanProperty] = useState(kanbanPropertyProp);
	const kanbanProperty = kanbanPropertyProp ?? internalKanbanProperty;
	const onKanbanPropertyChange = useCallback((property) => {
		setInternalKanbanProperty(property);
		onKanbanPropertyChangeProp?.(property);
	}, [onKanbanPropertyChangeProp]);
	const resolvedKanbanProperty = useMemo(() => {
		if (kanbanProperty) return kanbanProperty;
		const order = propertiesOrder ?? Object.keys(properties);
		for (const key of order) {
			const prop = properties[key];
			if (prop && prop.type === "string" && prop.enum) return key;
		}
	}, [
		kanbanProperty,
		properties,
		propertiesOrder
	]);
	const resolvedKanbanPropertyOptions = useMemo(() => {
		if (kanbanPropertyOptions) return kanbanPropertyOptions;
		const options = [];
		const order = propertiesOrder ?? Object.keys(properties);
		for (const key of order) {
			const prop = properties[key];
			if (prop && prop.type === "string" && prop.enum) options.push({
				key,
				label: prop.name
			});
		}
		return options;
	}, [
		kanbanPropertyOptions,
		properties,
		propertiesOrder
	]);
	const sharedProps = {
		dataController,
		properties,
		propertiesOrder,
		idProperty,
		titleProperty,
		onRowClick,
		cellRenderer,
		selectionEnabled,
		selectionController,
		highlightedItems,
		emptyComponent
	};
	const customViewEntries = useMemo(() => {
		if (!customViews) return [];
		return Object.entries(customViews).filter(([key]) => !DEFAULT_ENABLED_VIEWS.includes(key)).map(([key, entry]) => {
			const isBare = typeof entry === "function";
			return {
				key,
				name: isBare ? key : entry.name ?? key,
				icon: isBare ? void 0 : entry.icon,
				Component: isBare ? entry : entry.Component
			};
		});
	}, [customViews]);
	function renderView() {
		const custom = customViewEntries.find((entry) => entry.key === viewMode);
		if (custom) {
			const { Component } = custom;
			return /* @__PURE__ */ jsx(Component, {
				...sharedProps,
				onRowCreate: canCreate ? onRowCreate : void 0,
				canCreate,
				size
			});
		}
		switch (viewMode) {
			case "table": return /* @__PURE__ */ jsx(CollectionTableView, {
				...sharedProps,
				displayedColumnIds,
				size,
				hoverRow,
				onColumnResize,
				onColumnsOrderChange
			});
			case "cards": return /* @__PURE__ */ jsx(CollectionCardView, {
				...sharedProps,
				size
			});
			case "kanban": return resolvedKanbanProperty ? /* @__PURE__ */ jsx(CollectionKanbanView, {
				...sharedProps,
				kanbanProperty: resolvedKanbanProperty,
				onRowCreate: canCreate ? onRowCreate : void 0
			}) : /* @__PURE__ */ jsx("div", {
				className: "flex items-center justify-center p-8",
				children: /* @__PURE__ */ jsx(Typography, {
					variant: "body2",
					color: "secondary",
					children: "No enum property available for kanban grouping."
				})
			});
			default: return /* @__PURE__ */ jsx(CollectionListView, {
				...sharedProps,
				size
			});
		}
	}
	return /* @__PURE__ */ jsxs("div", {
		className: cls("overflow-hidden h-full w-full rounded-md flex flex-col", "bg-surface-sheet", className),
		children: [
			dataController.error && dataController.data.length > 0 && /* @__PURE__ */ jsx("div", {
				className: "flex items-center gap-4 px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 flex-shrink-0",
				children: /* @__PURE__ */ jsxs(Typography, {
					variant: "body2",
					className: "text-red-700 dark:text-red-300 flex-1",
					children: [
						/* @__PURE__ */ jsx("strong", { children: "Warning:" }),
						" ",
						dataController.error.message || "Failed to update data."
					]
				})
			}),
			!hideToolbar && /* @__PURE__ */ jsx(CollectionViewToolbar, {
				viewMode,
				onViewModeChange: enabledViews.length > 1 ? onViewModeChange : void 0,
				enabledViews,
				customViews: customViewEntries,
				size,
				onSizeChange,
				searchString: dataController.searchString,
				onSearchChange: dataController.setSearchString,
				loading: dataController.loading,
				actionsStart: toolbarActionsStart,
				actionsEnd: toolbarActionsEnd,
				kanbanPropertyOptions: viewMode === "kanban" ? resolvedKanbanPropertyOptions : void 0,
				selectedKanbanProperty: resolvedKanbanProperty,
				onKanbanPropertyChange
			}),
			/* @__PURE__ */ jsx("div", {
				className: "flex-1 overflow-hidden",
				children: renderView()
			})
		]
	});
}
//#endregion
export { Alert, AlertCircleIcon, AlertTriangleIcon, AlignLeftIcon, AppWindow, ArrowDownIcon, ArrowDownToLineIcon, ArrowLeftIcon, ArrowRightFromLineIcon, ArrowRightIcon, ArrowRightLeftIcon, ArrowRightToLineIcon, ArrowUpDownIcon, ArrowUpIcon, ArrowUpToLineIcon, Autocomplete, AutocompleteItem, Avatar, Badge, BoldIcon, BookOpenIcon, BooleanSwitch, BooleanSwitchWithLabel, Button, CHIP_COLORS, CHIP_HUES, CHIP_SEED_KEYS, CONTROL_HEIGHT, CalendarIcon, Card, CardView, CenteredView, CheckCircle2Icon, CheckCircleIcon, CheckIcon, CheckSquareIcon, Checkbox, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, ChevronsLeftIcon, ChevronsRightIcon, ChevronsUpDownIcon, Chip, CircleDotIcon, CircleIcon, CircleUserIcon, CircularProgress, CircularProgressCenter, CodeIcon, Collapse, CollectionView, ColorPicker, ColumnsIcon, Container, CopyIcon, DatabaseIcon, DateTimeField, DebouncedTextField, Dialog, DialogActions, DialogContent, DialogTitle, DollarSignIcon, DownloadIcon, ErrorBoundary, ExpandablePanel, ExternalLinkIcon, EyeIcon, EyeOffIcon, FileIcon, FileSearchIcon, FileTextIcon, FileUpload, FilterChip, FilterIcon, FilterXIcon, FlagIcon, FolderIcon, FolderKanbanIcon, FolderPlusIcon, FolderUpIcon, FunctionSquareIcon, GitBranchIcon, GitHubIcon, GlobeIcon, HandleIcon, HashIcon, Heading1Icon, Heading2Icon, Heading3Icon, HelpCircleIcon, HistoryIcon, HomeIcon, IconButton, ImageIcon, ImageOffIcon, InfoIcon, InfoLabel, InputLabel, ItalicIcon, KanbanIcon, KanbanView, KeyIcon, KeyRoundIcon, Label, LanguagesIcon, LayoutGridIcon, Link2Icon, LinkIcon, ListIcon, ListOrderedIcon, ListPlusIcon, ListTodoIcon, ListView, LoaderIcon, LoadingButton, LockIcon, LogOutIcon, LucideIconByName, MailIcon, Markdown, Maximize2Icon, Menu, MenuIcon, MenuItem, Menubar, MenubarCheckboxItem, MenubarContent, MenubarItem, MenubarItemIndicator, MenubarMenu, MenubarPortal, MenubarRadioGroup, MenubarRadioItem, MenubarSeparator, MenubarShortcut, MenubarSub, MenubarSubContent, MenubarSubTrigger, MenubarSubTriggerIndicator, MenubarTrigger, MessageCircleIcon, MinusCircleIcon, MinusIcon, MoonIcon, MoreVerticalIcon, MultiSelect, MultiSelectContext, MultiSelectItem, Music2Icon, PanelLeftCloseIcon, PanelLeftIcon, PanelLeftOpenIcon, Paper, PauseIcon, PenLineIcon, PencilIcon, PhoneIcon, PinIcon, PlayIcon, PlusIcon, Popover, PopoverPrimitive, Portal, PortalContainerProvider, QuoteIcon, RadioGroup, RadioGroupItem, RefreshCcwIcon, RefreshCwIcon, RepeatIcon, ResizablePanels, Rows3Icon, SaveIcon, SearchBar, SearchIcon, Select, SelectGroup, SelectItem, SendIcon, Separator, SettingsIcon, Sheet, ShieldIcon, ShoppingCartIcon, Skeleton, Slider, SlidersHorizontalIcon, Slot, SquareIcon, StarIcon, StickyNoteIcon, StrikethroughIcon, SunIcon, SunMoonIcon, Tab, Table, TableBody, TableCell, TableHeader, TableIcon, TableRow, Tabs, TagIcon, TerminalIcon, TextField, TextIcon, TextareaAutosize, ToggleButtonGroup, Tooltip, Trash2Icon, TrendingUpIcon, TypeIcon, Typography, UnderlineIcon, UndoIcon, Unlink2Icon, UploadCloudIcon, UploadIcon, UserCheckIcon, UserIcon, UserPlus, UsersIcon, VideoIcon, VirtualTable, VirtualTableDateField, VirtualTableInput, VirtualTableNumberInput, VirtualTableSelect, VirtualTableSelectionProvider, VirtualTableSwitch, VoteIcon, Wand2Icon, WrenchIcon, XCircleIcon, XIcon, accentTextMixin, cardClickableMixin, cardMixin, cardSelectedMixin, cls, codeSurfaceMixin, colorClassesMapping, controlHeightMixin, controlPaddingMixin, coolIconKeys, createVirtualTableSelectionStore, debounce, defaultBorderMixin, fieldBackgroundDisabledMixin, fieldBackgroundHoverMixin, fieldBackgroundInvisibleMixin, fieldBackgroundMixin, focusedClasses, focusedDisabled, focusedInvisibleMixin, getColorSchemeForKey, getColorSchemeForSeed, getLoadedLucideIcons, iconKeys, iconSize, isChunkLoadError, lazyChunk, loadChunk, loadLucideIcons, lucideIcons, paperMixin, resolveLucideIcon, surfaceActiveMixin, surfaceHoverMixin, useAutoComplete, useDebounceCallback, useDebounceValue, useDebouncedCallback, useInjectStyles, useIsDarkMode, useLucideIcons, useOutsideAlerter, usePortalContainer, useVirtualTableCellSelected, useVirtualTableSelection };

//# sourceMappingURL=index.es.js.map